// filepath: apps/web/src/i18n/i18n.ts
//
// FR-UI-CORE-8 — i18next initialization for SPA chrome.
//
// This module initializes a singleton `i18next` instance with three
// resource bundles (ru / kk / en) for two namespaces (`common`,
// `conflict`). It is imported once at application boot via
// `apps/web/src/main.tsx` for its side effect; `useTranslation.ts`
// re-exports react-i18next's `useTranslation` over this runtime.
//
// SECURITY / INVARIANT REMINDERS:
//   - INV-9: all chrome strings flow through React text nodes (i18next
//     escapes by default in v23+; we set `interpolation.escapeValue:
//     false` only because React's JSX rendering already escapes — not
//     because we trust the upstream translator to produce HTML-safe
//     output).
//   - The resource strings below are platform-authored (not
//     tenant-authored) and ship with the bundle. No runtime fetch —
//     ARCHITECTURE_WEB.md §1 mandates one tenant-agnostic build, and
//     §8 names ru / kk / en as the v1 shipped locales.
//
// FALLBACK CHAIN (per ARCHITECTURE_WEB.md §8):
//   missing key  →  active locale  →  ru  →  kk  →  en  →  key string.
// i18next's `fallbackLng: 'ru'` plus `supportedLngs: ['ru','kk','en']`
// achieves this with no per-call helper code in our hooks.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

/**
 * Resource bundle for the `common` namespace.
 *
 * Contains the platform-chrome keys the SPA renders via
 * `useTranslation().t('common.*')` and the conflict-modal keys
 * (`conflict.*`) carried over from the FR-UI-CORE-6 bridge.
 *
 * Why both `common.*` and `conflict.*` live here as FLAT keys (with
 * a literal dot in the key name, not as nested objects):
 *
 *   i18next's `keySeparator` is `.` (default). When `t("a.b")` is
 *   called with a nested-resource layout (`resources.ru.common.a.b`),
 *   i18next splits the key on `.` and looks up the nested path. The
 *   FR-UI-CORE-6 bridge contract accepted `t("conflict.title")` from
 *   a hook bound to the default namespace — i18next would interpret
 *   the dot as a nested-path separator, not a flat key, and the
 *   lookup would fail. Storing the keys as FLAT strings
 *   (`"conflict.title": "..."`) makes the lookup succeed and
 *   preserves the bridge contract.
 *
 * The `conflict` namespace is still declared in `ns` so future
 * callers can opt into `useTranslation("conflict")` if they want
 * namespace isolation; today the resources are colocated under
 * `common` because the bridge contract demands it.
 *
 * Tenant-authored strings (form labels, options, etc.) are NOT in
 * this bundle — they flow through the definition payload's
 * `{locale: value}` maps and the `resolveLocaleLabel()` helper in
 * `useTranslation.ts`.
 *
 * Each value is a static translation authored for FR-UI-CORE-8. If a
 * translation is missing for a locale, i18next's fallback chain
 * resolves to the next locale in `supportedLngs` order, ultimately
 * returning the key string itself.
 */
const COMMON_RESOURCES = {
  ru: {
    // common.* chrome keys.
    "common.save": "Сохранить",
    "common.cancel": "Отмена",
    "common.loading": "Загрузка…",
    "common.error": "Ошибка",
    "common.retry": "Повторить",
    "common.close": "Закрыть",
    "common.back": "Назад",
    "common.confirm": "Подтвердить",
    "common.delete": "Удалить",
    "common.edit": "Изменить",
    "common.create": "Создать",
    "common.search": "Поиск",
    "common.copy": "Копировать",
    "common.yes": "Да",
    "common.no": "Нет",
    "common.required": "Обязательное поле",
    "common.optional": "Необязательное поле",
    // FR-UI-CORE-10 AC-5: chrome label for inline field validation
    // errors. The aria-label on InlineFieldError resolves to this
    // key. The error *message* itself comes from the server's
    // `{locale: value}` map (FR-I18N-1) — never from chrome.
    "validation.error.inline": "Ошибка валидации",
    "validation.error.code_default": "Поле содержит ошибку",
    // FR-UI-VIEW-1 AC-4 (rv-2): chrome label for the list-level 422
    // surface (ValidationError primitive). Distinct from
    // validation.error.inline which is the per-field FormRenderer slot.
    "validation.error.list": "Проверка не пройдена",
    // conflict.* keys (FR-UI-CORE-6 carryover). The legacy bridge
    // accepted `t("conflict.title")` directly from
    // `useTranslation()` — the new bridge must do the same. See the
    // doc comment above for the flat-key rationale.
    "conflict.title": "Конфликт изменений",
    "conflict.body":
      "Эта запись была изменена с момента последней загрузки. Выберите действие.",
    "conflict.reapply": "Применить мои изменения к текущей версии",
    "conflict.refetch": "Получить последнюю версию",
    "conflict.discard": "Отменить мои изменения",
    "conflict.serverVersion": "Текущая версия (сервер)",
    "conflict.yourVersion": "Ваши изменения",
    "conflict.currentRowVersion": "Версия строки",
    // FR-TEN-6 / FR-UI-CORE-8: tenant deprovision dialog chrome.
    // Lives in `common` (flat-key layout) so the bridge contract
    // `t("deprovision.confirm")` resolves the same way callers
    // already resolve `t("conflict.title")`. See the
    // COMMON_RESOURCES doc comment above for the flat-key rationale.
    "deprovision.confirm": "Подтвердить деактивацию",
    // FR-UI-FORM-1 AC-2 — reference picker chrome (FR-UI-CORE-8
    // flat-key layout). Picker delegates to ReferencePicker.tsx
    // which calls `t('reference.*')` for all chrome. No hardcoded
    // English/Russian/Kazakh strings in the component source.
    "reference.placeholder": "Поиск…",
    "reference.empty": "Ничего не найдено",
    "reference.loading": "Загрузка…",
    "reference.error": "Не удалось загрузить варианты",
    "reference.cancel": "Отмена",
    "reference.select": "Выбрать",
    // FR-UI-PROM-2: non-skippable human-review gate chrome.
    // Flat-key layout so the bridge contract `t("promote.gate.*")`
    // resolves the same way callers already resolve `t("conflict.*")`.
    // See COMMON_RESOURCES doc comment for the flat-key rationale.
    "promote.gate.section.diff": "Изменения",
    "promote.gate.section.assertions": "Пройденные проверки",
    "promote.gate.section.artifact": "Пакет NEEDS_REVIEW",
    "promote.gate.diff.empty": "Нет изменений для проверки.",
    "promote.gate.assertions.none":
      "Этот промоушен не был сгенерирован агентом. Проверки не записаны.",
    "promote.gate.assertions.recorded": "записано",
    "promote.gate.artifact.none":
      "Артефакт NEEDS_REVIEW не приложен. Это человеческая заявка.",
    "promote.gate.artifact.envelope_meta": "Метаданные конверта",
    "promote.gate.artifact.task_spec_id": "task_spec_id",
    "promote.gate.artifact.task_spec_version": "task_spec_version",
    "promote.gate.artifact.kind": "kind",
    "promote.gate.artifact.agent_principal": "agent_principal",
    "promote.gate.artifact.submitted_at": "submitted_at",
    "promote.gate.artifact.attempt_count": "attempts",
    "promote.gate.artifact.spec_hash": "spec_hash",
    "promote.gate.artifact.final_status": "final_status",
    "promote.gate.artifact.spec": "spec",
    "promote.gate.artifact.attempts": "Попытки",
    "promote.gate.artifact.started_at": "started_at",
    "promote.gate.artifact.completed_at": "completed_at",
    "promote.gate.artifact.terminal_failure_reason": "terminal_failure_reason",
    "promote.gate.artifact.bpm_step_log_snapshot": "bpm_step_log_snapshot",
    "promote.gate.artifact.open_full_envelope":
      "Открыть полный конверт артефакта",
    "promote.gate.buttons.approve": "Утвердить промоушен",
    "promote.gate.buttons.approving": "Утверждение…",
    "promote.gate.buttons.reject": "Отклонить",
    "promote.gate.required_acknowledgement":
      "Подтвердите необратимость перед утверждением",
    "promote.gate.approve.error": "Не удалось утвердить промоушен.",
    "promote.gate.reject.reason_label": "Причина отклонения",
    "promote.gate.reject.reason_placeholder": "Опишите, что нужно исправить…",
    "promote.gate.reject.confirm": "Подтвердить отклонение",
    "promote.gate.reject.pending": "Отклонение…",
    "promote.gate.reject.cancel": "Отмена",
    "promote.gate.reject.error": "Не удалось отклонить промоушен.",
    "promote.gate.error.load_failed": "Не удалось загрузить рецензию:",
    // FR-UI-ADM-1: entity-type CRUD permission matrix editor chrome.
    "admin.matrix.title": "Матрица разрешений",
    "admin.matrix.subtitle":
      "Управление CRUD-разрешениями для ролей и типов сущностей",
    "admin.matrix.column.role": "Роль",
    "admin.matrix.column.create": "Создание",
    "admin.matrix.column.read": "Чтение",
    "admin.matrix.column.update": "Изменение",
    "admin.matrix.column.delete": "Удаление",
    "admin.matrix.column.all": "Все",
    "admin.matrix.column.platform":
      "Платформенная область (управляется сервером)",
    "admin.matrix.action.save": "Сохранить роль",
    "admin.matrix.action.cancel": "Отменить",
    "admin.matrix.action.new_role": "Создать роль",
    "admin.matrix.action.delete_role": "Удалить роль",
    "admin.matrix.confirm.delete_role":
      "Удалить роль {{roleName}}? Это действие нельзя отменить.",
    "admin.matrix.system_role.readonly": "Системная роль — только чтение",
    "admin.matrix.banner.pending_review":
      "{count, plural, one {# роль изменена в staging — ожидает проверки} few {# роли изменены в staging — ожидают проверки} many {# ролей изменено в staging — ожидает проверки} other {# роли изменены в staging — ожидают проверки}}",
    "admin.matrix.banner.open_review": "Открыть проверку",
    "admin.matrix.loading": "Загрузка матрицы...",
    "admin.errors.staging_only.title":
      "Редактор разрешений доступен только в staging",
    "admin.errors.staging_only.body":
      "Откройте страницу на staging-поддомене вашего тенанта.",
    "admin.errors.staging_only.code": "ENV_PRODUCTION",
    "admin.errors.version_conflict.title": "Конфликт версий",
    "admin.errors.version_conflict.body":
      "Эта роль была изменена с момента последней загрузки.",
    "admin.errors.version_conflict.refetch": "Получить последнюю версию",
    "admin.errors.version_conflict.merge": "Объединить мои изменения",
    "admin.errors.version_conflict.discard": "Отменить мои изменения",
    "admin.errors.fetch_failure": "Не удалось загрузить матрицу разрешений",
    "admin.errors.permission_denied":
      "У вас нет прав для редактирования разрешений",
    "admin.errors.rate_limit": "Слишком много запросов. Подождите немного.",
    "admin.errors.stale_version":
      "Версия определения устарела. Перезагрузите страницу.",
    "admin.errors.network": "Сетевая ошибка. Проверьте подключение.",
    // FR-UI-ADM-4 — admin viewer (role × entity-type field
    // classification). Flat-key layout matching the rest of the
    // bundle; see the COMMON_RESOURCES doc comment above for the
    // rationale.
    "admin.field_visibility.title":
      "Видимость полей: {{roleName}} / {{entityType}}",
    "admin.field_visibility.subtitle":
      "Какие поля видит роль {{roleName}} для типа сущности {{entityType}}",
    "admin.field_visibility.column.field": "Поле",
    "admin.field_visibility.column.classification": "Классификация",
    "admin.field_visibility.states.visible": "видимое",
    "admin.field_visibility.states.hidden_cosmetic": "скрыто (косметика)",
    "admin.field_visibility.states.denied_security": "запрещено (безопасность)",
    "admin.field_visibility.tooltip.denied_security_explainer":
      "Сервер не возвращает это поле — граница безопасности (FR-AUTH-7).",
    "admin.field_visibility.aria.visible": "видимое",
    "admin.field_visibility.aria.hidden_cosmetic":
      "скрыто формулой видимости — косметика, значение сохраняется сервером",
    "admin.field_visibility.aria.denied_security":
      "запрещено разрешением — граница безопасности, поле не возвращается в ответах API",
    "admin.field_visibility.aria.table_label":
      "Видимость полей для роли {{roleName}} на типе сущности {{entityType}}",
    "admin.field_visibility.empty": "Нет полей для отображения.",
    // FR-UI-ADM-6 — webhook endpoint admin editor chrome. Flat-key
    // layout matching the rest of the bundle; see the
    // COMMON_RESOURCES doc comment for the rationale.
    "admin.webhooks.title": "Вебхук-эндпоинты",
    "admin.webhooks.subtitle":
      "Управление эндпоинтами и активными ключами подписи (только идентификаторы)",
    "admin.webhooks.column.url": "URL",
    "admin.webhooks.column.process_def": "Определение процесса",
    "admin.webhooks.column.signing_key_id": "Идентификатор ключа подписи",
    "admin.webhooks.column.status": "Статус",
    "admin.webhooks.column.version": "Версия",
    "admin.webhooks.column.actions": "Действия",
    "admin.webhooks.status.active": "активный",
    "admin.webhooks.status.disabled": "отключён",
    "admin.webhooks.action.add": "Добавить эндпоинт",
    "admin.webhooks.action.disable": "Отключить",
    "admin.webhooks.action.delete": "Удалить",
    "admin.webhooks.dialog.create.title": "Создать вебхук-эндпоинт",
    "admin.webhooks.dialog.create.label.url": "URL",
    "admin.webhooks.dialog.create.label.process_def":
      "Идентификатор определения процесса",
    "admin.webhooks.dialog.create.cta": "Создать",
    "admin.webhooks.dialog.create.success.title": "Вебхук создан",
    "admin.webhooks.dialog.create.success.copy_key_value":
      "Скопируйте это значение сейчас — позже вы не сможете его увидеть снова.",
    "admin.webhooks.dialog.create.success.copied": "Скопировано!",
    "admin.webhooks.dialog.conflict.title":
      "Другой администратор изменил этот эндпоинт",
    "admin.webhooks.dialog.conflict.refetch":
      "Получить последнюю версию и объединить",
    "admin.webhooks.dialog.conflict.discard": "Отменить мои изменения",
    "admin.webhooks.empty.message":
      "Эндпоинтов пока нет. Нажмите «Добавить эндпоинт», чтобы создать.",
    "admin.webhooks.errors.staging_only":
      "Управление вебхук-эндпоинтами доступно только в staging.",
    "admin.webhooks.errors.version_conflict":
      "Другой администратор изменил этот эндпоинт. Получите последнюю версию или отмените изменения.",
    "admin.webhooks.errors.fetch_failure":
      "Не удалось загрузить вебхук-эндпоинты.",
    "admin.webhooks.errors.permission_denied":
      "У вас нет прав для управления вебхук-эндпоинтами.",
    "admin.webhooks.errors.rate_limit":
      "Слишком много запросов. Повторите через {{seconds}} с.",
    "admin.webhooks.banner.pending_review":
      "{count, plural, one {# эндпоинт изменён в staging — ожидает проверки} few {# эндпоинта изменены в staging — ожидают проверки} many {# эндпоинтов изменено в staging — ожидает проверки} other {# эндпоинта изменено в staging — ожидает проверки}}",
    "admin.webhooks.banner.open_review": "Открыть проверку",
    "admin.webhooks.table.aria_label": "Таблица вебхук-эндпоинтов",
    "admin.webhooks.keys.title": "Webhook HMAC ключи",
    "admin.webhooks.keys.active": "Активный",
    "admin.webhooks.keys.grace_period": "Льготный период",
    "admin.webhooks.keys.expired": "Истёк",
    "admin.webhooks.keys.rotate_action": "Сменить ключ",
    "admin.webhooks.keys.issue_action": "Выпустить ключ",
    "admin.webhooks.keys.revoke_action": "Отозвать",
    "admin.webhooks.keys.grace_window_label": "Льготный период",
    "admin.webhooks.keys.grace_window_hours":
      "{hours, plural, one {# час} few {# часа} many {# часов} other {# часа}}",
    "admin.webhooks.keys.grace_until_label": "Истекает",
    "admin.webhooks.keys.expired_at_label": "Истёк",
    "admin.webhooks.keys.key_id_label": "ID ключа",
    "admin.webhooks.keys.created_at_label": "Создан",
    "admin.webhooks.keys.state_label": "Состояние",
    "admin.webhooks.keys.one_time_copy_title":
      "Новое значение ключа (одноразовое)",
    "admin.webhooks.keys.one_time_copy_warning":
      "Скопируйте значение сейчас. Оно больше НЕ будет показано.",
    "admin.webhooks.keys.copy_button": "Копировать",
    "admin.webhooks.keys.done_button": "Готово",
    "admin.webhooks.keys.value_cleared_message":
      "Значение ключа удалено из соображений безопасности. Смените ключ снова.",
    "admin.webhooks.keys.destructive_rotate_title":
      "Сменить ключ во время льготного периода?",
    "admin.webhooks.keys.destructive_rotate_warning":
      "Ключ сейчас в льготном периоде. Смена ключа отозвёт его и завершит льготный период. Текущие webhook-доставки, использующие этот ключ, могут завершиться ошибкой.",
    "admin.webhooks.keys.confirm_rotate_button": "Подтвердить смену",
    "admin.webhooks.keys.cancel_button": "Отмена",
    "admin.webhooks.keys.revoke_confirm_title": "Отозвать ключ?",
    "admin.webhooks.keys.revoke_confirm_warning":
      "Отзыв происходит немедленно и необратим. Введите ID ключа для подтверждения.",
    "admin.webhooks.keys.revoke_confirm_placeholder": "Введите ID ключа",
    "admin.webhooks.keys.new_deliveries_tooltip":
      "Webhook-доставки, созданные ПОСЛЕ этой смены, будут использовать этот ключ. Доставки, выполняющиеся до смены, продолжат использовать прежний ключ до {{grace_until}}.",
    "admin.webhooks.keys.loading_error": "Не удалось загрузить HMAC ключи.",
    "admin.webhooks.keys.retry": "Повторить",
    "admin.webhooks.keys.permission_denied":
      "У вас нет прав для управления HMAC ключами.",
    "admin.webhooks.keys.countdown.separator": "и",
    "admin.webhooks.keys.countdown.expired": "Истёк",
    "admin.webhooks.keys.grace_window_minutes_min": "минимум {{minutes}} минут",
    "admin.webhooks.keys.grace_window_minutes_max":
      "максимум {{minutes}} минут",
    "admin.webhooks.keys.errors.invalid_grace_window":
      "Недопустимое окно льготного периода. Допустимый диапазон: 1–72 часа.",
    "admin.webhooks.keys.errors.staging_only":
      "Управление HMAC ключами webhook доступно только в staging.",
    // FR-UI-ADM-14 -- tier & quota admin panel.
    "tier.title": "Уровень и квоты",
    "tier.subtitle":
      "Только-для-чтения: лимиты уровня и текущее использование.",
    "tier.tier.small": "Малый",
    "tier.tier.middle": "Средний",
    "tier.tier.corporate": "Корпоративный",
    "tier.limits.script_wall_clock_ms": "Стенд-байм-аут скрипта (мс)",
    "tier.limits.script_memory_pages": "Страницы памяти скрипта",
    "tier.limits.storage_mb": "Квота хранилища (МБ)",
    "tier.limits.file_storage_mb": "Квота файлового хранилища (МБ)",
    "tier.limits.concurrent_sandboxes": "Параллельные песочницы",
    "tier.limits.agent_retry_budget": "Бюджет повторов агента",
    "tier.limits.max_single_upload_mb": "Макс. размер загрузки (МБ)",
    "tier.limits.outbox_depth_cap": "Ограничение outbox",
    "tier.usage.storage_bytes": "Использование хранилища",
    "tier.usage.file_storage_bytes": "Использование файлов",
    "tier.usage.concurrent_sandboxes": "Активные песочницы",
    "tier.unavailable": "недоступно",
    "tier.state.loading": "Загрузка уровня и квот…",
    "tier.state.fetch_failure": "Не удалось загрузить уровень и квоты.",
    "tier.state.permission_denied": "У вас нет прав на просмотр уровня и квот.",
    "tier.state.rate_limit":
      "Слишком много запросов. Повторите через {{seconds}} с.",
    // FR-UI-ADM-14 -- definition audit log viewer.
    "audit.title": "Журнал изменений определений",
    "audit.filter.definition_type": "Тип определения",
    "audit.filter.all_types": "Все типы",
    "audit.filter.author": "Автор",
    "audit.filter.after": "После",
    "audit.filter.before": "До",
    "audit.filter.clear": "Очистить фильтры",
    "audit.column.definition_type": "Тип",
    "audit.column.definition_id": "Определение",
    "audit.column.action": "Действие",
    "audit.column.author": "Автор",
    "audit.column.agent": "Агент",
    "audit.column.agent_label": "Запись агента",
    "audit.column.task_spec": "Спецификация задачи",
    "audit.column.orchestrator": "Оркестратор",
    "audit.column.version": "Версия",
    "audit.column.created_at": "Когда",
    "audit.column.definition_type.form": "Форма",
    "audit.column.definition_type.process": "Процесс",
    "audit.column.definition_type.entity": "Сущность",
    "audit.column.definition_type.permission": "Разрешение",
    "audit.column.definition_type.task_spec": "Спецификация",
    "audit.empty": "Нет записей по текущим фильтрам.",
    "audit.pagination.label": "Пагинация журнала",
    "audit.pagination.previous": "Назад",
    "audit.pagination.next": "Вперёд",
    "audit.state.loading": "Загрузка журнала…",
    "audit.state.fetch_failure": "Не удалось загрузить журнал.",
    "audit.state.permission_denied": "У вас нет прав на просмотр журнала.",
    "audit.state.validation_error": "Некорректный фильтр — проверьте ввод.",
    "audit.state.rate_limit":
      "Слишком много запросов. Повторите через {{seconds}} с.",
    // FR-UI-ADM-8 — effects admin
    "admin.effects.http.page.title": "HTTP-эффекты",
    "admin.effects.http.empty": "HTTP-эндпоинты не настроены.",
    "admin.effects.http.table_aria_label": "Таблица HTTP-эффектов",
    "admin.effects.http.column.url": "URL",
    "admin.effects.http.column.method": "Метод",
    "admin.effects.http.column.status": "Статус",
    "admin.effects.http.column.actions": "Действия",
    "admin.effects.http.action.create": "Добавить эндпоинт",
    "admin.effects.http.action.disable": "Отключить",
    "admin.effects.http.action.delete": "Удалить",
    "admin.effects.http.field.url": "URL",
    "admin.effects.http.field.method": "HTTP-метод",
    "admin.effects.http.field.timeout": "Тайм-аут (секунды)",
    "admin.effects.http.dialog.create.title": "Добавить HTTP-эндпоинт",
    "admin.effects.http.dialog.create.success.title": "Эндпоинт создан",
    "admin.effects.http.dialog.create.success.description":
      "Скопируйте секрет сейчас — он больше не будет показан.",
    "admin.effects.http.dialog.create.success.copy_secret":
      "Скопировать секрет",
    "admin.effects.email.page.title": "Email-каналы",
    "admin.effects.email.empty": "Email-каналы не настроены.",
    "admin.effects.email.table_aria_label": "Таблица Email-каналов",
    "admin.effects.email.column.from": "Адрес отправителя",
    "admin.effects.email.column.subject": "Тема",
    "admin.effects.email.column.locales": "Языки",
    "admin.effects.email.column.status": "Статус",
    "admin.effects.email.column.actions": "Действия",
    "admin.effects.email.action.create": "Добавить канал",
    "admin.effects.email.action.disable": "Отключить",
    "admin.effects.email.action.delete": "Удалить",
    "admin.effects.email.field.from_address": "Адрес отправителя",
    "admin.effects.email.field.subject": "Тема",
    "admin.effects.email.field.provider": "Провайдер",
    "admin.effects.email.field.body": "Текст письма (с поддержкой локали)",
    "admin.effects.email.dialog.create.title": "Добавить Email-канал",
    "admin.effects.email.dialog.create.success.title": "Канал создан",
    "admin.effects.email.dialog.create.success.description":
      "Скопируйте секрет SMTP сейчас — он больше не будет показан.",
    "admin.effects.email.dialog.create.success.copy_secret":
      "Скопировать секрет",
    "admin.effects.status.active": "активный",
    "admin.effects.status.disabled": "отключён",
    "admin.effects.dialog.conflict.title": "Конфликт версий",
    "admin.effects.dialog.conflict.server_version": "Версия на сервере",
    "admin.effects.dialog.conflict.discard": "Отменить мои изменения",
    "admin.effects.dialog.conflict.refetch": "Обновить данные",
    // FR-UI-ADM-10 — secret reference manager admin chrome (Russian).
    "admin.secrets.title": "Секреты",
    "admin.secrets.create_button": "Создать секрет",
    "admin.secrets.empty_state": "Секреты ещё не созданы.",
    "admin.secrets.table.name": "Название",
    "admin.secrets.table.ref_id": "ID ссылки",
    "admin.secrets.table.status": "Статус",
    "admin.secrets.table.created_at": "Создан",
    "admin.secrets.table.updated_at": "Обновлён",
    "admin.secrets.table.version": "Версия",
    "admin.secrets.table.actions": "Действия",
    "admin.secrets.dialog.create.title": "Создать секрет",
    "admin.secrets.dialog.create.description":
      "Введите имя. Сервер сгенерирует значение.",
    "admin.secrets.dialog.create.name_label": "Название секрета",
    "admin.secrets.dialog.create.name_hint":
      "До 120 символов. Используется только для идентификации в этом списке.",
    "admin.secrets.dialog.create.success.title": "Секрет создан",
    "admin.secrets.dialog.create.success.copy_secret_value":
      "Скопируйте значение секрета сейчас — оно больше не будет показано.",
    "admin.secrets.dialog.create.success.copied": "Скопировано!",
    "admin.secrets.dialog.impact.title": "Удаление секрета",
    "admin.secrets.dialog.impact.subtitle":
      "Секрет «{{name}}» будет удалён. Сначала проверьте, где он используется.",
    "admin.secrets.dialog.impact.references_label":
      "{{count}} эффект-конфигураций ссылаются на этот секрет:",
    "admin.secrets.dialog.impact.kind_http_endpoint": "HTTP-эндпоинт",
    "admin.secrets.dialog.impact.kind_email_channel": "Email-канал",
    "admin.secrets.dialog.impact.safe_to_delete":
      "Ни одна эффект-конфигурация не ссылается на этот секрет. Удаление безопасно.",
    "admin.secrets.dialog.impact.acknowledge_label":
      "Я понимаю, что эти эффекты потеряют доступ к секрету после удаления.",
    "admin.secrets.banner.pending_review":
      "{{count}} секрет ожидает проверки перед промоушеном",
    "admin.secrets.banner.open_review": "Открыть проверку",
    "admin.secrets.status.active": "активный",
    "admin.secrets.status.disabled": "отключён",
    // FR-UI-ADM-11 -- template registry admin chrome (Russian).
    "admin.templates.registry.title": "Реестр шаблонов",
    "admin.templates.registry.column.template_id": "ID шаблона",
    "admin.templates.registry.column.version": "Версия",
    "admin.templates.registry.column.description": "Описание",
    "admin.templates.registry.column.publisher_kind": "Издатель",
    "admin.templates.registry.column.pre_vetted": "Pre-vetted",
    "admin.templates.registry.column.published_at": "Опубликовано",
    "admin.templates.registry.column.published_by": "Кем",
    "admin.templates.registry.column.content_hash": "Content hash",
    "admin.templates.registry.column.actions": "Действия",
    "admin.templates.registry.badge.auto_activates":
      "Активируется автоматически при provisioning",
    "admin.templates.registry.badge.tooltip":
      "Этот шаблон автоматически промоутится из staging в production при выборе во время provisioning тенанта. При ручной установке через эту страницу требуется обычный human-review.",
    "admin.templates.registry.action.install": "Установить",
    "admin.templates.registry.action.installing": "Установка…",
    "admin.templates.registry.action.install_aria":
      "Установить шаблон {{template_id}} версии {{version}}",
    "admin.templates.registry.success.installed":
      "Шаблон успешно установлен в staging",
    "admin.templates.registry.success.installed.review_link":
      "Открыть проверку промоушена",
    "admin.templates.registry.empty.message":
      "Пока нет опубликованных шаблонов.",
    "admin.templates.registry.table.aria_label": "Таблица реестра шаблонов",
    "admin.templates.registry.errors.staging_only":
      "Установка шаблонов доступна только в staging.",
    "admin.templates.registry.errors.permission_denied":
      "У вас нет прав на установку шаблонов.",
    "admin.templates.registry.errors.fetch_failure":
      "Не удалось загрузить реестр шаблонов.",
    "admin.templates.registry.errors.rate_limit":
      "Слишком много запросов. Подождите немного.",
    "admin.templates.registry.errors.seeding_failed":
      "Не удалось установить шаблон: ошибка сидинга.",
    "admin.templates.registry.errors.publisher_not_allowed":
      "Издатель шаблона не разрешён для этого тенанта.",
    "admin.templates.registry.errors.template_not_found":
      "Шаблон или версия не найдены.",
    // FR-UI-ADM-12 — template-update flow chrome (Russian).
    "admin.templates.update.badge.singular": "{{count}} обновление доступно",
    "admin.templates.update.badge.plural": "{{count}} обновлений доступно",
    "admin.templates.update.badge.aria":
      "{{count}} обновлений шаблона доступно для просмотра",
    "admin.templates.update.badge.view": "Открыть обновления",
    "admin.templates.update.title": "Обновления шаблонов",
    "admin.templates.update.list.empty": "Доступных обновлений нет.",
    "admin.templates.update.list.column.template_id": "ID шаблона",
    "admin.templates.update.list.column.installed": "Установлено",
    "admin.templates.update.list.column.target": "Целевая",
    "admin.templates.update.list.column.conflicts": "Конфликты",
    "admin.templates.update.list.column.irreversible": "Необратимо",
    "admin.templates.update.list.row.view": "Просмотр",
    "admin.templates.update.detail.title": "Обновление {{template_id}}",
    "admin.templates.update.detail.installed_version":
      "Установлено: {{version}}",
    "admin.templates.update.detail.target_version": "Целевая: {{version}}",
    "admin.templates.update.detail.total_conflicts":
      "{{count}} конфликт(ов) требует разрешения",
    "admin.templates.update.detail.counter":
      "{{resolved}} из {{total}} конфликтов разрешено",
    "admin.templates.update.classification.unchanged": "Не изменялось тенантом",
    "admin.templates.update.classification.modified": "Изменено тенантом",
    "admin.templates.update.classification.added": "Добавлено тенантом",
    "admin.templates.update.resolution.keep_tenant": "Оставить тенант",
    "admin.templates.update.resolution.accept_update": "Принять обновление",
    "admin.templates.update.resolution.merge": "Объединить",
    "admin.templates.update.resolution.close": "Закрыть",
    "admin.templates.update.resolution.preview": "Просмотр объединения",
    "admin.templates.update.apply": "Применить обновление",
    "admin.templates.update.apply.disabled_tooltip":
      "Все конфликты должны быть разрешены перед применением",
    "admin.templates.update.apply.success": "Обновление применено",
    "admin.templates.update.apply.irreversible_redirect":
      "Перенаправление на проверку промоушена",
    "admin.templates.update.errors.unresolved": "Не все конфликты разрешены",
    "admin.templates.update.errors.installed_drift":
      "Установленная версия изменилась с момента загрузки. Обновите страницу.",
    "admin.templates.update.errors.validation_failed":
      "Проверка пакета не пройдена",
    "admin.templates.update.errors.not_installed":
      "Шаблон не установлен у тенанта",
    "admin.templates.update.errors.staging_only":
      "Применение обновлений шаблонов доступно только в staging.",
    "admin.templates.update.errors.permission_denied":
      "У вас нет прав на обновление шаблонов.",
    // FR-UI-ADM-13 — template export/import admin chrome (Russian).
    "admin.templates.export_import.action.export": "Экспорт",
    "admin.templates.export_import.action.exporting": "Экспорт…",
    "admin.templates.export_import.action.export_aria":
      "Экспортировать шаблон {{template_id}} версии {{version}}",
    "admin.templates.export_import.action.import": "Импорт",
    "admin.templates.export_import.notice.operator_console_required":
      "Для импорта шаблонов используйте операторскую консоль.",
    "admin.templates.export_import.dialog.import.title": "Импорт шаблона",
    "admin.templates.export_import.dialog.import.file_label":
      "Файл пакета шаблона",
    "admin.templates.export_import.dialog.import.submit": "Отправить",
    "admin.templates.export_import.dialog.import.submit_disabled_tooltip":
      "Для отправки используйте операторскую консоль",
    "admin.templates.export_import.dialog.replace.title":
      "Заменить существующий шаблон?",
    "admin.templates.export_import.dialog.replace.existing_label":
      "Существующий:",
    "admin.templates.export_import.dialog.replace.imported_label":
      "Импортируемый:",
    "admin.templates.export_import.dialog.replace.diff":
      "существующий v{{existing_version}} → импорт v{{import_version}}",
    "admin.templates.export_import.dialog.replace.confirm": "Заменить",
    "admin.templates.export_import.dialog.replace.cancel": "Отмена",
    "admin.templates.export_import.dialog.replace.server_rules_notice":
      "Серверные правила неизменности версий применяются при отправке — это подтверждение их не отменяет.",
    "admin.templates.export_import.errors.manifest_missing":
      "manifest.json не найден в архиве.",
    "admin.templates.export_import.errors.manifest_invalid_json":
      "manifest.json содержит невалидный JSON.",
    "admin.templates.export_import.errors.cross_publisher_kind":
      "Только шаблоны, опубликованные платформой, могут импортироваться операторами.",
    "admin.templates.export_import.errors.manifest_incomplete":
      "Манифест содержит не все обязательные поля.",
    "admin.templates.export_import.errors.file_pick_failed":
      "Файл не удалось прочитать.",
    "admin.templates.export_import.errors.payload_too_large":
      "Пакет слишком большой.",
    "admin.templates.export_import.errors.validation_failed":
      "Проверка пакета не пройдена.",
    "admin.templates.export_import.tooltip.replaces_preserves_customizations":
      "FR-TPL-4 сохраняет кастомизации тенанта при установке — это не затрагивается повторным импортом версии шаблона.",
    "admin.templates.export_import.source.imported": "импортирован",
    "admin.templates.export_import.source.native": "нативный",
    // FR-UI-ADM-3 — formula-editor widget chrome. Flat-key layout
    // matching the rest of the bundle; see the COMMON_RESOURCES doc
    // comment above for the rationale.
    "formula.editor.label": "Формула",
    "formula.editor.validate": "Проверить",
    "formula.editor.type": "Тип: {{type}}",
    "formula.editor.placeholder": "Введите формулу…",
    "formula.editor.a11y.description":
      "Редактор формул с серверной валидацией. Сервер проверяет синтаксис и тип; локальная оценка формул не выполняется.",
    // FR-UI-LIST-1 — definition-driven list view chrome (Russian).
    // Flat-key layout matching the rest of the bundle; see the
    // COMMON_RESOURCES doc comment for the rationale.
    "lists.title": "Записи",
    "lists.table.aria_label": "Таблица записей сущности",
    "lists.empty.no_records": "Нет записей для отображения.",
    "lists.empty.permission_denied": "У вас нет доступа к этим записям.",
    "lists.filter.group_label": "Фильтры",
    "lists.filter.placeholder": "Фильтр…",
    "lists.filter.aria_input": "Фильтр по полю {{field}}",
    "lists.sort.asc": "▲",
    "lists.sort.desc": "▼",
    "lists.sort.toggle_aria": "Изменить сортировку для {{field}}",
    "lists.pagination.next": "Вперёд",
    "lists.pagination.aria_label": "Постраничная навигация",
    "lists.pagination.page_size": "Размер страницы: {{pageSize}}",
    "lists.boolean.yes": "Да",
    "lists.boolean.no": "Нет",
    "lists.error.fetch_failure": "Не удалось загрузить записи.",
    // FR-UI-VIEW-2 — Tree view chrome (Russian). The locked-copy
    // count-banner key is intentionally omitted — the TreeCountBanner
    // uses `count_banner` with locked-copy interpolation. ASCII
    // placeholder strings here follow D1 hand-off.
    "lists.tree.title": "Дерево записей",
    "lists.tree.error.empty": "В этой сущности нет записей.",
    "lists.tree.error.fetch_failure": "Не удалось загрузить дерево записей.",
    "lists.tree.error.permission_denied": "У вас нет доступа к этим записям.",
    "lists.tree.error.stale_version":
      "Структура этой сущности изменилась. Обновите страницу.",
    "lists.tree.error.rate_limit":
      "Слишком много запросов. Повторите попытку позже.",
    "lists.tree.error.validation_error":
      "Конфигурация дерева для этой сущности отклонена сервером.",
    "lists.tree.count_banner": "{{shown}} из {{total}} записей показано.",
    // FR-UI-VIEW-3 — Gantt view chrome (Russian). Keys mirror the
    // FR document + manifest contract; locked-copy keys (title /
    // noStartField / zeroDuration / loadMore / invalidConfig /
    // dependsOnLabel / conflictOnMove) carry static chrome, the
    // {count}-bearing capBanner uses i18next interpolation. See
    // the kanban / tree blocks above for the same flat-key layout
    // rationale.
    "lists.gantt.title": "Диаграмма Ганта",
    "lists.gantt.noStartField": "нет даты начала",
    "lists.gantt.zeroDuration": "дата начала позже даты окончания",
    "lists.gantt.capBanner":
      "Показано {{count}} записей из {{total}}. Остальные записи загружены не были.",
    "lists.gantt.loadMore": "Загрузить ещё",
    "lists.gantt.invalidConfig":
      "Конфигурация диаграммы Ганта отсутствует или некорректна.",
    "lists.gantt.dependsOnLabel": "Зависимости",
    "lists.gantt.conflictOnMove":
      "Запись была изменена другим пользователем. Обновите страницу.",
    // FR-UI-PROC-1 — execution-trace viewer + failed-instance
    // dashboard chrome (Russian). Flat-key layout matching the
    // rest of the bundle; see COMMON_RESOURCES doc comment for
    // the rationale.
    "processes.failed.title": "Неуспешные экземпляры процессов",
    "processes.failed.subtitle":
      "Экземпляры процессов в состоянии failed для текущего тенанта. Можно повторить шаг или отменить экземпляр.",
    "failed_instance.empty": "Нет неуспешных экземпляров.",
    "failed_instance.list_aria_label":
      "Неуспешные экземпляры процессов для этого тенанта",
    "failed_instance.failed_node_label": "Узел сбоя",
    "failed_instance.reason_label": "Причина",
    "failed_instance.retries_label": "Попыток повтора",
    "failed_instance.trace_label": "Трассировка сбоя",
    "failed_instance.trace_summary": "Записей в логе: {{count}}",
    "failed_instance.view_trace": "Открыть трассировку",
    "failed_instance.retry_button": "Повторить шаг",
    "failed_instance.retry_pending": "Повтор шага…",
    "failed_instance.retry_error": "Не удалось повторить шаг.",
    "failed_instance.retry_not_failed":
      "Экземпляр больше не в состоянии failed. Обновите страницу.",
    "failed_instance.cancel_button": "Отменить экземпляр",
    "failed_instance.cancel_pending": "Отмена…",
    "failed_instance.cancel_error": "Не удалось отменить экземпляр.",
    "failed_instance.cancel_confirm_title": "Отменить экземпляр?",
    "failed_instance.cancel_confirm_body":
      "Экземпляр процесса будет остановлен. Запись о сбое сохраняется для аудита; дальнейшие шаги не выполнятся.",
    "failed_instance.restored_orphan_badge": "Восстановленный orphan",
    "failed_instance.restored_orphan_explainer":
      "Сбой зафиксирован во время восстановления тенанта. Исходный шаг может быть уже не воспроизводим.",
    "failed_instance.state.fetch_failure":
      "Не удалось загрузить список неуспешных экземпляров.",
    "trace.title": "Трассировка выполнения",
    "trace.subtitle":
      "Лог шагов выбранного экземпляра процесса, упорядоченный хронологически.",
    "trace.table_aria_label": "Лог шагов трассировки выполнения",
    "trace.column.node_id": "Узел",
    "trace.column.status": "Статус",
    "trace.column.retry_count": "Повторы",
    "trace.column.started_at": "Начало",
    "trace.column.duration_ms": "Длительность",
    "trace.column.error_message": "Ошибка",
    "trace.empty": "Нет записей в логе шагов.",
    "trace.state.fetch_failure": "Не удалось загрузить лог шагов.",
    // FR-UI-OPS-1 — operator console chrome (Russian). Flat-key
    // layout matching the rest of the bundle; see the
    // COMMON_RESOURCES doc comment for the rationale.
    "operator.title": "Консоль оператора",
    "operator.summary.title": "Сводка по тенантам",
    "operator.summary.tenantCount": "Тенанты",
    "operator.summary.activeInstances": "Активные экземпляры",
    "operator.summary.errorRate": "Доля ошибок",
    "operator.summary.empty": "Тенантов пока нет",
    "operator.summary.computedAt": "Обновлено",
    // FR-UI-OPS-3 — per-tenant error-count badge suffix
    // (renders as e.g. "6 err" inside the redesigned <Badge> cell).
    "operator.summary.errorUnit": "ош",
    "operator.tokenPrompt.label": "Токен оператора",
    "operator.tokenPrompt.button": "Войти",
    "operator.tokenPrompt.placeholder": "Вставьте токен оператора",
    "operator.tokenPrompt.error.unauthorized":
      "Не авторизовано. Проверьте токен и попробуйте снова.",
    "apex.title": "ASCOA Platform",
    "apex.subtitle":
      "Это платформенный apex. Консоль оператора — управление тенантами, депровизией и кросс-тенантной наблюдаемостью.",
    "apex.how_to_enter_title": "Войти в консоль оператора",
    "apex.how_to_enter_body":
      "Консоль оператора находится по тому же домену в /operator/. Нужен платформенный токен (PLATFORM_ADMIN_TOKEN). Нажмите ниже, чтобы открыть токен-гейт; после принятия токена вы попадаете на кросс-тенантную сводку.",
    "apex.open_operator": "Открыть консоль оператора",
    "apex.tour_title": "URL тенантов",
    "apex.tour_body":
      "Каждый тенант работает на своём поддомене. Список тенантов доступен в консоли оператора после входа. Тенант открывается по своему поддомену; с apex в тенант войти нельзя.",
    "apex.tour_local": "дефолтный dev-тенант",
    "apex.tour_tenant": "поддомен тенанта (после провизии)",
    "operator.error.fetchFailure": "Не удалось загрузить сводку.",
    "operator.error.retry": "Повторить",
    "operator.summary.table.tenant": "Тенант",
    "operator.summary.table.instanceCount": "Активные экземпляры",
    "operator.summary.table.errorCount": "Ошибки",
    "operator.summary.table.activeSandboxCount": "Активные песочницы",
    "operator.summary.table.aria_label": "Разбивка по тенантам",
    // FR-UI-OPS-2 — operator nav shell + drill-down + audit
    // placeholder chrome (Russian).
    "operator.nav.label": "Разделы оператора",
    "operator.nav.summary": "Сводка",
    "operator.nav.tenants": "Тенанты",
    "operator.nav.effects": "Эффекты / Операции",
    "operator.nav.audit": "Аудит",
    "operator.drillDown.title": "Детали тенанта",
    "operator.drillDown.instanceCount": "Активные экземпляры",
    "operator.drillDown.errorCount": "Ошибки",
    "operator.drillDown.activeSandboxCount": "Активные песочницы",
    "operator.drillDown.openAdmin": "Открыть /admin тенанта",
    "operator.drillDown.openFor": "Открыть детали тенанта {{slug}}",
    "operator.drillDown.close": "Закрыть",
    "operator.audit.title": "Аудит",
    "operator.audit.placeholder": "Журнал аудита ещё не реализован.",
    "operator.tenants.loadError": "Не удалось загрузить тенантов.",
    "operator.topbar.localeLabel": "Язык интерфейса",
    "operator.tenants.list.title": "Тенанты",
    "operator.tenants.list.empty": "Тенантов не найдено.",
    "operator.tenants.list.refresh": "Обновить",
    "operator.tenants.list.loading": "Загрузка тенантов…",
    "operator.tenants.list.errorPrefix": "Не удалось загрузить тенантов:",
    "operator.tenants.list.deprovision": "Деактивировать",
    "operator.tenants.list.ariaLabel":
      "Список тенантов с действиями деактивации",
    "operator.tenants.list.col.slug": "Слаг",
    "operator.tenants.list.col.name": "Название",
    "operator.tenants.list.col.tier": "Уровень",
    "operator.tenants.list.col.status": "Статус",
    "operator.tenants.list.col.tenantId": "ID арендатора",
    "operator.tenants.list.col.actions": "Действия",
    // ISS-509 (BA ruling): localized "Unknown" status label for the
    // decoder fallback path (status field absent from wire). The
    // internal value is the machine-readable "unknown"; the displayed
    // text is the i18n value here. Style mirrors the
    // admin.webhooks.status.active / .disabled keys (lowercase in ru).
    "operator.tenants.list.status.unknown": "неизвестно",
    // ISS-510 (follow-up to ISS-509): localized status value labels. The
    // internal values are machine-readable ("active" | "disabled" |
    // "quiesced" | "unknown"); the displayed text is the i18n value
    // below. Lowercase adjectives in ru (mirrors admin.webhooks.status.active).
    "operator.tenants.list.status.active": "активный",
    "operator.tenants.list.status.disabled": "отключён",
    "operator.tenants.list.status.quiesced": "приостановлен",
    // ISS-510: localized tier value labels. Mirrors the existing
    // tier.tier.small / .middle / .corporate style (Title Case in en,
    // adjective form in ru).
    "operator.tenants.list.tier.small": "Малый",
    "operator.tenants.list.tier.middle": "Средний",
    "operator.tenants.list.tier.corporate": "Корпоративный",
    // FR-UI-CABINET-1 — tenant workspace shell ("My Cabinet") chrome
    // (Russian). Flat-key layout matching the rest of the bundle; see
    // the COMMON_RESOURCES doc comment for the rationale.
    "cabinet.title": "Рабочее пространство тенанта",
    "cabinet.nav.label": "Разделы рабочего пространства",
    "cabinet.nav.cabinet": "Мой кабинет",
    "cabinet.nav.tasks": "Мои задачи",
    "cabinet.nav.processes": "Процессы",
    "cabinet.nav.admin": "Администрирование",
    "cabinet.nav.section.records": "Записи",
    "cabinet.nav.section.admin": "Администрирование",
    "cabinet.landing.title": "Мой кабинет",
    "cabinet.landing.subtitle": "Ваша работа с первого взгляда.",
    "cabinet.landing.tiles_label": "Сводка",
    "cabinet.landing.tasks_due_soon": "Задачи скоро",
    "cabinet.landing.open_processes": "Открытые процессы",
    "cabinet.landing.failed_processes": "Неуспешные процессы",
    "cabinet.landing.recent_records": "Недавние записи",
    "cabinet.landing.recent_records.placeholder":
      "Недавние записи появятся здесь, когда будут настроены разрешения для типов сущностей.",
    "cabinet.landing.recent_records.configureAction": "Настроить разрешения",
    "cabinet.admin.title": "Администрирование",
    "cabinet.admin.subtitle": "Управление конфигурацией тенанта.",
    "cabinet.admin.grid_label": "Разделы администрирования",
    "cabinet.admin.denied": "У вас нет доступа к разделу администрирования.",
    "cabinet.admin.tile.users": "Роли",
    "cabinet.admin.tile.permissions_matrix": "Матрица разрешений",
    "cabinet.admin.tile.record_field_permissions": "Права на записи и поля",
    "cabinet.admin.tile.webhooks": "Вебхук-эндпоинты",
    "cabinet.admin.tile.hmac_keys": "HMAC-ключи",
    "cabinet.admin.tile.http_endpoints": "HTTP-эндпоинты",
    "cabinet.admin.tile.email_channels": "Email-каналы",
    "cabinet.admin.tile.secrets": "Секреты",
    "cabinet.admin.tile.templates": "Шаблоны",
    "cabinet.admin.tile.template_updates": "Обновления шаблонов",
    "cabinet.admin.tile.tier": "Уровень и квоты",
    "cabinet.admin.tile.audit": "Журнал аудита",
    "cabinet.admin.tile.forms": "Формы",
    "cabinet.admin.tile.processes": "Процессы",
    "cabinet.errors.fetch_failure": "Не удалось загрузить.",
    "cabinet.errors.permission_denied": "У вас нет доступа к этому.",
    "cabinet.errors.rate_limit": "Слишком много запросов. Подождите.",
    "cabinet.errors.stale_version": "Эта версия больше недоступна.",
    "cabinet.errors.tile_label": "Сводка",
    "cabinet.errors.retry_after": "Повтор через {{seconds}} с.",
    // FR-UI-ADM-2 — record-level + field-level permission editors.
    // Flat-key layout matching the rest of the bundle. See the
    // COMMON_RESOURCES doc comment for the rationale.
    "admin.record_permissions.page_title":
      "Права доступа на уровне записей и полей",
    "admin.record_permissions.role": "Роль",
    "admin.record_permissions.cell.ownership": "Правило владения",
    "admin.record_permissions.cell.fields": "Права полей",
    "admin.record_permissions.cell.owner": "Поле владельца",
    "admin.record_permissions.editor.title": "Редактировать правила записи",
    "admin.record_permissions.editor.tab.ownership": "Владение",
    "admin.record_permissions.editor.tab.condition": "Условие",
    "admin.record_permissions.editor.expr_label": "Выражение (expr-lang)",
    "admin.record_permissions.editor.preview_button": "Предпросмотр",
    "admin.record_permissions.editor.preview.loading": "Загрузка выборки…",
    "admin.record_permissions.editor.preview.matches":
      "Совпадает {{matches}} из {{total}} выбранных записей (предпросмотр)",
    "admin.record_permissions.conflict.title": "Конфликт версии",
    "admin.record_permissions.conflict.body":
      "Эти права изменены с момента последней загрузки. Выберите действие.",
    "admin.record_permissions.conflict.refetch": "Получить последнюю версию",
    "admin.record_permissions.conflict.merge": "Объединить мои изменения",
    "admin.record_permissions.conflict.discard": "Отменить мои изменения",
    "admin.record_permissions.errors.staging_only_body":
      "Редактор прав доступен только в staging.",
    "admin.record_permissions.error.title": "Ошибка загрузки",
    "admin.field_permissions.title": "Редактировать права полей",
    "admin.field_permissions.field": "Поле",
    "admin.field_permissions.state": "Состояние",
    "admin.field_permissions.state.readable": "Чтение",
    "admin.field_permissions.state.writable": "Чтение и запись",
    "admin.field_permissions.state.hidden": "Скрыто",
    "admin.entity_field_owner.title": "Поле владельца сущности",
    "admin.entity_field_owner.field_label": "Поле владельца",

    // FR-UI-ADM-5 Russian locale additions (paste inside the existing ru block before the closing brace).
    "admin.users.title": "Пользователи и роли",
    "admin.users.subtitle":
      "Назначение ролей Keycloak пользователям тенанта. Изменения вступают в силу при следующем обновлении токена OIDC.",
    "admin.users.column.display_name": "Имя",
    "admin.users.column.email": "Email",
    "admin.users.column.assigned_roles": "Назначенные роли",
    "admin.users.column.actions": "Действия",
    "admin.users.column.select_all": "Выбрать всех",
    "admin.users.column.select_user": "Выбрать пользователя {{name}}",
    "admin.users.action.assign_roles": "Назначить роли",
    "admin.users.action.assign_roles_for": "Назначить роли для {{name}}",
    "admin.users.action.sync_refresh": "Обновить синхронизацию",
    "admin.users.action.sync_refresh_cooldown": "Подождите…",
    "admin.users.sync_status.ok": "Синхронизация в порядке",
    "admin.users.sync_status.degraded": "Синхронизация частичная",
    "admin.users.sync_status.failed": "Синхронизация не выполнена",
    "admin.users.dialog.role_picker.title": "Выберите роли",
    "admin.users.dialog.role_picker.label.roles": "Роли",
    "admin.users.dialog.role_picker.empty":
      "Доступных ролей нет. Синхронизируйте Keycloak.",
    "admin.users.dialog.role_picker.cta": "Сохранить",
    "admin.users.dialog.role_picker.error": "Не удалось сохранить.",
    "admin.users.dialog.bulk_assign.title":
      "Массовое назначение ролей ({{count}})",
    "admin.users.dialog.bulk_assign.cta": "Назначить {{count}}",
    "admin.users.dialog.bulk_assign.roles_placeholder":
      "admin, viewer, editor (через запятую)",
    "admin.users.dialog.bulk_assign.partial_success":
      "Назначено {{assigned}} пользователям.",
    "admin.users.dialog.bulk_assign.failures_label": "Ошибки ({{count}})",
    "admin.users.dialog.bulk_assign.dismiss": "Готово",
    "admin.users.dialog.bulk_assign.error":
      "Не удалось выполнить массовое назначение.",
    "admin.users.bulk.selected_count": "Выбрано: {{count}}",
    "admin.users.orphan_panel.toggle":
      "Потерянные пользователи ({{count}}) — проверить",
    "admin.users.orphan_panel.title": "Потерянные пользователи",
    "admin.users.orphan_panel.empty": "Потерянных пользователей нет.",
    "admin.users.orphan_panel.remove_action":
      "Удалить назначения ролей для {{id}}",
    "admin.users.orphan_panel.remove_action_short": "Очистить",
    "admin.users.orphan_panel.cleaned": "Очищено",
    "admin.users.notice.takes_effect_next_login":
      "Роли вступают в силу при следующем входе пользователя.",
    "admin.users.notice.dismiss": "Закрыть",
    "admin.users.empty.message": "Пользователей не найдено.",
    "admin.users.error.fetch_failure": "Не удалось загрузить пользователей.",
    "admin.users.error.permission_denied":
      "У вас нет прав на просмотр пользователей.",
    "admin.users.error.stale_version": "Версия устарела. Обновите страницу.",
    // FR-UI-ADM-9 — effects operations panel chrome.
    "admin.effects.metrics.page_title": "Операционная панель эффектов",
    "admin.effects.metrics.section.metrics": "Метрики по каналам",
    "admin.effects.metrics.section.circuit":
      "Состояние circuit-breaker по эндпоинтам",
    "admin.effects.metrics.table_label": "Метрики каналов эффектов",
    "admin.effects.metrics.circuit_table_label": "Состояние circuit-breaker",
    "admin.effects.metrics.no_endpoints":
      "Эндпоинты с circuit-breaker не настроены.",
    "admin.effects.metrics.column.channel_name": "Канал",
    "admin.effects.metrics.column.delivery_count": "Доставки (с начала работы)",
    "admin.effects.metrics.column.success_rate": "Успех %",
    "admin.effects.metrics.column.retry_count": "Повторы",
    "admin.effects.metrics.column.dead_letter_count": "Dead-letter",
    "admin.effects.metrics.column.latency_p50": "p50 мс",
    "admin.effects.metrics.column.latency_p95": "p95 мс",
    "admin.effects.metrics.column.latency_p99": "p99 мс",
    "admin.effects.metrics.column.endpoint": "Эндпоинт",
    "admin.effects.metrics.column.state": "Состояние",
    "admin.effects.metrics.column.time_in_state": "Время в состоянии",
    "admin.effects.metrics.column.failures_5min": "Сбои за 5 мин",
    "admin.effects.metrics.column.consecutive_failures":
      "Последовательных сбоев",
    "admin.effects.metrics.column.action": "Действие",
    "admin.effects.metrics.refresh.now": "Обновить",
    "admin.effects.metrics.last_refreshed": "Последнее обновление: {{time}}",
    "admin.effects.metrics.last_refreshed_prefix": "Последнее обновление:",
    "admin.effects.metrics.historical_placeholder_title":
      "Исторические данные (7 дней)",
    "admin.effects.metrics.historical_placeholder_body":
      "Исторические данные (7 дней) — скоро будет доступно (требуется персистентность метрик; задокументировано в ARCH §8.4).",
    "admin.effects.metrics.circuit_open_banner":
      "Цепь разомкнута для {{endpoint}}. ",
    "admin.effects.metrics.circuit_open_affected":
      "Затронутые доставки поставлены в очередь для повтора.",
    "admin.effects.metrics.banner_dismiss": "Закрыть",
    "admin.effects.metrics.banner_dismiss_label":
      "Закрыть уведомление для {{endpoint}}",
    "admin.effects.metrics.policy.consecutive_failures":
      "Последовательных сбоев (1-100)",
    "admin.effects.metrics.policy.open_timeout_ms":
      "Таймаут открытия, мс (1000-300000)",
    "admin.effects.metrics.policy.half_open_max_probes":
      "Макс. проб в half-open (1-10)",
    "admin.effects.metrics.policy.out_of_range":
      "Значение должно быть в [{{min}}, {{max}}]",
    "admin.effects.metrics.policy.updated_toast": "Политика обновлена",
    "admin.effects.metrics.dialog_title": "Настроить breaker",
    "admin.effects.metrics.dialog_save": "Сохранить",
    "admin.effects.metrics.dialog_saving": "Сохранение…",
    "admin.effects.metrics.dialog_cancel": "Отмена",
    "admin.effects.metrics.dialog_description":
      "Измените параметры circuit-breaker для выбранного эндпоинта.",
    "admin.effects.metrics.configure_endpoint":
      "Настроить breaker для {{endpoint}}",
    "admin.effects.metrics.loading": "Загрузка метрик эффектов…",
    "admin.effects.metrics.error_fetch":
      "Не удалось загрузить метрики эффектов: {{message}}",
    "admin.effects.metrics.error_401":
      "Требуются учётные данные оператора. Войдите снова.",
    "admin.effects.metrics.error_504": "Сервер эффектов не отвечает (таймаут).",
    "admin.effects.metrics.error_5xx": "Сервер эффектов вернул ошибку.",
    // FR-UI-BLD-1 -- form-builder chrome.
    "admin.builder.page_title": "Конструктор форм",
    "admin.builder.save.cta": "Сохранить форму",
    "admin.builder.save.saving": "Сохранение...",
    "admin.builder.save.invalid": "Неверный ввод",
    "admin.builder.palette.aria": "Палитра полей",
    "admin.builder.palette.title": "Поля",
    "admin.builder.canvas.title": "Холст формы",
    "admin.builder.canvas.empty": "Перетащите поле из палитры, чтобы начать",
    "admin.builder.canvas.remove": "Удалить поле {{id}}",
    "admin.builder.settings.aria": "Настройки",
    "admin.builder.settings.title": "Настройки",
    "admin.builder.settings.entity_type": "Тип сущности",
    "admin.builder.settings.field_heading": "Поле {{id}}",
    "admin.builder.settings.label": "Метка",
    "admin.builder.settings.placeholder": "Подсказка",
    "admin.builder.settings.required": "Обязательное",
    "admin.builder.settings.visible_when": "Видимость (формула)",
    "admin.builder.settings.computed_from": "Вычисляется из (формула)",
    "admin.builder.settings.target_entity": "Целевая сущность",
    "admin.builder.settings.no_selection": "Выберите поле на холсте",
    "admin.builder.formula.ok": "Формула валидна",
    "admin.builder.formula.error": "Формула невалидна",
    "admin.builder.field_types.string": "Строка",
    "admin.builder.field_types.text": "Текст",
    "admin.builder.field_types.number": "Число",
    "admin.builder.field_types.integer": "Целое число",
    "admin.builder.field_types.float": "Дробное число",
    "admin.builder.field_types.boolean": "Логическое",
    "admin.builder.field_types.date": "Дата",
    "admin.builder.field_types.datetime": "Дата и время",
    "admin.builder.field_types.enum": "Перечисление",
    "admin.builder.field_types.reference": "Ссылка",
    // FR-UI-BLD-1 -- BPM designer chrome.
    "admin.designer.page_title": "Конструктор процессов",
    "admin.designer.save.cta": "Сохранить процесс",
    "admin.designer.save.saving": "Сохранение...",
    "admin.designer.save.invalid": "Неверный ввод",
    "admin.designer.palette.aria": "Палитра узлов",
    "admin.designer.palette.title": "Узлы",
    "admin.designer.canvas.title": "Холст процесса",
    "admin.designer.canvas.empty": "Перетащите узел из палитры, чтобы начать",
    "admin.designer.canvas.aria": "Холст BPM-процесса",
    "admin.designer.canvas.remove": "Удалить узел {{id}}",
    "admin.designer.canvas.dragging": "Перетаскивается {{id}}",
    "admin.designer.settings.aria": "Свойства",
    "admin.designer.settings.title": "Свойства",
    "admin.designer.settings.process_name": "Название процесса",
    "admin.designer.settings.process_description": "Описание",
    "admin.designer.settings.node_heading": "Узел {{id}}",
    "admin.designer.settings.node_label": "Метка",
    "admin.designer.settings.form_id": "Форма",
    "admin.designer.settings.candidate_role_id": "Кандидатская роль",
    "admin.designer.settings.assignee_user_id": "Назначенный пользователь",
    "admin.designer.settings.due_in_seconds": "Срок (секунды)",
    "admin.designer.settings.script_id": "Скрипт",
    "admin.designer.settings.script_formula": "Формула (необяз.)",
    "admin.designer.settings.delay_expression": "Задержка (выражение)",
    "admin.designer.settings.edge_heading": "Переход {{id}}",
    "admin.designer.settings.edge_guard": "Условие (формула)",
    "admin.designer.settings.no_config": "У этого узла нет настроек",
    "admin.designer.settings.no_selection":
      "Выберите узел или переход на холсте",
    "admin.designer.guard.ok": "Условие валидно",
    "admin.designer.guard.error": "Условие невалидно",
    "admin.designer.node_types.start": "Старт",
    "admin.designer.node_types.end": "Завершение",
    "admin.designer.node_types.userTask": "Пользовательская задача",
    "admin.designer.node_types.scriptTask": "Скрипт-задача",
    "admin.designer.node_types.xorGateway": "XOR-шлюз",
    "admin.designer.node_types.andGateway": "AND-шлюз",
    "admin.designer.node_types.timerCatch": "Таймер",
    // FR-UI-BLD-1 -- list-page chrome (ISS-f4fa1b7b-258).
    "admin.forms.list.title": "Формы",
    "admin.forms.list.empty": "Форм пока нет.",
    "admin.forms.list.aria": "Список форм",
    "admin.processes.list.title": "Процессы",
    "admin.processes.list.empty": "Процессов пока нет.",
    "admin.processes.list.aria": "Список процессов",
    // FR-UI-DASH-1 -- per-tenant observability dashboard chrome
    // (Russian). 19 chrome keys covering AC-1 widgets, AC-2
    // alert-rule authoring, AC-3 active-alerts panel, AC-4
    // refresh / loading states, AC-5 drill-down labels.
    "observability.dashboard.title": "Наблюдаемость тенанта",
    "observability.dashboard.subtitle":
      "Агрегированные метрики, процент ошибок и длительность шагов для этого тенанта.",
    "observability.dashboard.refresh": "Обновить",
    "observability.dashboard.author_alert": "Создать правило оповещения",
    "observability.dashboard.error.rate_limit":
      "Слишком много запросов. Подождите.",
    "observability.dashboard.widget.drill_down":
      "Открыть неуспешные экземпляры",
    "observability.dashboard.widget.instance_count.title":
      "Экземпляры процессов (за 24 ч)",
    "observability.dashboard.widget.instance_count.active": "Активные",
    "observability.dashboard.widget.instance_count.failed": "Неуспешные",
    "observability.dashboard.widget.instance_count.completed": "Завершённые",
    "observability.dashboard.widget.error_rate.title":
      "Процент ошибок (за 7 дней)",
    "observability.dashboard.widget.error_rate.empty":
      "Нет недавней активности по шагам.",
    "observability.dashboard.widget.error_rate.sparkline_aria":
      "Почасовой спарклайн процента ошибок",
    "observability.dashboard.widget.step_duration.title":
      "Перцентили длительности шагов",
    "observability.dashboard.widget.step_duration.p50": "p50",
    "observability.dashboard.widget.step_duration.p95": "p95",
    "observability.dashboard.widget.step_duration.p99": "p99",
    "observability.dashboard.widget.process_breakdown.title":
      "Экземпляры по типу процесса",
    "observability.dashboard.widget.process_breakdown.empty":
      "Экземпляров процессов пока нет.",
    "observability.dashboard.widget.process_breakdown.instances_label":
      "экземпляров",
    "observability.dashboard.widget.process_breakdown.failed_label":
      "неуспешных",
    "observability.dashboard.alerts.title": "Активные оповещения",
    "observability.dashboard.alerts.empty":
      "Активные оповещения появятся здесь после поставки FR-OBS-4.",
    "observability.dashboard.alert_rule.title": "Создать правило оповещения",
    "observability.dashboard.alert_rule.name_label": "Название правила",
    "observability.dashboard.alert_rule.severity_label": "Важность",
    "observability.dashboard.alert_rule.expression_label": "Выражение",
    "observability.dashboard.alert_rule.save": "Сохранить",
    "observability.dashboard.alert_rule.saving": "Сохранение…",
    "observability.dashboard.alert_rule.error.persist_unavailable":
      "сохранение правил оповещения будет доступно с FR-OBS-4",
    // FR-UI-CABINET-2 — workspace chrome (topbar + footer + breadcrumbs).
    "cabinet.topbar.skipToMain": "Перейти к основному содержимому",
    "cabinet.topbar.environment.production": "Рабочая среда",
    "cabinet.topbar.environment.staging": "Тестовая среда",
    "cabinet.topbar.environment.sandbox": "Песочница",
    "cabinet.languageMenu.label.en": "English",
    "cabinet.languageMenu.label.ru": "Русский",
    "cabinet.languageMenu.label.kk": "Қазақша",
    "cabinet.languageMenu.label.uk": "Русский",
    "cabinet.languageMenu.label.be": "Беларуская",
    "cabinet.footer.copyright": "© {{year}} ABiTech. Все права защищены.",
    "cabinet.footer.supportLabel": "Поддержка",
    "cabinet.notfound.title": "Эта страница ещё не готова",
    "cabinet.notfound.subtitle":
      "Открытый адрес пока не имеет экрана. Команда знает.",
    "cabinet.notfound.back": "Назад в Мой кабинет",
    // FR-UI-CABINET-3 — required user parameters page + chrome button.
    "cabinet.required_user_parameters.menu_label":
      "Обязательные параметры пользователя",
    "cabinet.required_user_parameters.title":
      "Обязательные параметры пользователя",
    "cabinet.required_user_parameters.subtitle":
      "Ваш тенант требует указать следующие параметры.",
    "cabinet.required_user_parameters.save": "Сохранить",
    "cabinet.required_user_parameters.resolve": "Указать значения",
    "cabinet.required_user_parameters.retry": "Повторить",
    "cabinet.required_user_parameters.missing": "Не заполнено",
    "cabinet.required_user_parameters.not_provided": "Ещё не указано",
    "cabinet.required_user_parameters.supplied": "Указано",
    "cabinet.required_user_parameters.missing_count":
      "{count} обязательных параметров не заполнено",
    "cabinet.required_user_parameters.missing_badge_aria":
      "Не заполнен обязательный параметр",
    "cabinet.required_user_parameters.stale_version_banner":
      "Страница была изменена в другой сессии. Обновите, чтобы продолжить.",
    "cabinet.required_user_parameters.stale_version.title": "Конфликт версий",
    "cabinet.required_user_parameters.stale_version.body":
      "Страница была изменена в другой сессии. Текущая версия строки: {{version}}.",
    "cabinet.required_user_parameters.stale_version.refetch":
      "Получить последнюю версию",
    "cabinet.required_user_parameters.fetch_failure.title":
      "Не удалось загрузить страницу",
    "cabinet.required_user_parameters.fetch_failure.body":
      "Пожалуйста, попробуйте снова.",
    "cabinet.required_user_parameters.permission_denied.title":
      "У вас нет доступа к этой странице",
    "cabinet.required_user_parameters.permission_denied.body":
      "Свяжитесь с администратором, если считаете, что это ошибка.",
    "cabinet.required_user_parameters.rate_limit.title":
      "Слишком много запросов",
    "cabinet.required_user_parameters.rate_limit.body":
      "Подождите немного и попробуйте снова.",
    "cabinet.required_user_parameters.empty.title":
      "Обязательные параметры отсутствуют",
    "cabinet.required_user_parameters.empty.body":
      "Ваш тенант не настроил обязательные параметры пользователя.",
    "cabinet.required_user_parameters.summary.complete":
      "Все обязательные параметры указаны.",
    "cabinet.required_user_parameters.summary.missing_count":
      "{count} обязательных параметров ещё не заполнены.",
    "cabinet.required_user_parameters.summary.supplied_count":
      "{count} указано.",
    // FR-CFG-1..4: tenant presentation, preferences, help, module-config.
    "theme.picker.title": "Тема",
    "theme.picker.preview": "Предпросмотр",
    "theme.picker.save": "Сохранить",
    "theme.picker.discard": "Отменить",
    "theme.picker.empty": "Нет сохранённых тем.",
    "theme.picker.fetch_failure": "Не удалось загрузить тему.",
    "theme.picker.permission_denied": "Нет доступа к теме.",
    "theme.picker.stale_version": "Тема была обновлена — обновите страницу.",
    "theme.picker.rate_limit":
      "Слишком много запросов. Повторите через {{seconds}}с.",
    "theme.picker.validation_error": "Тема содержит ошибки валидации.",
    "theme.fallback.platform_default": "Платформенная тема",
    "preferences.favorites": "Избранное",
    "preferences.page_settings": "Настройки страниц",
    "preferences.current_org": "Текущая организация",
    "preferences.current_location": "Текущая локация",
    "preferences.current_locale": "Язык интерфейса",
    "preferences.save.success": "Настройки сохранены",
    "preferences.save.stale_rv":
      "Настройки были обновлены — обновите страницу.",
    "preferences.admin_read_only": "Только для администратора",
    "help.tour.step_counter": "Шаг {{current}} из {{total}}",
    "help.tour.next": "Далее",
    "help.tour.back": "Назад",
    "help.tour.skip": "Пропустить",
    "help.tour.dismiss": "Закрыть",
    "help.tour.permission_denied.title": "Тур недоступен",
    "help.tour.permission_denied.body": "У вас нет доступа к этому туру.",
    "help.tour.permission_denied.target_id": "Идентификатор цели",
    "help.tour.stale_version.title": "Тур был обновлён",
    "help.tour.stale_version.detail": "Шаги тура были изменены.",
    "help.tour.stale_version.active_conflict": "Конфликт активной версии",
    "help.tour.stale_version.reset_progress": "Сбросить прогресс",
    "help.tour.rate_limit.title": "Слишком много запросов",
    "help.tour.rate_limit.detail":
      "Повторите попытку через {{seconds}} секунд.",
    "help.tour.rate_limit.countdown": "Повтор через {{seconds}}с",
    "help.tour.validation_error.title": "Тур содержит ошибки",
    "help.tour.validation_error.item": "Шаг {{step}}: {{error}}",
    "help.center.title": "Центр помощи",
    "help.center.empty.title": "Нет доступных туров",
    "help.center.empty.body":
      "Когда администратор опубликует тур, он появится здесь.",
    "help.center.empty.author": "Автор",
    "config.editor.title": "Конфигурация модуля",
    "config.editor.namespace": "Пространство имён",
    "config.editor.environment": "Окружение",
    "config.editor.value": "Значение",
    "config.editor.save": "Сохранить",
    "config.editor.discard": "Отменить",
    "config.editor.promote": "Промоутить",
    "config.editor.schema_error.title": "Ошибка схемы",
    "config.editor.schema_error.item": "{{path}}: {{message}}",
    "config.editor.stale_rv":
      "Версия конфигурации устарела — обновите страницу.",
    "config.editor.production_admin.reason_label": "Причина изменения",
    "config.editor.production_admin.reason_required":
      "Укажите причину изменения.",
    "config.editor.promotion_review.title": "Запрос на промоушен",
    "config.editor.promotion_review.approve": "Утвердить",
    "config.editor.promotion_review.reject": "Отклонить",
    // FR-COL-1 — record communication threads chrome (ru-first per FR-I18N-1).
    "comments.tab_label": "Комментарии",
    "comments.composer.placeholder": "Написать комментарий...",
    "comments.composer.submit": "Отправить",
    "comments.delete.confirm": "Удалить комментарий?",
    "comments.deleted_marker": "(удалено)",
    "comments.empty": "Нет комментариев",
    "activity.tab_label": "История",
    "activity.empty": "Нет событий",
    // FR-UI-DIR-1 — People and Organization Directory Workspace chrome (ru).
    "directory.nav.section": "Каталог",
    "directory.nav.people": "Люди",
    "directory.nav.organization": "Оргструктура",
    "directory.nav.positions": "Должности",
    "directory.nav.employment": "Занятость",
    "directory.nav.locations": "Локации",
    "directory.nav.teams": "Команды",
    "directory.nav.counterparties": "Контрагенты",
    "directory.nav.qualifications": "Квалификации",
    "directory.nav.audit": "Аудит каталога",
    "directory.nav.admin": "Администрирование каталога",
    "directory.people.title": "Каталог людей",
    "directory.people.subtitle": "Сотрудники и контрагенты тенанта.",
    "directory.people.search.placeholder":
      "Поиск по имени, email, табельному номеру…",
    "directory.people.filter.status": "Статус",
    "directory.people.filter.unit": "Подразделение",
    "directory.people.filter.location": "Локация",
    "directory.people.filter.clear": "Сбросить фильтры",
    "directory.people.empty": "По заданным фильтрам ничего не найдено.",
    "directory.people.row.status": "Статус",
    "directory.people.row.unit": "Подразделение",
    "directory.people.row.title": "Должность",
    "directory.people.row.location": "Локация",
    "directory.people.row.email": "Email",
    "directory.people.row.tabs.summary": "Карточка",
    "directory.person.summary.title": "Карточка сотрудника",
    "directory.person.summary.tabs.overview": "Обзор",
    "directory.person.summary.tabs.contacts": "Контакты",
    "directory.person.summary.tabs.employment": "Занятость",
    "directory.person.summary.tabs.qualifications": "Квалификации",
    "directory.person.summary.tabs.audit": "Аудит",
    "directory.person.summary.email": "Email",
    "directory.person.summary.phone": "Телефон",
    "directory.person.summary.manager": "Руководитель",
    "directory.person.summary.unavailable": "Скрыто настройками приватности.",
    "directory.person.summary.status.active": "Активен",
    "directory.person.summary.status.inactive": "Неактивен",
    "directory.person.summary.status.on_leave": "В отпуске",
    "directory.org.title": "Организационная структура",
    "directory.org.subtitle": "Подразделения и их иерархия.",
    "directory.org.empty": "Подразделения не настроены.",
    "directory.org.members_label": "Сотрудники",
    "directory.positions.title": "Должности",
    "directory.positions.subtitle": "Каталог должностей тенанта.",
    "directory.positions.empty": "Должности отсутствуют.",
    "directory.locations.title": "Локации",
    "directory.locations.subtitle": "Адреса и площадки тенанта.",
    "directory.locations.empty": "Локации не указаны.",
    // ISS-805 — the location / person forms' interpolated strings MUST be
    // declared here: the `t(key, fallback, interpolation)` bridge returns the
    // raw fallback — placeholders intact — when the key is absent from every
    // bundle, so an undeclared `{{message}}` key renders the placeholder
    // itself instead of the server's message.
    "directory.locations.form.error": "Ошибка: {{message}}",
    "directory.locations.form.server_conflict":
      "{{field}} изменилось на сервере, пока вы редактировали. Ваш текст сохранён — на сервере сейчас: {{value}}",
    "directory.people.form.error": "Ошибка: {{message}}",
    "directory.people.form.server_conflict":
      "{{field}} изменилось на сервере, пока вы редактировали. Ваш текст сохранён — на сервере сейчас: {{value}}",
    // ISS-517 — Organization unit CRUD admin surface (ru is the
    // primary v1 locale per ARCHITECTURE_WEB.md §8). Every key has an
    // English inline fallback at the call site in
    // apps/web/src/admin/organization-units/ and
    // apps/web/src/features/directory/organization-tree/, so the
    // uk / be bundles — which carry no `directory.*` keys at all —
    // resolve through i18next's `fallbackLng: 'ru'` chain.
    "directory.organization_units.new.title": "Новое подразделение",
    "directory.organization_units.new.submit": "Создать подразделение",
    "directory.organization_units.new.submitting": "Создание…",
    "directory.organization_units.new.picker_error":
      "Не удалось загрузить справочные данные. Обновите страницу и повторите попытку.",
    "directory.organization_units.new.invalid_input":
      "Сервер отклонил эти значения. Проверьте название и код и повторите попытку.",
    "directory.organization_units.edit.title": "Редактирование подразделения",
    "directory.organization_units.edit.submit": "Сохранить",
    "directory.organization_units.edit.submitting": "Сохранение…",
    "directory.organization_units.edit.loading": "Загрузка подразделения…",
    "directory.organization_units.edit.not_found": "Подразделение не найдено",
    "directory.organization_units.edit.invalid_input":
      "Сервер отклонил эти значения. Проверьте название и код и повторите попытку.",
    "directory.organization_units.edit.error": "Не удалось сохранить изменения",
    "directory.organization_units.detail.title": "Подразделение",
    "directory.organization_units.detail.loading": "Загрузка подразделения…",
    "directory.organization_units.detail.not_found": "Подразделение не найдено",
    "directory.organization_units.detail.name": "Название",
    "directory.organization_units.detail.code": "Код",
    "directory.organization_units.detail.legal_entity": "Юридическое лицо",
    "directory.organization_units.detail.parent": "Вышестоящее подразделение",
    "directory.organization_units.detail.status": "Статус",
    "directory.organization_units.detail.closed_at": "Дата закрытия",
    "directory.organization_units.detail.row_version": "Версия записи",
    "directory.organization_units.detail.created_at": "Дата создания",
    "directory.organization_units.detail.created_by": "Создал",
    "directory.organization_units.detail.edit": "Редактировать",
    "directory.organization_units.detail.back": "Назад",
    "directory.organization_units.detail.archived":
      "Подразделение архивировано.",
    "directory.organization_units.detail.archive": "Архивировать",
    "directory.organization_units.detail.archiving": "Архивирование…",
    "directory.organization_units.detail.archive_confirm_title":
      "Архивировать подразделение",
    "directory.organization_units.detail.archive_confirm_body":
      "Архивирование закрывает подразделение. Существующие дочерние подразделения не закрываются.",
    "directory.organization_units.detail.archive_confirm": "Архивировать",
    "directory.organization_units.detail.archive_cancel": "Отмена",
    "directory.organization_units.detail.error":
      "Не удалось загрузить подразделение",
    "directory.organization_units.form.name": "Название",
    "directory.organization_units.form.code": "Код",
    "directory.organization_units.form.legal_entity": "Юридическое лицо",
    "directory.organization_units.form.parent": "Вышестоящее подразделение",
    "directory.organization_units.form.no_legal_entity":
      "(выберите юридическое лицо)",
    "directory.organization_units.form.no_parent": "(без родителя — корень)",
    "directory.organization_units.form.cancel": "Отмена",
    "directory.organization_units.form.error": "Ошибка: {{message}}",
    "directory.organization_units.form.server_conflict":
      "{{field}} изменилось на сервере, пока вы редактировали. Ваш текст сохранён — на сервере сейчас: {{value}}",
    "directory.organization_units.action.create": "Создать подразделение",
    "directory.organization_units.action.edit": "Редактировать",
    "directory.organization_units.action.move": "Переместить",
    "directory.organization_units.action.moving": "Перемещение…",
    "directory.organization_units.action.archive": "Архивировать",
    "directory.organization_units.action.archiving": "Архивирование…",
    "directory.organization_units.action.add_child": "Добавить дочернее",
    "directory.organization_units.move.title": "Перемещение подразделения",
    "directory.organization_units.move.submit": "Переместить",
    "directory.organization_units.move.moving": "Перемещение…",
    "directory.organization_units.move.moved": "Подразделение перемещено.",
    "directory.organization_units.move.parent_label":
      "Новое вышестоящее подразделение",
    "directory.organization_units.move.parent_cycle":
      "Перенос создал бы цикл в иерархии.",
    "directory.organization_units.move.cancel": "Отмена",
    "directory.organization_units.status.active": "Активно",
    "directory.organization_units.status.closed": "Закрыто",
    "directory.employment.title": "История занятости",
    "directory.employment.empty": "Записи о назначениях отсутствуют.",
    "directory.employment.current": "Текущее назначение",
    "directory.teams.title": "Команды",
    "directory.teams.subtitle": "Кросс-функциональные и проектные группы.",
    "directory.teams.deferred.title": "Команды появятся позже",
    "directory.teams.deferred.body":
      "Раздел команд отложен до публикации соответствующего API. Мы не подставляем синтетические данные.",
    "directory.qualifications.title": "Квалификации",
    "directory.qualifications.subtitle":
      "Сертификаты, допуски и лицензии сотрудника.",
    "directory.qualifications.deferred.title": "Квалификации появятся позже",
    "directory.qualifications.deferred.body":
      "Раздел квалификаций отложен до публикации соответствующего API. Мы не подставляем синтетические данные.",
    "directory.audit.title": "Аудит каталога",
    "directory.audit.subtitle": "История изменений записей каталога.",
    "directory.audit.empty": "Событий аудита нет.",
    "directory.audit.column.when": "Когда",
    "directory.audit.column.actor": "Кто",
    "directory.audit.column.action": "Действие",
    "directory.audit.column.target": "Объект",
    "directory.admin.title": "Администрирование каталога",
    "directory.admin.subtitle": "Действия администратора каталога.",
    "directory.admin.denied":
      "У вас нет роли администратора каталога. Обратитесь к оператору тенанта.",
    "directory.conflict.title": "Запись была изменена",
    "directory.conflict.body":
      "Сервер отклонил сохранение: другая транзакция изменила эту запись. Обновите страницу, чтобы увидеть актуальные данные, и повторите попытку.",
    "directory.conflict.action.reload": "Обновить страницу",
    "directory.conflict.action.discard": "Отменить изменения",
    "directory.conflict.action.retry": "Повторить",
    "directory.fetch.failure.title": "Не удалось загрузить данные каталога",
    "directory.fetch.failure.body":
      "Сервер не ответил. Проверьте подключение и повторите попытку.",
    "directory.fetch.failure.retry": "Повторить",
    // FR-PM-1 — workmanagement (BP-01.01 — Project registry + New
    // Project form). All labels are RU-first per FR-I18N-1 / FR-I18N-3;
    // the other locales follow with their own translations below.
    "workmanagement.title": "Проекты",
    "workmanagement.code": "Код",
    "workmanagement.name": "Название",
    "workmanagement.responsible_department": "Подразделение",
    "workmanagement.project_administrator": "Администратор проекта",
    "workmanagement.planned_start_date": "Дата начала",
    "workmanagement.planned_end_date": "Дата окончания",
    "workmanagement.description": "Описание",
    "workmanagement.status": "Статус",
    "workmanagement.status.draft": "Черновик",
    "workmanagement.status.active": "Активный",
    "workmanagement.status.completed": "Завершён",
    "workmanagement.status.archived": "Архивный",
    "workmanagement.status.pending_approval": "На согласовании",
    "workmanagement.status.closed": "Закрыт",
    "workmanagement.new_button": "Создать проект",
    "workmanagement.save": "Сохранить",
    "workmanagement.cancel": "Отмена",
    "workmanagement.registry.heading": "Реестр проектов",
    "workmanagement.form.title": "Новый проект",
    "workmanagement.registry.empty": "Нет проектов",
    "workmanagement.registry.filter.status_label": "Статус",
    "workmanagement.registry.filter.all": "Все статусы",
    "workmanagement.registry.error.load_failed":
      "Не удалось загрузить реестр проектов",
    "workmanagement.registry.error.create_failed": "Не удалось создать проект",
    "workmanagement.error.dates_invalid":
      "Дата окончания должна быть позже даты начала",
    "workmanagement.error.name_required": "Введите название проекта",
    "workmanagement.error.name_too_long":
      "Название проекта не должно превышать 200 символов",
    "workmanagement.error.description_too_long":
      "Описание проекта не должно превышать 1000 символов",
    "workmanagement.detail.code": "Код проекта",
    "workmanagement.detail.responsible_department": "Подразделение",
    "workmanagement.detail.project_administrator": "Администратор проекта",
    "workmanagement.detail.planned_start_date": "Дата начала",
    "workmanagement.detail.planned_end_date": "Дата окончания",
    "workmanagement.detail.description": "Описание",
    "workmanagement.detail.created_at": "Дата создания",
    "workmanagement.detail.created_by": "Автор",
    "workmanagement.detail.status": "Статус",
    "workmanagement.detail.row_version": "Версия строки",
    // FR-PM-3 BP-01.04 — approval-flow chrome. RU-first per FR-I18N-1
    // / FR-I18N-3; the other locales follow with their own
    // translations below. Keys are referenced by
    // ProjectApprovalView / ApprovalDecisionPanel /
    // ApprovalHistoryTimeline.
    "workmanagement.approval.title": "Согласование проекта",
    "workmanagement.approval.stage_label": "Этап согласования",
    "workmanagement.approval.panel.title": "Решение по этапу согласования",
    "workmanagement.approval.decision.label": "Решение",
    "workmanagement.approval.decision.approved": "Согласовать",
    "workmanagement.approval.decision.rejected": "Отклонить",
    "workmanagement.approval.comment.label": "Комментарий",
    "workmanagement.approval.comment.placeholder":
      "Укажите причину или комментарий…",
    "workmanagement.approval.submit": "Зафиксировать решение",
    "workmanagement.approval.submit_for_approval": "Отправить на согласование",
    "workmanagement.approval.preflight.title": "Предварительная проверка",
    "workmanagement.approval.preflight.submittable":
      "Можно отправить на согласование",
    "workmanagement.approval.preflight.not_submittable": "Не готово к отправке",
    "workmanagement.approval.history.title": "История согласования",
    "workmanagement.approval.history.empty": "Решений ещё нет",
    "workmanagement.approval.history.stage": "Этап {{stage}}",
    "workmanagement.approval.error.decision_required": "Выберите решение",
    "workmanagement.approval.error.comment_required":
      "При отклонении необходимо указать комментарий",
    "workmanagement.approval.error.not_found": "Проект не найден",
    // FR-PM-4 — Work Package view chrome (BP-01.02 §2.2). All
    // labels are flat keys under the `common` namespace per the
    // FR-UI-CORE-8 bridge contract. The Work Package → Task
    // hierarchy tree widget is the EXISTING generic
    // `apps/web/src/features/lists/tree/TreeView.tsx`; the
    // chrome below is consumed by the per-route surfaces in
    // `apps/web/src/features/workmanagement/`.
    "work_package.title": "Пакет работ",
    "work_package.contract_label": "Договор",
    "work_package.create": "Создать пакет работ",
    "work_package.update": "Обновить пакет работ",
    "work_package.list_empty": "Нет пакетов работ",
    "work_package.assign_parent_task": "Назначить родительскую задачу",
    "work_package.remove_parent_task": "Снять родительскую задачу",
    "work_package.field.code": "Код пакета",
    "work_package.field.name": "Название пакета",
    "work_package.field.status": "Статус пакета",
    "work_package.field.presentation_label": "Метка представления",
    "work_package.field.legal_document_ref": "Юридический документ",
    "work_package.field.project_id": "Проект",
    "work_package.field.parent_task_id": "Родительская задача",
    "work_package.field.row_version": "Версия строки",
    "work_package.status.planning": "Планирование",
    "work_package.status.executing": "Исполнение",
    "work_package.status.closed": "Закрыт",
    "work_package.presentation_mode.work_package": "Пакет работ",
    "work_package.presentation_mode.contract": "Договор",
    "work_package.section.children": "Задачи",
    "work_package.section.detail": "Сведения о пакете работ",
    "work_package.registry.heading": "Реестр пакетов работ",
    "work_package.registry.filter.project_label": "Проект",
    "work_package.registry.error.load_failed":
      "Не удалось загрузить реестр пакетов работ",
    "work_package.registry.error.create_failed":
      "Не удалось создать пакет работ",
    "work_package.error.name_required": "Введите название пакета работ",
    "work_package.error.name_too_long":
      "Название пакета работ не должно превышать 200 символов",
    "work_package.error.hierarchy_mandatory_levels":
      "Прямая связь «Проект → задача» запрещена; создайте пакет работ",
    "work_package.error.parent_missing": "Родительская задача не найдена",
    "work_package.error.parent_other_tenant":
      "Родительская задача принадлежит другому тенанту",
    "work_package.error.parent_other_work_package":
      "Родительская задача принадлежит другому пакету работ",
    "work_package.error.cycle_detected": "Связь обнажает цикл в иерархии задач",
    "work_package.error.second_immediate_parent":
      "Задача уже имеет непосредственную родительскую задачу",
    "work_package.error.legal_document_ref_invalid":
      "Указанный юридический документ не найден в этом тенанте",
  },
  kk: {
    "common.save": "Сақтау",
    "common.cancel": "Болдырмау",
    "common.loading": "Жүктелуде…",
    "common.error": "Қате",
    "common.retry": "Қайталау",
    "common.close": "Жабу",
    "common.back": "Артқа",
    "common.confirm": "Растау",
    "common.delete": "Жою",
    "common.edit": "Өзгерту",
    "common.create": "Құру",
    "common.search": "Іздеу",
    "common.copy": "Көшіру",
    "common.yes": "Иә",
    "common.no": "Жоқ",
    "common.required": "Міндетті өріс",
    "common.optional": "Міндетті емес өріс",
    // FR-UI-CORE-10 AC-5: inline validation chrome label.
    "validation.error.inline": "Тексеру қатесі",
    "validation.error.code_default": "Өрісте қате бар",
    // FR-UI-VIEW-1 AC-4 (rv-2): list-level 422 chrome.
    "validation.error.list": "Тексеру сәтсіз аяқталды",
    "conflict.title": "Өзгерістер қақтығысы",
    "conflict.body":
      "Бұл жазба соңғы жүктеуден кейін өзгертілді. Әрекетті таңдаңыз.",
    "conflict.reapply": "Өзгерістерімді ағымдағы нұсқаға қолдану",
    "conflict.refetch": "Соңғы нұсқаны алу",
    "conflict.discard": "Өзгерістерімді болдырмау",
    "conflict.serverVersion": "Ағымдағы нұсқа (сервер)",
    "conflict.yourVersion": "Сіздің өзгерістеріңіз",
    "conflict.currentRowVersion": "Жол нұсқасы",
    "deprovision.confirm": "Деактивацияны растау",
    // FR-UI-FORM-1 AC-2 — reference picker chrome (Kazakh).
    "reference.placeholder": "Іздеу…",
    "reference.empty": "Ештеңе табылмады",
    "reference.loading": "Жүктелуде…",
    "reference.error": "Нұсқаларды жүктеу мүмкін емес",
    "reference.cancel": "Болдырмау",
    "reference.select": "Таңдау",
    // FR-UI-PROM-2: non-skippable human-review gate chrome (Kazakh).
    "promote.gate.section.diff": "Өзгерістер",
    "promote.gate.section.assertions": "Өткен тексерулер",
    "promote.gate.section.artifact": "NEEDS_REVIEW пакеті",
    "promote.gate.diff.empty": "Тексеруге арналған өзгерістер жоқ.",
    "promote.gate.assertions.none":
      "Бұл промоушен агентпен жасалмаған. Тексерулер жазылмаған.",
    "promote.gate.assertions.recorded": "жазылған",
    "promote.gate.artifact.none":
      "NEEDS_REVIEW артефактісі тіркелмеген. Бұл адам өтініші.",
    "promote.gate.artifact.envelope_meta": "Конверт метадеректері",
    "promote.gate.artifact.task_spec_id": "task_spec_id",
    "promote.gate.artifact.task_spec_version": "task_spec_version",
    "promote.gate.artifact.kind": "kind",
    "promote.gate.artifact.agent_principal": "agent_principal",
    "promote.gate.artifact.submitted_at": "submitted_at",
    "promote.gate.artifact.attempt_count": "attempts",
    "promote.gate.artifact.spec_hash": "spec_hash",
    "promote.gate.artifact.final_status": "final_status",
    "promote.gate.artifact.spec": "spec",
    "promote.gate.artifact.attempts": "Әрекеттер",
    "promote.gate.artifact.started_at": "started_at",
    "promote.gate.artifact.completed_at": "completed_at",
    "promote.gate.artifact.terminal_failure_reason": "terminal_failure_reason",
    "promote.gate.artifact.bpm_step_log_snapshot": "bpm_step_log_snapshot",
    "promote.gate.artifact.open_full_envelope": "Артефакт конвертін толық ашу",
    "promote.gate.buttons.approve": "Промоушенді растау",
    "promote.gate.buttons.approving": "Растау…",
    "promote.gate.buttons.reject": "Қабылдамау",
    "promote.gate.required_acknowledgement":
      "Растау алдында қайтымсыздықты растаңыз",
    "promote.gate.approve.error": "Промоушенді растау мүмкін емес.",
    "promote.gate.reject.reason_label": "Қабылдамау себебі",
    "promote.gate.reject.reason_placeholder":
      "Не түзету керектігін сипаттаңыз…",
    "promote.gate.reject.confirm": "Қабылдамауды растау",
    "promote.gate.reject.pending": "Қабылдамау…",
    "promote.gate.reject.cancel": "Болдырмау",
    "promote.gate.reject.error": "Промоушенді қабылдамау мүмкін емес.",
    "promote.gate.error.load_failed": "Рецензияны жүктеу мүмкін емес:",
    // FR-UI-ADM-1: entity-type CRUD permission matrix editor chrome.
    "admin.matrix.title": "Рұқсаттар матрицасы",
    "admin.matrix.subtitle":
      "Рөлдер мен субъектілер түрлері үшін CRUD рұқсаттарын басқару",
    "admin.matrix.column.role": "Рөл",
    "admin.matrix.column.create": "Құру",
    "admin.matrix.column.read": "Оқу",
    "admin.matrix.column.update": "Өзгерту",
    "admin.matrix.column.delete": "Жою",
    "admin.matrix.column.all": "Барлығы",
    "admin.matrix.column.platform": "Платформа ауқымы (сервер басқарады)",
    "admin.matrix.action.save": "Рөлді сақтау",
    "admin.matrix.action.cancel": "Болдырмау",
    "admin.matrix.action.new_role": "Жаңа рөл",
    "admin.matrix.action.delete_role": "Рөлді жою",
    "admin.matrix.confirm.delete_role":
      "{{roleName}} рөлін жою керек пе? Бұл әрекетті қайтару мүмкін емес.",
    "admin.matrix.system_role.readonly": "Жүйелік рөл — тек оқу үшін",
    "admin.matrix.banner.pending_review":
      "{count, plural, one {# рөл staging-те өзгертілді — тексеруді күтуде} other {# рөл staging-те өзгертілді — тексеруді күтуде}}",
    "admin.matrix.banner.open_review": "Тексеруді ашу",
    "admin.matrix.loading": "Матрицаны жүктеу...",
    "admin.errors.staging_only.title":
      "Рұқсаттар редакторы тек staging-те қолжетімді",
    "admin.errors.staging_only.body":
      "Бұл бетті тенанттың staging ішкі доменінде ашыңыз.",
    "admin.errors.staging_only.code": "ENV_PRODUCTION",
    "admin.errors.version_conflict.title": "Нұсқа қақтығысы",
    "admin.errors.version_conflict.body":
      "Бұл рөл соңғы жүктеуден кейін өзгертілді.",
    "admin.errors.version_conflict.refetch": "Соңғы нұсқаны алу",
    "admin.errors.version_conflict.merge": "Менің өзгерістерімді біріктіру",
    "admin.errors.version_conflict.discard": "Менің өзгерістерімді болдырмау",
    "admin.errors.fetch_failure": "Рұқсаттар матрицасын жүктеу мүмкін емес",
    "admin.errors.permission_denied": "Рұқсаттарды өңдеуге құқығыңыз жоқ",
    "admin.errors.rate_limit": "Тым көп сұраулар. Күте тұрыңыз.",
    "admin.errors.stale_version":
      "Анықтама нұсқасы ескірген. Бетті қайта жүктеңіз.",
    "admin.errors.network": "Желі қатесі. Байланысты тексеріңіз.",
    // FR-UI-ADM-4 — admin viewer (role × entity-type field
    // classification) chrome. Flat-key layout; see
    // COMMON_RESOURCES doc comment.
    "admin.field_visibility.title":
      "Өріс көрінуі: {{roleName}} / {{entityType}}",
    "admin.field_visibility.subtitle":
      "Рөл {{roleName}} субъект түрі {{entityType}} үшін қандай өрістерді көреді",
    "admin.field_visibility.column.field": "Өріс",
    "admin.field_visibility.column.classification": "Жіктеу",
    "admin.field_visibility.states.visible": "көрінеді",
    "admin.field_visibility.states.hidden_cosmetic": "жасырын (косметика)",
    "admin.field_visibility.states.denied_security":
      "тыйым салынған (қауіпсіздік)",
    "admin.field_visibility.tooltip.denied_security_explainer":
      "Сервер бұл өрісті қайтармайды — қауіпсіздік шекарасы (FR-AUTH-7).",
    "admin.field_visibility.aria.visible": "көрінеді",
    "admin.field_visibility.aria.hidden_cosmetic":
      "көріну формуласымен жасырылған — косметика, мән серверде сақталады",
    "admin.field_visibility.aria.denied_security":
      "рұқсатпен тыйым салынған — қауіпсіздік шекарасы, өріс API жауаптарында қайтарылмайды",
    "admin.field_visibility.aria.table_label":
      "Рөл {{roleName}} үшін субъект түрі {{entityType}} бойынша өріс көрінуі",
    "admin.field_visibility.empty": "Көрсетілетін өрістер жоқ.",
    // FR-UI-ADM-6 — webhook endpoint admin editor chrome (Kazakh).
    "admin.webhooks.title": "Вебхук эндпоинттері",
    "admin.webhooks.subtitle":
      "Эндпоинттер мен белсенді қолтаңба кілттерін басқару (тек идентификаторлар)",
    "admin.webhooks.column.url": "URL",
    "admin.webhooks.column.process_def": "Процесс анықтамасы",
    "admin.webhooks.column.signing_key_id": "Қолтаңба кілті идентификаторы",
    "admin.webhooks.column.status": "Күй",
    "admin.webhooks.column.version": "Нұсқа",
    "admin.webhooks.column.actions": "Әрекеттер",
    "admin.webhooks.status.active": "белсенді",
    "admin.webhooks.status.disabled": "өшірілген",
    "admin.webhooks.action.add": "Эндпоинт қосу",
    "admin.webhooks.action.disable": "Өшіру",
    "admin.webhooks.action.delete": "Жою",
    "admin.webhooks.dialog.create.title": "Вебхук эндпоинтін жасау",
    "admin.webhooks.dialog.create.label.url": "URL",
    "admin.webhooks.dialog.create.label.process_def":
      "Процесс анықтамасының идентификаторы",
    "admin.webhooks.dialog.create.cta": "Жасау",
    "admin.webhooks.dialog.create.success.title": "Вебхук жасалды",
    "admin.webhooks.dialog.create.success.copy_key_value":
      "Бұл мәнді қазір көшіріңіз — оны кейін қайта көре алмайсыз.",
    "admin.webhooks.dialog.create.success.copied": "Көшірілді!",
    "admin.webhooks.dialog.conflict.title":
      "Басқа әкімші бұл эндпоинтті өзгертті",
    "admin.webhooks.dialog.conflict.refetch": "Соңғы нұсқаны алып, біріктіру",
    "admin.webhooks.dialog.conflict.discard": "Менің өзгерістерімді болдырмау",
    "admin.webhooks.empty.message":
      "Эндпоинттер әлі жоқ. Жасау үшін «Эндпоинт қосу» түймесін басыңыз.",
    "admin.webhooks.errors.staging_only":
      "Вебхук эндпоинттерін басқару тек staging-те қолжетімді.",
    "admin.webhooks.errors.version_conflict":
      "Басқа әкімші бұл эндпоинтті өзгертті. Соңғы нұсқаны алыңыз немесе өзгерістеріңізді болдырмаңыз.",
    "admin.webhooks.errors.fetch_failure":
      "Вебхук эндпоинттерін жүктеу мүмкін емес.",
    "admin.webhooks.errors.permission_denied":
      "Вебхук эндпоинттерін басқаруға құқығыңыз жоқ.",
    "admin.webhooks.errors.rate_limit":
      "Тым көп сұраулар. {{seconds}} секундтан кейін қайталаңыз.",
    "admin.webhooks.banner.pending_review":
      "{count, plural, one {# эндпоинт staging-те өзгертілді — тексеруді күтуде} other {# эндпоинт staging-те өзгертілді — тексеруді күтуде}}",
    "admin.webhooks.banner.open_review": "Тексеруді ашу",
    "admin.webhooks.table.aria_label": "Вебхук эндпоинттері кестесі",
    "admin.webhooks.keys.title": "Webhook HMAC кілттері",
    "admin.webhooks.keys.active": "Белсенді",
    "admin.webhooks.keys.grace_period": "Жеңілдік кезеңі",
    "admin.webhooks.keys.expired": "Мерзімі өткен",
    "admin.webhooks.keys.rotate_action": "Кілтті ауыстыру",
    "admin.webhooks.keys.issue_action": "Кілт шығару",
    "admin.webhooks.keys.revoke_action": "Кері шақыру",
    "admin.webhooks.keys.grace_window_label": "Жеңілдік кезеңі",
    "admin.webhooks.keys.grace_window_hours":
      "{hours, plural, one {# сағат} other {# сағат}}",
    "admin.webhooks.keys.grace_until_label": "Мерзімі",
    "admin.webhooks.keys.expired_at_label": "Мерзімі өткен",
    "admin.webhooks.keys.key_id_label": "Кілт ID",
    "admin.webhooks.keys.created_at_label": "Жасалды",
    "admin.webhooks.keys.state_label": "Күй",
    "admin.webhooks.keys.one_time_copy_title": "Жаңа кілт мәні (бір реттік)",
    "admin.webhooks.keys.one_time_copy_warning":
      "Мәнді қазір көшіріңіз. Ол қайта көрсетілмейді.",
    "admin.webhooks.keys.copy_button": "Көшіру",
    "admin.webhooks.keys.done_button": "Дайын",
    "admin.webhooks.keys.value_cleared_message":
      "Қауіпсіздік үшін кілт мәні жойылды. Қайта ауыстырыңыз.",
    "admin.webhooks.keys.destructive_rotate_title":
      "Жеңілдік кезеңінде кілтті ауыстыру?",
    "admin.webhooks.keys.destructive_rotate_warning":
      "Кілт қазір жеңілдік кезеңінде. Ауыстыру оны қайтарып, жеңілдік кезеңін аяқтайды. Ағымдағы webhook жеткізілімдері сәтсіз аяқталуы мүмкін.",
    "admin.webhooks.keys.confirm_rotate_button": "Ауыстыруды растау",
    "admin.webhooks.keys.cancel_button": "Болдырмау",
    "admin.webhooks.keys.revoke_confirm_title": "Кілтті қайтару?",
    "admin.webhooks.keys.revoke_confirm_warning":
      "Қайтару бірден жүргізіледі және қайтарылмайды. Растау үшін кілт ID енгізіңіз.",
    "admin.webhooks.keys.revoke_confirm_placeholder": "Кілт ID енгізіңіз",
    "admin.webhooks.keys.new_deliveries_tooltip":
      "Осы ауыстырудан КЕЙІН жасалған webhook жеткізілімдері осы кілтті пайдаланады. Ауыстыруға дейін жүріп жатқан жеткізілімдер {{grace_until}} уақытына дейін ескі кілтті пайдаланады.",
    "admin.webhooks.keys.loading_error": "HMAC кілттерін жүктеу мүмкін емес.",
    "admin.webhooks.keys.retry": "Қайталау",
    "admin.webhooks.keys.permission_denied":
      "HMAC кілттерін басқаруға құқығыңыз жоқ.",
    "admin.webhooks.keys.countdown.separator": "және",
    "admin.webhooks.keys.countdown.expired": "Мерзімі өткен",
    "admin.webhooks.keys.grace_window_minutes_min": "минимум {{minutes}} минут",
    "admin.webhooks.keys.grace_window_minutes_max":
      "максимум {{minutes}} минут",
    "admin.webhooks.keys.errors.invalid_grace_window":
      "Жеңілдік кезеңінің терезесі жарамсыз. Рұқсат етілген ауқым: 1–72 сағат.",
    "admin.webhooks.keys.errors.staging_only":
      "Webhook HMAC кілттерін басқару тек staging-те қолжетімді.",
    // FR-UI-ADM-8 — effects admin (Kazakh)
    "admin.effects.http.page.title": "HTTP эффект эндпоинттері",
    "admin.effects.http.empty": "HTTP эндпоинттері конфигурацияланбаған.",
    "admin.effects.http.table_aria_label": "HTTP эффекттер кестесі",
    "admin.effects.http.column.url": "URL",
    "admin.effects.http.column.method": "Әдіс",
    "admin.effects.http.column.status": "Күй",
    "admin.effects.http.column.actions": "Әрекеттер",
    "admin.effects.http.action.create": "Эндпоинт қосу",
    "admin.effects.http.action.disable": "Өшіру",
    "admin.effects.http.action.delete": "Жою",
    "admin.effects.http.field.url": "URL",
    "admin.effects.http.field.method": "HTTP әдісі",
    "admin.effects.http.field.timeout": "Күту уақыты (секунд)",
    "admin.effects.http.dialog.create.title": "HTTP эндпоинт қосу",
    "admin.effects.http.dialog.create.success.title": "Эндпоинт жасалды",
    "admin.effects.http.dialog.create.success.description":
      "Құпияны қазір көшіріңіз — ол қайта көрсетілмейді.",
    "admin.effects.http.dialog.create.success.copy_secret": "Құпияны көшіру",
    "admin.effects.email.page.title": "Email хабарлама арналары",
    "admin.effects.email.empty": "Email арналары конфигурацияланбаған.",
    "admin.effects.email.table_aria_label": "Email арналар кестесі",
    "admin.effects.email.column.from": "Жіберуші мекенжайы",
    "admin.effects.email.column.subject": "Тақырып",
    "admin.effects.email.column.locales": "Тілдер",
    "admin.effects.email.column.status": "Күй",
    "admin.effects.email.column.actions": "Әрекеттер",
    "admin.effects.email.action.create": "Арна қосу",
    "admin.effects.email.action.disable": "Өшіру",
    "admin.effects.email.action.delete": "Жою",
    "admin.effects.email.field.from_address": "Жіберуші мекенжайы",
    "admin.effects.email.field.subject": "Тақырып",
    "admin.effects.email.field.provider": "Провайдер",
    "admin.effects.email.field.body": "Хат мәтіні (тілдік қолдаумен)",
    "admin.effects.email.dialog.create.title": "Email арна қосу",
    "admin.effects.email.dialog.create.success.title": "Арна жасалды",
    "admin.effects.email.dialog.create.success.description":
      "SMTP құпиясын қазір көшіріңіз — ол қайта көрсетілмейді.",
    "admin.effects.email.dialog.create.success.copy_secret": "Құпияны көшіру",
    "admin.effects.status.active": "белсенді",
    "admin.effects.status.disabled": "өшірілген",
    "admin.effects.dialog.conflict.title": "Нұсқа қақтығысы",
    "admin.effects.dialog.conflict.server_version": "Сервер нұсқасы",
    "admin.effects.dialog.conflict.discard": "Өзгерістерімді болдырмау",
    "admin.effects.dialog.conflict.refetch": "Деректерді жаңарту",
    // FR-UI-ADM-10 — secret reference manager admin chrome (Kazakh).
    "admin.secrets.title": "Құпиялар",
    "admin.secrets.create_button": "Құпия жасау",
    "admin.secrets.empty_state": "Әзірге құпиялар жасалмаған.",
    "admin.secrets.table.name": "Атауы",
    "admin.secrets.table.ref_id": "Сілтеме ID",
    "admin.secrets.table.status": "Күйі",
    "admin.secrets.table.created_at": "Жасалған",
    "admin.secrets.table.updated_at": "Жаңартылған",
    "admin.secrets.table.version": "Нұсқа",
    "admin.secrets.table.actions": "Әрекеттер",
    "admin.secrets.dialog.create.title": "Құпия жасау",
    "admin.secrets.dialog.create.description":
      "Атауын енгізіңіз. Сервер мәнді жасайды.",
    "admin.secrets.dialog.create.name_label": "Құпия атауы",
    "admin.secrets.dialog.create.name_hint":
      "120 таңбаға дейін. Тек осы тізімде сәйкестендіру үшін қолданылады.",
    "admin.secrets.dialog.create.success.title": "Құпия жасалды",
    "admin.secrets.dialog.create.success.copy_secret_value":
      "Құпия мәнді қазір көшіріңіз — ол қайта көрсетілмейді.",
    "admin.secrets.dialog.create.success.copied": "Көшірілді!",
    "admin.secrets.dialog.impact.title": "Құпияны жою",
    "admin.secrets.dialog.impact.subtitle":
      "«{{name}}» құпиясы жойылады. Алдымен оның қайда пайдаланылатынын тексеріңіз.",
    "admin.secrets.dialog.impact.references_label":
      "{{count}} эффект конфигурациясы осы құпияға сілтеме жасайды:",
    "admin.secrets.dialog.impact.kind_http_endpoint": "HTTP эндпойнт",
    "admin.secrets.dialog.impact.kind_email_channel": "Email арнасы",
    "admin.secrets.dialog.impact.safe_to_delete":
      "Бірде-бір эффект конфигурациясы осы құпияға сілтеме жасамайды. Жою қауіпсіз.",
    "admin.secrets.dialog.impact.acknowledge_label":
      "Мен осы эффектілер жойылғаннан кейін құпияға қол жеткізуді жоғалтатынын түсінемін.",
    "admin.secrets.banner.pending_review":
      "{{count}} құпия промоушен алдында тексеруді күтуде",
    "admin.secrets.banner.open_review": "Тексеруді ашу",
    "admin.secrets.status.active": "белсенді",
    "admin.secrets.status.disabled": "өшірулі",
    // FR-UI-ADM-11 -- template registry admin chrome (Kazakh).
    "admin.templates.registry.title": "Шаблондар тізілімі",
    "admin.templates.registry.column.template_id": "Шаблон ID",
    "admin.templates.registry.column.version": "Нұсқа",
    "admin.templates.registry.column.description": "Сипаттама",
    "admin.templates.registry.column.publisher_kind": "Жариялаушы",
    "admin.templates.registry.column.pre_vetted": "Pre-vetted",
    "admin.templates.registry.column.published_at": "Жарияланды",
    "admin.templates.registry.column.published_by": "Кім",
    "admin.templates.registry.column.content_hash": "Content hash",
    "admin.templates.registry.column.actions": "Әрекеттер",
    "admin.templates.registry.badge.auto_activates":
      "Provisioning кезінде автоматты түрде белсендіріледі",
    "admin.templates.registry.badge.tooltip":
      "Бұл шаблон provisioning кезінде staging-тен production-ға автоматты түрде промоутталады. Осы бет арқылы қолмен орнатқанда қалыпты human-review қажет.",
    "admin.templates.registry.action.install": "Орнату",
    "admin.templates.registry.action.installing": "Орнатылуда…",
    "admin.templates.registry.action.install_aria":
      "{{template_id}} шаблонын {{version}} нұсқасымен орнату",
    "admin.templates.registry.success.installed":
      "Шаблон staging-ке сәтті орнатылды",
    "admin.templates.registry.success.installed.review_link":
      "Промоушен тексеруін ашу",
    "admin.templates.registry.empty.message":
      "Әзірге жарияланған шаблондар жоқ.",
    "admin.templates.registry.table.aria_label": "Шаблондар тізілімі кестесі",
    "admin.templates.registry.errors.staging_only":
      "Шаблондарды орнату тек staging-те қолжетімді.",
    "admin.templates.registry.errors.permission_denied":
      "Шаблондарды орнатуға рұқсатыңыз жоқ.",
    "admin.templates.registry.errors.fetch_failure":
      "Шаблондар тізілімін жүктеу мүмкін болмады.",
    "admin.templates.registry.errors.rate_limit":
      "Тым көп сұраулар. Біраз күтіңіз.",
    "admin.templates.registry.errors.seeding_failed":
      "Шаблонды орнату мүмкін болмады: сидинг қатесі.",
    "admin.templates.registry.errors.publisher_not_allowed":
      "Шаблон жариялаушысы осы тенант үшін рұқсат етілмеген.",
    "admin.templates.registry.errors.template_not_found":
      "Шаблон немесе нұсқа табылмады.",
    // FR-UI-ADM-13 — template export/import admin chrome (Kazakh).
    "admin.templates.export_import.action.export": "Экспорт",
    "admin.templates.export_import.action.exporting": "Экспорт…",
    "admin.templates.export_import.action.export_aria":
      "{{template_id}} шаблонын {{version}} нұсқасымен экспорттау",
    "admin.templates.export_import.action.import": "Импорт",
    "admin.templates.export_import.notice.operator_console_required":
      "Шаблондарды импорттау үшін оператор консолін пайдаланыңыз.",
    "admin.templates.export_import.dialog.import.title": "Шаблонды импорттау",
    "admin.templates.export_import.dialog.import.file_label":
      "Шаблон пакетінің файлы",
    "admin.templates.export_import.dialog.import.submit": "Жіберу",
    "admin.templates.export_import.dialog.import.submit_disabled_tooltip":
      "Жіберу үшін оператор консолін пайдаланыңыз",
    "admin.templates.export_import.dialog.replace.title":
      "Бар шаблонды ауыстыру керек пе?",
    "admin.templates.export_import.dialog.replace.existing_label": "Бар:",
    "admin.templates.export_import.dialog.replace.imported_label":
      "Импортталатын:",
    "admin.templates.export_import.dialog.replace.diff":
      "бар v{{existing_version}} → импорт v{{import_version}}",
    "admin.templates.export_import.dialog.replace.confirm": "Ауыстыру",
    "admin.templates.export_import.dialog.replace.cancel": "Бас тарту",
    "admin.templates.export_import.dialog.replace.server_rules_notice":
      "Жібергенде серверлік нұсқа тұрақтылығының ережелері қолданылады — бұл растау оларды айналдырмайды.",
    "admin.templates.export_import.errors.manifest_missing":
      "manifest.json мұрағатта табылмады.",
    "admin.templates.export_import.errors.manifest_invalid_json":
      "manifest.json жарамсыз JSON.",
    "admin.templates.export_import.errors.cross_publisher_kind":
      "Операторлар тек платформа жариялаған шаблондарды импорттай алады.",
    "admin.templates.export_import.errors.manifest_incomplete":
      "Манифестте міндетті өрістердің барлығы жоқ.",
    "admin.templates.export_import.errors.file_pick_failed":
      "Файлды оқу мүмкін болмады.",
    "admin.templates.export_import.errors.payload_too_large":
      "Пакет тым үлкен.",
    "admin.templates.export_import.errors.validation_failed":
      "Пакет тексеруден өтпеді.",
    "admin.templates.export_import.tooltip.replaces_preserves_customizations":
      "FR-TPL-4 орнату кезінде тенант баптауларын сақтайды — шаблон нұсқасын қайта импорттау бұған әсер етпейді.",
    "admin.templates.export_import.source.imported": "импортталған",
    "admin.templates.export_import.source.native": "табиғи",
    // FR-UI-ADM-3 — formula-editor widget chrome (Kazakh).
    "formula.editor.label": "Формула",
    "formula.editor.validate": "Тексеру",
    "formula.editor.type": "Түрі: {{type}}",
    "formula.editor.placeholder": "Формуланы енгізіңіз…",
    "formula.editor.a11y.description":
      "Серверлік валидациясы бар формула редакторы. Сервер синтаксис пен түрді тексереді; формулалар жергілікті түрде есептелмейді.",
    // FR-UI-LIST-1 — definition-driven list view chrome (Kazakh).
    "lists.title": "Жазбалар",
    "lists.table.aria_label": "Субъект жазбалары кестесі",
    "lists.empty.no_records": "Көрсетілетін жазбалар жоқ.",
    "lists.empty.permission_denied":
      "Бұл жазбаларға қол жеткізу құқығыңыз жоқ.",
    "lists.filter.group_label": "Сүзгілер",
    "lists.filter.placeholder": "Сүзгі…",
    "lists.filter.aria_input": "{{field}} өрісі бойынша сүзгі",
    "lists.sort.asc": "▲",
    "lists.sort.desc": "▼",
    "lists.sort.toggle_aria": "{{field}} өрісі үшін сұрыптауды ауыстыру",
    "lists.pagination.next": "Алға",
    "lists.pagination.aria_label": "Беттік навигация",
    "lists.pagination.page_size": "Бет өлшемі: {{pageSize}}",
    "lists.boolean.yes": "Иә",
    "lists.boolean.no": "Жоқ",
    "lists.error.fetch_failure": "Жазбаларды жүктеу мүмкін болмады.",
    // FR-UI-VIEW-2 — Tree view chrome (Kazakh). See Russian block for
    // rationale on the locked-copy key.
    "lists.tree.title": "Жазбалар ағашы",
    "lists.tree.error.empty": "Бұл субъектіде жазбалар жоқ.",
    "lists.tree.error.fetch_failure": "Жазбалар ағашын жүктеу мүмкін болмады.",
    "lists.tree.error.permission_denied":
      "Бұл жазбаларға қол жеткізу құқығыңыз жоқ.",
    "lists.tree.error.stale_version":
      "Бұл субъектінің құрылымы өзгерді. Бетті жаңартыңыз.",
    "lists.tree.error.rate_limit":
      "Тым көп сұраулар. Біраздан соң қайталап көріңіз.",
    "lists.tree.error.validation_error":
      "Бұл субъекті үшін ағаш конфигурациясы сервермен қабылданбады.",
    "lists.tree.count_banner": "{{shown}} / {{total}} жазба көрсетілді.",
    // FR-UI-VIEW-3 — Gantt view chrome (Kazakh). See Russian block for
    // the key-by-key rationale; the canonical sentence for the
    // {count}-bearing capBanner follows the i18next interpolation
    // contract documented at the top of i18next.init.
    "lists.gantt.title": "Гантт диаграммасы",
    "lists.gantt.noStartField": "басталу күні жоқ",
    "lists.gantt.zeroDuration": "басталу күні аяқталу күнінен кейін",
    "lists.gantt.capBanner":
      "{{total}}-дан {{count}} жазба көрсетілді. Қалғандары жүктелмеді.",
    "lists.gantt.loadMore": "Тағы жүктеу",
    "lists.gantt.invalidConfig":
      "Гантт диаграммасының конфигурациясы жоқ немесе дұрыс емес.",
    "lists.gantt.dependsOnLabel": "Тәуелділіктер",
    "lists.gantt.conflictOnMove":
      "Жазбаны басқа пайдаланушы өзгертті. Бетті жаңартыңыз.",
    // FR-UI-PROC-1 — execution-trace viewer + failed-instance
    // dashboard chrome (Kazakh).
    "processes.failed.title": "Сәтсіз процестер",
    "processes.failed.subtitle":
      "Осы тенант үшін failed күйіндегі процестер. Қадамды қайталауға немесе дананы болдырмауға болады.",
    "failed_instance.empty": "Сәтсіз даналар жоқ.",
    "failed_instance.list_aria_label":
      "Осы тенант үшін сәтсіз процестер даналары",
    "failed_instance.failed_node_label": "Сәтсіздік түйіні",
    "failed_instance.reason_label": "Себеп",
    "failed_instance.retries_label": "Қайталау саны",
    "failed_instance.trace_label": "Сәтсіздік трассировкасы",
    "failed_instance.trace_summary": "Журналдағы жазбалар: {{count}}",
    "failed_instance.view_trace": "Трассировканы ашу",
    "failed_instance.retry_button": "Қадамды қайталау",
    "failed_instance.retry_pending": "Қайталау…",
    "failed_instance.retry_error": "Қадамды қайталау мүмкін болмады.",
    "failed_instance.retry_not_failed":
      "Дана failed күйінде емес. Бетті жаңартыңыз.",
    "failed_instance.cancel_button": "Дананы болдырмау",
    "failed_instance.cancel_pending": "Болдырмау…",
    "failed_instance.cancel_error": "Дананы болдырмау мүмкін болмады.",
    "failed_instance.cancel_confirm_title": "Дананы болдырмау керек пе?",
    "failed_instance.cancel_confirm_body":
      "Процесс данасы тоқтатылады. Сәтсіздік жазбасы аудит үшін сақталады; кейінгі қадамдар орындалмайды.",
    "failed_instance.restored_orphan_badge": "Қалпына келтірілген orphan",
    "failed_instance.restored_orphan_explainer":
      "Сәтсіздік тенантты қалпына келтіру кезінде тіркелді. Бастапқы қадам қайта орындалмауы мүмкін.",
    "failed_instance.state.fetch_failure":
      "Сәтсіз даналар тізімін жүктеу мүмкін болмады.",
    "trace.title": "Орындау трассировкасы",
    "trace.subtitle":
      "Таңдалған процес данасының қадамдар журналы, хронологиялық ретпен.",
    "trace.table_aria_label": "Орындау трассировкасының қадамдар журналы",
    "trace.column.node_id": "Түйін",
    "trace.column.status": "Күй",
    "trace.column.retry_count": "Қайталаулар",
    "trace.column.started_at": "Басталды",
    "trace.column.duration_ms": "Ұзақтығы",
    "trace.column.error_message": "Қате",
    "trace.empty": "Қадамдар журналында жазбалар жоқ.",
    "trace.state.fetch_failure": "Қадамдар журналын жүктеу мүмкін болмады.",
    // FR-UI-OPS-1 — operator console chrome (Kazakh). Flat-key
    // layout matching the rest of the bundle; see the
    // COMMON_RESOURCES doc comment for the rationale.
    "operator.title": "Оператор консолі",
    "operator.summary.title": "Тенанттар бойынша жиынтық",
    "operator.summary.tenantCount": "Тенанттар",
    "operator.summary.activeInstances": "Белсенді даналар",
    "operator.summary.errorRate": "Қате деңгейі",
    "operator.summary.empty": "Тенанттар әлі жоқ",
    "operator.summary.computedAt": "Жаңартылды",
    // FR-UI-OPS-3 — per-tenant error-count badge suffix
    // (renders as e.g. "6 қат" inside the redesigned <Badge> cell).
    "operator.summary.errorUnit": "қат",
    "operator.tokenPrompt.label": "Оператор токені",
    "operator.tokenPrompt.button": "Кіру",
    "operator.tokenPrompt.placeholder": "Оператор токенін қойыңыз",
    "operator.tokenPrompt.error.unauthorized":
      "Авторизация қатесі. Токенді тексеріп, қайталаңыз.",
    "apex.title": "ASCOA Platform",
    "apex.subtitle":
      "Бұл платформалық apex. Оператор консолі — тенанттарды басқару, депровизия және кросс-тенант бақылау үшін.",
    "apex.how_to_enter_title": "Оператор консоліне кіру",
    "apex.how_to_enter_body":
      "Оператор консолі сол доменде /operator/ арқылы қол жетімді. Платформалық токен қажет (PLATFORM_ADMIN_TOKEN). Төмендегі батырма токен гейтін ашады; қабылданғаннан кейін кросс-тенант сводкасына түсесіз.",
    "apex.open_operator": "Оператор консолін ашу",
    "apex.tour_title": "Тенант URL-дары",
    "apex.tour_body":
      "Әр тенант өзіндік субдоменде жұмыс істейді. Тенанттар тізімі оператор консолінде кіруден кейін қол жетімді. Тенант өз субдомені арқылы ашылады; apex-тен тенантқа кіру мүмкін емес.",
    "apex.tour_local": "әдепкі dev-тенант",
    "apex.tour_tenant": "тенант субдомені (провизиядан кейін)",
    "operator.error.fetchFailure": "Жиынтықты жүктеу мүмкін болмады.",
    "operator.error.retry": "Қайталау",
    "operator.summary.table.tenant": "Тенант",
    "operator.summary.table.instanceCount": "Белсенді даналар",
    "operator.summary.table.errorCount": "Қателер",
    "operator.summary.table.activeSandboxCount": "Белсенді песочницалар",
    "operator.summary.table.aria_label": "Тенанттар бойынша бөлу",
    // FR-UI-OPS-2 — operator nav shell + drill-down + audit
    // placeholder chrome (Kazakh).
    "operator.nav.label": "Оператор бөлімдері",
    "operator.nav.summary": "Жиынтық",
    "operator.nav.tenants": "Тенанттар",
    "operator.nav.effects": "Эффекттер / Операциялар",
    "operator.nav.audit": "Аудит",
    "operator.drillDown.title": "Тенант мәліметтері",
    "operator.drillDown.instanceCount": "Белсенді даналар",
    "operator.drillDown.errorCount": "Қателер",
    "operator.drillDown.activeSandboxCount": "Белсенді песочницалар",
    "operator.drillDown.openAdmin": "Тенант /admin ашу",
    "operator.drillDown.openFor": "{{slug}} тенантының мәліметтерін ашу",
    "operator.drillDown.close": "Жабу",
    "operator.audit.title": "Аудит",
    "operator.audit.placeholder": "Аудит журналы әлі іске асырылмаған.",
    "operator.tenants.loadError": "Тенанттарды жүктеу мүмкін болмады.",
    "operator.topbar.localeLabel": "Интерфейс тілі",
    "operator.tenants.list.title": "Тенанттар",
    "operator.tenants.list.empty": "Тенанттар табылмады.",
    "operator.tenants.list.refresh": "Жаңарту",
    "operator.tenants.list.loading": "Тенанттар жүктелуде…",
    "operator.tenants.list.errorPrefix": "Тенанттарды жүктеу мүмкін болмады:",
    "operator.tenants.list.deprovision": "Өшіру",
    "operator.tenants.list.ariaLabel": "Өшіру әрекеттері бар тенанттар тізімі",
    "operator.tenants.list.col.slug": "Слаг",
    "operator.tenants.list.col.name": "Атауы",
    "operator.tenants.list.col.tier": "Деңгей",
    "operator.tenants.list.col.status": "Күй",
    "operator.tenants.list.col.tenantId": "Жалға алушы ID",
    "operator.tenants.list.col.actions": "Әрекеттер",
    // ISS-509 (BA ruling): localized "Unknown" status label for the
    // decoder fallback path (status field absent from wire).
    "operator.tenants.list.status.unknown": "белгісіз",
    // ISS-510: localized status + tier value labels.
    "operator.tenants.list.status.active": "белсенді",
    "operator.tenants.list.status.disabled": "өшірілген",
    "operator.tenants.list.status.quiesced": "тоқтатылған",
    "operator.tenants.list.tier.small": "Шағын",
    "operator.tenants.list.tier.middle": "Орташа",
    "operator.tenants.list.tier.corporate": "Корпоративтік",
    // FR-UI-CABINET-1 — tenant workspace shell ("My Cabinet") chrome
    // (Kazakh locale). Flat-key layout per COMMON_RESOURCES convention.
    "cabinet.title": "Тенант жұмыс кеңістігі",
    "cabinet.nav.label": "Жұмыс кеңістігі бөлімдері",
    "cabinet.nav.cabinet": "Менің кабинетім",
    "cabinet.nav.tasks": "Менің тапсырмаларым",
    "cabinet.nav.processes": "Процестер",
    "cabinet.nav.admin": "Әкімшілік",
    "cabinet.nav.section.records": "Жазбалар",
    "cabinet.nav.section.admin": "Әкімшілік",
    "cabinet.landing.title": "Менің кабинетім",
    "cabinet.landing.subtitle": "Жұмысыңыз бір көріністе.",
    "cabinet.landing.tiles_label": "Қорытынды",
    "cabinet.landing.tasks_due_soon": "Жуық тапсырмалар",
    "cabinet.landing.open_processes": "Ашық процестер",
    "cabinet.landing.failed_processes": "Сәтсіз процестер",
    "cabinet.landing.recent_records": "Соңғы жазбалар",
    "cabinet.landing.recent_records.placeholder":
      "Нысан түрлерінің рұқсаттары реттелген кезде соңғы жазбалар осында көрсетіледі.",
    "cabinet.landing.recent_records.configureAction": "Рұқсаттарды реттеу",
    "cabinet.admin.title": "Әкімшілік",
    "cabinet.admin.subtitle": "Тенант конфигурациясын басқару.",
    "cabinet.admin.grid_label": "Әкімшілік бөлімдері",
    "cabinet.admin.denied": "Сізде әкімшілік бөліміне рұқсат жоқ.",
    "cabinet.admin.tile.users": "Рөлдер",
    "cabinet.admin.tile.permissions_matrix": "Рұқсат матрицасы",
    "cabinet.admin.tile.record_field_permissions": "Жазба және өріс рұқсаттары",
    "cabinet.admin.tile.webhooks": "Вебхук-эндпоинттері",
    "cabinet.admin.tile.hmac_keys": "HMAC кілттері",
    "cabinet.admin.tile.http_endpoints": "HTTP-эндпоинттері",
    "cabinet.admin.tile.email_channels": "Email арналары",
    "cabinet.admin.tile.secrets": "Құпиялар",
    "cabinet.admin.tile.templates": "Үлгілер",
    "cabinet.admin.tile.template_updates": "Үлгі жаңартулары",
    "cabinet.admin.tile.tier": "Деңгей және квоталар",
    "cabinet.admin.tile.audit": "Аудит журналы",
    "cabinet.admin.tile.forms": "Формалар",
    "cabinet.admin.tile.processes": "Процестер",
    "cabinet.errors.fetch_failure": "Жүктеу мүмкін болмады.",
    "cabinet.errors.permission_denied": "Сізде бұған рұқсат жоқ.",
    "cabinet.errors.rate_limit": "Сұраныс тым көп. Күте тұрыңыз.",
    "cabinet.errors.stale_version": "Бұл нұсқа енді қолжетімді емес.",
    "cabinet.errors.tile_label": "Қорытынды",
    "cabinet.errors.retry_after": "{{seconds}} секундтан кейін қайталаңыз.",
    // FR-UI-ADM-2 — record-level + field-level permission editors
    // (Kazakh locale). Flat-key layout per COMMON_RESOURCES convention.
    "admin.record_permissions.page_title":
      "Жазба деңгейіндегі және өрістер деңгейіндегі рұқсаттар",
    "admin.record_permissions.role": "Роль",
    "admin.record_permissions.cell.ownership": "Иелік ету ережесі",
    "admin.record_permissions.cell.fields": "Өріс рұқсаттары",
    "admin.record_permissions.cell.owner": "Иесі өрісі",
    "admin.record_permissions.editor.title": "Жазба ережесін редакциялау",
    "admin.record_permissions.editor.tab.ownership": "Иелік",
    "admin.record_permissions.editor.tab.condition": "Шарт",
    "admin.record_permissions.editor.expr_label": "Өрнек (expr-lang)",
    "admin.record_permissions.editor.preview_button": "Алдын ала қарау",
    "admin.record_permissions.editor.preview.loading": "Үлгі жүктелуде…",
    "admin.record_permissions.editor.preview.matches":
      "{{matches}} / {{total}} үлгі жазбадан сәйкес келеді",
    "admin.record_permissions.conflict.title": "Нұсқа қақтығысы",
    "admin.record_permissions.conflict.body":
      "Бұл рұқсаттар соңғы жүктеуден бері өзгерді. Әрекетті таңдаңыз.",
    "admin.record_permissions.conflict.refetch": "Соңғы нұсқасын алу",
    "admin.record_permissions.conflict.merge": "Менің өзгерістерімді біріктіру",
    "admin.record_permissions.conflict.discard": "Менің өзгерістерімді тастау",
    "admin.record_permissions.errors.staging_only_body":
      "Рұқсат редакторы тек staging-та қол жетімді.",
    "admin.record_permissions.error.title": "Жүктеу қатесі",
    "admin.field_permissions.title": "Өріс рұқсаттарын редакциялау",
    "admin.field_permissions.field": "Өріс",
    "admin.field_permissions.state": "Күй",
    "admin.field_permissions.state.readable": "Оқу",
    "admin.field_permissions.state.writable": "Оқу және жазу",
    "admin.field_permissions.state.hidden": "Жасырын",
    "admin.entity_field_owner.title": "Иесі өрісі",
    "admin.entity_field_owner.field_label": "Иесі өрісі",

    // FR-UI-ADM-5 Kazakh locale additions.
    "admin.users.title": "Пайдаланушылар мен рөлдер",
    "admin.users.subtitle":
      "Keycloak рөлдерін тенант пайдаланушыларына тағайындау. Өзгерістер келесі OIDC токенін жаңартқанда күшіне енеді.",
    "admin.users.column.display_name": "Аты",
    "admin.users.column.email": "Email",
    "admin.users.column.assigned_roles": "Тағайындалған рөлдер",
    "admin.users.column.actions": "Әрекеттер",
    "admin.users.column.select_all": "Барлығын таңдау",
    "admin.users.column.select_user": "{{name}} пайдаланушысын таңдау",
    "admin.users.action.assign_roles": "Рөлдерді тағайындау",
    "admin.users.action.assign_roles_for": "{{name}} үшін рөлдерді тағайындау",
    "admin.users.action.sync_refresh": "Синхрондауды жаңарту",
    "admin.users.action.sync_refresh_cooldown": "Күте тұрыңыз…",
    "admin.users.sync_status.ok": "Синхрондау қалыпты",
    "admin.users.sync_status.degraded": "Синхрондау ішінара",
    "admin.users.sync_status.failed": "Синхрондау орындалмады",
    "admin.users.dialog.role_picker.title": "Рөлдерді таңдаңыз",
    "admin.users.dialog.role_picker.label.roles": "Рөлдер",
    "admin.users.dialog.role_picker.empty":
      "Қол жетімді рөлдер жоқ. Keycloak синхрондаңыз.",
    "admin.users.dialog.role_picker.cta": "Сақтау",
    "admin.users.dialog.role_picker.error": "Сақтау мүмкін болмады.",
    "admin.users.dialog.bulk_assign.title": "Жаппай рөл тағайындау ({{count}})",
    "admin.users.dialog.bulk_assign.cta": "{{count}} тағайындау",
    "admin.users.dialog.bulk_assign.roles_placeholder":
      "admin, viewer, editor (үтір арқылы)",
    "admin.users.dialog.bulk_assign.partial_success":
      "{{assigned}} пайдаланушыға тағайындалды.",
    "admin.users.dialog.bulk_assign.failures_label": "Қателіктер ({{count}})",
    "admin.users.dialog.bulk_assign.dismiss": "Дайын",
    "admin.users.dialog.bulk_assign.error":
      "Жаппай тағайындау сәтсіз аяқталды.",
    "admin.users.bulk.selected_count": "Таңдалды: {{count}}",
    "admin.users.orphan_panel.toggle":
      "Жетім пайдаланушылар ({{count}}) — тексеру",
    "admin.users.orphan_panel.title": "Жетім пайдаланушылар",
    "admin.users.orphan_panel.empty": "Жетім пайдаланушылар жоқ.",
    "admin.users.orphan_panel.remove_action":
      "{{id}} үшін рөл тағайындауларын жою",
    "admin.users.orphan_panel.remove_action_short": "Тазарту",
    "admin.users.orphan_panel.cleaned": "Тазартылды",
    "admin.users.notice.takes_effect_next_login":
      "Рөлдер пайдаланушының келесі кіруінде күшіне енеді.",
    "admin.users.notice.dismiss": "Жабу",
    "admin.users.empty.message": "Пайдаланушылар табылмады.",
    "admin.users.error.fetch_failure":
      "Пайдаланушыларды жүктеу мүмкін болмады.",
    "admin.users.error.permission_denied":
      "Пайдаланушыларды көру құқығыңыз жоқ.",
    "admin.users.error.stale_version": "Нұсқа ескірген. Бетті жаңартыңыз.",
    // FR-UI-ADM-9 — effects operations panel chrome.
    "admin.effects.metrics.page_title": "Эффекттер операциялық тақтасы",
    "admin.effects.metrics.section.metrics": "Арна бойынша метрикалар",
    "admin.effects.metrics.section.circuit":
      "Эндпойнт бойынша circuit-breaker күйі",
    "admin.effects.metrics.table_label": "Эффект арналарының метрикалары",
    "admin.effects.metrics.circuit_table_label": "Circuit-breaker күйі",
    "admin.effects.metrics.no_endpoints":
      "Circuit-breaker эндпойнттары теңшелмеген.",
    "admin.effects.metrics.column.channel_name": "Арна",
    "admin.effects.metrics.column.delivery_count":
      "Жеткізулер (процесс басталғаннан)",
    "admin.effects.metrics.column.success_rate": "Сәттілік %",
    "admin.effects.metrics.column.retry_count": "Қайталаулар",
    "admin.effects.metrics.column.dead_letter_count": "Dead-letter",
    "admin.effects.metrics.column.latency_p50": "p50 мс",
    "admin.effects.metrics.column.latency_p95": "p95 мс",
    "admin.effects.metrics.column.latency_p99": "p99 мс",
    "admin.effects.metrics.column.endpoint": "Эндпойнт",
    "admin.effects.metrics.column.state": "Күй",
    "admin.effects.metrics.column.time_in_state": "Күйдегі уақыт",
    "admin.effects.metrics.column.failures_5min": "5 мин ішіндегі сәтсіздіктер",
    "admin.effects.metrics.column.consecutive_failures":
      "Тізбекті сәтсіздіктер",
    "admin.effects.metrics.column.action": "Әрекет",
    "admin.effects.metrics.refresh.now": "Жаңарту",
    "admin.effects.metrics.last_refreshed": "Соңғы жаңарту: {{time}}",
    "admin.effects.metrics.last_refreshed_prefix": "Соңғы жаңарту:",
    "admin.effects.metrics.historical_placeholder_title":
      "Тарихи деректер (7 күн)",
    "admin.effects.metrics.historical_placeholder_body":
      "Тарихи деректер (7 күн) — жақында қолжетімді (метрикалардың тұрақтылығы қажет; ARCH §8.4-те тіркелген).",
    "admin.effects.metrics.circuit_open_banner":
      "{{endpoint}} үшін тізбек ажыратылған. ",
    "admin.effects.metrics.circuit_open_affected":
      "Әсер еткен жеткізулер қайталау үшін кезекке қойылды.",
    "admin.effects.metrics.banner_dismiss": "Жабу",
    "admin.effects.metrics.banner_dismiss_label":
      "{{endpoint}} үшін хабарламаны жабу",
    "admin.effects.metrics.policy.consecutive_failures":
      "Тізбекті сәтсіздіктер (1-100)",
    "admin.effects.metrics.policy.open_timeout_ms":
      "Ашу таймауты, мс (1000-300000)",
    "admin.effects.metrics.policy.half_open_max_probes":
      "Half-open макс. пробалар (1-10)",
    "admin.effects.metrics.policy.out_of_range":
      "Мән [{{min}}, {{max}}] аралығында болуы керек",
    "admin.effects.metrics.policy.updated_toast": "Саясат жаңартылды",
    "admin.effects.metrics.dialog_title": "Breaker баптау",
    "admin.effects.metrics.dialog_save": "Сақтау",
    "admin.effects.metrics.dialog_saving": "Сақталуда…",
    "admin.effects.metrics.dialog_cancel": "Болдырмау",
    "admin.effects.metrics.dialog_description":
      "Таңдалған эндпойнт үшін circuit-breaker параметрлерін өзгертіңіз.",
    "admin.effects.metrics.configure_endpoint":
      "{{endpoint}} үшін breaker баптау",
    "admin.effects.metrics.loading": "Эффект метрикалары жүктелуде…",
    "admin.effects.metrics.error_fetch":
      "Эффект метрикаларын жүктеу мүмкін болмады: {{message}}",
    "admin.effects.metrics.error_401":
      "Оператор тіркелгісі қажет. Қайта кіріңіз.",
    "admin.effects.metrics.error_504":
      "Эффект сервері жауап бермейді (таймаут).",
    "admin.effects.metrics.error_5xx": "Эффект сервері қате қайтарды.",
    // FR-UI-BLD-1 -- form-builder chrome (Kazakh fallback).
    "admin.builder.page_title": "Формалар конструкторы",
    "admin.builder.save.cta": "Форманы сақтау",
    "admin.builder.save.saving": "Сақталуда...",
    "admin.builder.save.invalid": "Қате енгізу",
    "admin.builder.palette.aria": "Өрістер палитрасы",
    "admin.builder.palette.title": "Өрістер",
    "admin.builder.canvas.title": "Форма холсты",
    "admin.builder.canvas.empty": "Бастау үшін палитрадан өрісті сүйреңіз",
    "admin.builder.canvas.remove": "{{id}} өрісін жою",
    "admin.builder.settings.aria": "Баптаулар",
    "admin.builder.settings.title": "Баптаулар",
    "admin.builder.settings.entity_type": "Субъект түрі",
    "admin.builder.settings.field_heading": "Өріс {{id}}",
    "admin.builder.settings.label": "Белгі",
    "admin.builder.settings.placeholder": "Кеңес",
    "admin.builder.settings.required": "Міндетті",
    "admin.builder.settings.visible_when": "Көріну шарты (формула)",
    "admin.builder.settings.computed_from": "Есептеу (формула)",
    "admin.builder.settings.target_entity": "Нысана субъект",
    "admin.builder.settings.no_selection": "Холсттан өрісті таңдаңыз",
    "admin.builder.formula.ok": "Формула жарамды",
    "admin.builder.formula.error": "Формула жарамсыз",
    "admin.builder.field_types.string": "Жол",
    "admin.builder.field_types.text": "Мәтін",
    "admin.builder.field_types.number": "Сан",
    "admin.builder.field_types.integer": "Бүтін сан",
    "admin.builder.field_types.float": "Бөлшек сан",
    "admin.builder.field_types.boolean": "Логикалық",
    "admin.builder.field_types.date": "Күн",
    "admin.builder.field_types.datetime": "Күн мен уақыт",
    "admin.builder.field_types.enum": "Тізім",
    "admin.builder.field_types.reference": "Сілтеме",
    // FR-UI-BLD-1 -- BPM designer chrome (Kazakh fallback).
    "admin.designer.page_title": "Процестер конструкторы",
    "admin.designer.save.cta": "Процесті сақтау",
    "admin.designer.save.saving": "Сақталуда...",
    "admin.designer.save.invalid": "Қате енгізу",
    "admin.designer.palette.aria": "Түйіндер палитрасы",
    "admin.designer.palette.title": "Түйіндер",
    "admin.designer.canvas.title": "Процесс холсты",
    "admin.designer.canvas.empty": "Бастау үшін палитрадан түйінді сүйреңіз",
    "admin.designer.canvas.aria": "BPM-процесс холсты",
    "admin.designer.canvas.remove": "{{id}} түйінін жою",
    "admin.designer.canvas.dragging": "Сүйрелуде: {{id}}",
    "admin.designer.settings.aria": "Қасиеттер",
    "admin.designer.settings.title": "Қасиеттер",
    "admin.designer.settings.process_name": "Процесс атауы",
    "admin.designer.settings.process_description": "Сипаттама",
    "admin.designer.settings.node_heading": "Түйін {{id}}",
    "admin.designer.settings.node_label": "Белгі",
    "admin.designer.settings.form_id": "Форма",
    "admin.designer.settings.candidate_role_id": "Кандидат рөлі",
    "admin.designer.settings.assignee_user_id": "Тағайындалған пайдаланушы",
    "admin.designer.settings.due_in_seconds": "Мерзім (секунд)",
    "admin.designer.settings.script_id": "Скрипт",
    "admin.designer.settings.script_formula": "Формула (міндетті емес)",
    "admin.designer.settings.delay_expression": "Кідіріс (өрнек)",
    "admin.designer.settings.edge_heading": "Ауысу {{id}}",
    "admin.designer.settings.edge_guard": "Шарт (формула)",
    "admin.designer.settings.no_config": "Бұл түйінде баптаулар жоқ",
    "admin.designer.settings.no_selection":
      "Холсттан түйін немесе ауысуды таңдаңыз",
    "admin.designer.guard.ok": "Шарт жарамды",
    "admin.designer.guard.error": "Шарт жарамсыз",
    "admin.designer.node_types.start": "Бастау",
    "admin.designer.node_types.end": "Аяқтау",
    "admin.designer.node_types.userTask": "Пайдаланушы тапсырмасы",
    "admin.designer.node_types.scriptTask": "Скрипт тапсырмасы",
    "admin.designer.node_types.xorGateway": "XOR шлюзі",
    "admin.designer.node_types.andGateway": "AND шлюзі",
    "admin.designer.node_types.timerCatch": "Таймер",
    // FR-UI-BLD-1 -- list-page chrome (Kazakh fallback, ISS-f4fa1b7b-258).
    "admin.forms.list.title": "Формалар",
    "admin.forms.list.empty": "Әзірге формалар жоқ.",
    "admin.forms.list.aria": "Формалар тізімі",
    "admin.processes.list.title": "Процестер",
    "admin.processes.list.empty": "Әзірге процестер жоқ.",
    "admin.processes.list.aria": "Процестер тізімі",
    // FR-UI-DASH-1 -- per-tenant observability dashboard chrome
    // (Kazakh). 19 chrome keys covering AC-1 widgets, AC-2
    // alert-rule authoring, AC-3 active-alerts panel, AC-4
    // refresh / loading states, AC-5 drill-down labels.
    "observability.dashboard.title": "Тенанттың бақылауы",
    "observability.dashboard.subtitle":
      "Бұл тенант үшін жиынтық метрикалар, қате пайызы және қадам ұзақтығы.",
    "observability.dashboard.refresh": "Жаңарту",
    "observability.dashboard.author_alert": "Ереже жасау",
    "observability.dashboard.error.rate_limit":
      "Тым көп сұраулар. Күте тұрыңыз.",
    "observability.dashboard.widget.drill_down": "Сәтсіз даналарды ашу",
    "observability.dashboard.widget.instance_count.title":
      "Процесс даналары (соңғы 24 сағат)",
    "observability.dashboard.widget.instance_count.active": "Белсенді",
    "observability.dashboard.widget.instance_count.failed": "Сәтсіз",
    "observability.dashboard.widget.instance_count.completed": "Аяқталған",
    "observability.dashboard.widget.error_rate.title":
      "Қате пайызы (соңғы 7 күн)",
    "observability.dashboard.widget.error_rate.empty":
      "Соңғы қадам белсенділігі жоқ.",
    "observability.dashboard.widget.error_rate.sparkline_aria":
      "Сағаттық қате пайызының спарклайны",
    "observability.dashboard.widget.step_duration.title":
      "Қадам ұзақтығының перцентильдері",
    "observability.dashboard.widget.step_duration.p50": "p50",
    "observability.dashboard.widget.step_duration.p95": "p95",
    "observability.dashboard.widget.step_duration.p99": "p99",
    "observability.dashboard.widget.process_breakdown.title":
      "Процесс түрі бойынша даналар",
    "observability.dashboard.widget.process_breakdown.empty":
      "Әзірге процесс даналары жоқ.",
    "observability.dashboard.widget.process_breakdown.instances_label": "дана",
    "observability.dashboard.widget.process_breakdown.failed_label": "сәтсіз",
    "observability.dashboard.alerts.title": "Белсенді ескертулер",
    "observability.dashboard.alerts.empty":
      "FR-OBS-4 жеткізілгеннен кейін белсенді ескертулер осында пайда болады.",
    "observability.dashboard.alert_rule.title": "Ереже жасау",
    "observability.dashboard.alert_rule.name_label": "Ереже атауы",
    "observability.dashboard.alert_rule.severity_label": "Маңыздылық",
    "observability.dashboard.alert_rule.expression_label": "Өрнек",
    "observability.dashboard.alert_rule.save": "Сақтау",
    "observability.dashboard.alert_rule.saving": "Сақталуда…",
    "observability.dashboard.alert_rule.error.persist_unavailable":
      "ескерту ережелерін сақтау FR-OBS-4-пен қолжетімді болады",
    // FR-UI-CABINET-2 — workspace chrome.
    "cabinet.topbar.skipToMain": "Негізгі мазмұнға өту",
    "cabinet.topbar.environment.production": "Өндірістік орта",
    "cabinet.topbar.environment.staging": "Тест ортасы",
    "cabinet.topbar.environment.sandbox": "Құмсалғыш",
    "cabinet.languageMenu.label.en": "English",
    "cabinet.languageMenu.label.ru": "Русский",
    "cabinet.languageMenu.label.kk": "Қазақша",
    "cabinet.languageMenu.label.uk": "Ағылшын",
    "cabinet.languageMenu.label.be": "Украин",
    "cabinet.footer.copyright":
      "© {{year}} ABiTech. Барлық құқықтар қорғалған.",
    "cabinet.footer.supportLabel": "Қолдау",
    "cabinet.notfound.title": "Бұл бет әлі дайын емес",
    "cabinet.notfound.subtitle":
      "Ашқан мекенжайыңызда әлі экран жоқ. Команда біледі.",
    "cabinet.notfound.back": "Менің кабинетіме оралу",
    // FR-UI-CABINET-3 — required user parameters page + chrome button (Kazakh).
    "cabinet.required_user_parameters.menu_label":
      "Міндетті пайдаланушы параметрлері",
    "cabinet.required_user_parameters.title":
      "Міндетті пайдаланушы параметрлері",
    "cabinet.required_user_parameters.subtitle":
      "Тенант осы параметрлерді талап етеді.",
    "cabinet.required_user_parameters.save": "Сақтау",
    "cabinet.required_user_parameters.resolve": "Мәндерді енгізу",
    "cabinet.required_user_parameters.retry": "Қайталау",
    "cabinet.required_user_parameters.missing": "Толтырылмаған",
    "cabinet.required_user_parameters.not_provided": "Әлі енгізілмеген",
    "cabinet.required_user_parameters.supplied": "Енгізілген",
    "cabinet.required_user_parameters.missing_count":
      "{count} міндетті параметр толтырылмаған",
    "cabinet.required_user_parameters.missing_badge_aria":
      "Міндетті параметр толтырылмаған",
    "cabinet.required_user_parameters.stale_version_banner":
      "Бет басқа сессияда өзгертілді. Жалғастыру үшін жаңартыңыз.",
    "cabinet.required_user_parameters.stale_version.title": "Нұсқа қақтығысы",
    "cabinet.required_user_parameters.stale_version.body":
      "Бет басқа сессияда өзгертілді. Ағымдағы жол нұсқасы: {{version}}.",
    "cabinet.required_user_parameters.stale_version.refetch":
      "Соңғы нұсқаны алу",
    "cabinet.required_user_parameters.fetch_failure.title":
      "Бетті жүктеу мүмкін емес",
    "cabinet.required_user_parameters.fetch_failure.body": "Қайталап көріңіз.",
    "cabinet.required_user_parameters.permission_denied.title":
      "Сізде бұл бетке қол жетімділік жоқ",
    "cabinet.required_user_parameters.permission_denied.body":
      "Егер қате деп санасаңыз, администраторға хабарласыңыз.",
    "cabinet.required_user_parameters.rate_limit.title": "Тым көп сұрау",
    "cabinet.required_user_parameters.rate_limit.body":
      "Бір сәт күтіп, қайталап көріңіз.",
    "cabinet.required_user_parameters.empty.title": "Міндетті параметрлер жоқ",
    "cabinet.required_user_parameters.empty.body":
      "Тенант міндетті пайдаланушы параметрлерін орнатпаған.",
    "cabinet.required_user_parameters.summary.complete":
      "Барлық міндетті параметрлер енгізілген.",
    "cabinet.required_user_parameters.summary.missing_count":
      "{count} міндетті параметр әлі толтырылмаған.",
    "cabinet.required_user_parameters.summary.supplied_count":
      "{count} енгізілген.",
    // FR-CFG-1..4: tenant presentation, preferences, help, module-config (Kazakh).
    "theme.picker.title": "Тақырып",
    "theme.picker.preview": "Алдын ала қарау",
    "theme.picker.save": "Сақтау",
    "theme.picker.discard": "Болдырмау",
    "theme.picker.empty": "Сақталған тақырыптар жоқ.",
    "theme.picker.fetch_failure": "Тақырыпты жүктеу мүмкін емес.",
    "theme.picker.permission_denied": "Тақырыпқа қол жетімділік жоқ.",
    "theme.picker.stale_version": "Тақырып жаңартылды — бетті жаңартыңыз.",
    "theme.picker.rate_limit":
      "Тым көп сұрау. {{seconds}} секундтан соң қайталаңыз.",
    "theme.picker.validation_error": "Тақырыпта тексеру қателері бар.",
    "theme.fallback.platform_default": "Платформа тақырыбы",
    "preferences.favorites": "Таңдаулылар",
    "preferences.page_settings": "Бет параметрлері",
    "preferences.current_org": "Ағымдағы ұйым",
    "preferences.current_location": "Ағымдағы орын",
    "preferences.current_locale": "Интерфейс тілі",
    "preferences.save.success": "Параметрлер сақталды",
    "preferences.save.stale_rv": "Параметрлер жаңартылды — бетті жаңартыңыз.",
    "preferences.admin_read_only": "Тек әкімшіге арналған",
    "help.tour.step_counter": "{{current}} / {{total}} қадам",
    "help.tour.next": "Келесі",
    "help.tour.back": "Артқа",
    "help.tour.skip": "Өткізіп жіберу",
    "help.tour.dismiss": "Жабу",
    "help.tour.permission_denied.title": "Тур қолжетімді емес",
    "help.tour.permission_denied.body": "Сізде бұл турға қол жетімділік жоқ.",
    "help.tour.permission_denied.target_id": "Мақсат идентификаторы",
    "help.tour.stale_version.title": "Тур жаңартылды",
    "help.tour.stale_version.detail": "Тур қадамдары өзгерді.",
    "help.tour.stale_version.active_conflict": "Белсенді нұсқа қақтығысы",
    "help.tour.stale_version.reset_progress": "Прогресті қалпына келтіру",
    "help.tour.rate_limit.title": "Тым көп сұрау",
    "help.tour.rate_limit.detail": "{{seconds}} секундтан соң қайталаңыз.",
    "help.tour.rate_limit.countdown": "{{seconds}}с күту",
    "help.tour.validation_error.title": "Турда қателер бар",
    "help.tour.validation_error.item": "{{step}} қадам: {{error}}",
    "help.center.title": "Анықтама орталығы",
    "help.center.empty.title": "Қол жетімді турлар жоқ",
    "help.center.empty.body":
      "Әкімші тур жариялаған кезде ол осында пайда болады.",
    "help.center.empty.author": "Автор",
    "config.editor.title": "Модуль конфигурациясы",
    "config.editor.namespace": "Атау кеңістігі",
    "config.editor.environment": "Орта",
    "config.editor.value": "Мән",
    "config.editor.save": "Сақтау",
    "config.editor.discard": "Болдырмау",
    "config.editor.promote": "Промоут",
    "config.editor.schema_error.title": "Схема қатесі",
    "config.editor.schema_error.item": "{{path}}: {{message}}",
    "config.editor.stale_rv":
      "Конфигурация нұсқасы ескірген — бетті жаңартыңыз.",
    "config.editor.production_admin.reason_label": "Өзгеріс себебі",
    "config.editor.production_admin.reason_required":
      "Өзгеріс себебін көрсетіңіз.",
    "config.editor.promotion_review.title": "Промоут сұрауы",
    "config.editor.promotion_review.approve": "Бекіту",
    "config.editor.promotion_review.reject": "Қабылдамау",
    // FR-COL-1 — record communication threads chrome.
    "comments.tab_label": "Пікірлер",
    "comments.composer.placeholder": "Пікір жазу...",
    "comments.composer.submit": "Жіберу",
    "comments.delete.confirm": "Пікірді жою?",
    "comments.deleted_marker": "(жойылды)",
    "comments.empty": "Пікірлер жоқ",
    "activity.tab_label": "Тарих",
    "activity.empty": "Оқиғалар жоқ",
    // FR-UI-DIR-1 — People and Organization Directory Workspace chrome (kk).
    "directory.nav.section": "Каталог",
    "directory.nav.people": "Адамдар",
    "directory.nav.organization": "Ұйым құрылымы",
    "directory.nav.positions": "Лауазымдар",
    "directory.nav.employment": "Жұмыспен қамту",
    "directory.nav.locations": "Орынжайлар",
    "directory.nav.teams": "Командалар",
    "directory.nav.counterparties": "Контрагенттер",
    "directory.nav.qualifications": "Біліктіліктер",
    "directory.nav.audit": "Каталог аудиті",
    "directory.nav.admin": "Каталог әкімшілігі",
    "directory.people.title": "Адамдар каталогы",
    "directory.people.subtitle": "Тенант қызметкерлері мен контрагенттері.",
    "directory.people.search.placeholder":
      "Аты, email, табельдік нөмірі бойынша іздеу…",
    "directory.people.filter.status": "Күйі",
    "directory.people.filter.unit": "Бөлімше",
    "directory.people.filter.location": "Орынжай",
    "directory.people.filter.clear": "Сүзгілерді тазалау",
    "directory.people.empty": "Берілген сүзгілер бойынша ештеңе табылмады.",
    "directory.people.row.status": "Күйі",
    "directory.people.row.unit": "Бөлімше",
    "directory.people.row.title": "Лауазым",
    "directory.people.row.location": "Орынжай",
    "directory.people.row.email": "Email",
    "directory.people.row.tabs.summary": "Карточка",
    "directory.person.summary.title": "Қызметкер карточкасы",
    "directory.person.summary.tabs.overview": "Шолу",
    "directory.person.summary.tabs.contacts": "Байланыс",
    "directory.person.summary.tabs.employment": "Жұмыспен қамту",
    "directory.person.summary.tabs.qualifications": "Біліктіліктер",
    "directory.person.summary.tabs.audit": "Аудит",
    "directory.person.summary.email": "Email",
    "directory.person.summary.phone": "Телефон",
    "directory.person.summary.manager": "Басшы",
    "directory.person.summary.unavailable":
      "Құпиялылық параметрлерімен жасырылған.",
    "directory.person.summary.status.active": "Белсенді",
    "directory.person.summary.status.inactive": "Белсенді емес",
    "directory.person.summary.status.on_leave": "Демалыста",
    "directory.org.title": "Ұйымдық құрылым",
    "directory.org.subtitle": "Бөлімшелер мен олардың иерархиясы.",
    "directory.org.empty": "Бөлімшелер теңшелмеген.",
    "directory.org.members_label": "Қызметкерлер",
    "directory.positions.title": "Лауазымдар",
    "directory.positions.subtitle": "Тенант лауазымдарының каталогы.",
    "directory.positions.empty": "Лауазымдар жоқ.",
    "directory.locations.title": "Орынжайлар",
    "directory.locations.subtitle": "Тенант мекенжайлары мен алаңдары.",
    "directory.locations.empty": "Орынжайлар көрсетілмеген.",
    "directory.locations.form.error": "Қате: {{message}}",
    "directory.locations.form.server_conflict":
      "{{field}} серверде өзгерді, сіз өңдеп жаттыңыз. Мәтніңіз сақталды — серверде қазір: {{value}}",
    "directory.people.form.error": "Қате: {{message}}",
    "directory.people.form.server_conflict":
      "{{field}} серверде өзгерді, сіз өңдеп жаттыңыз. Мәтніңіз сақталды — серверде қазір: {{value}}",
    // ISS-517 — Organization unit CRUD admin surface (kk).
    "directory.organization_units.new.title": "Жаңа бөлімше",
    "directory.organization_units.new.submit": "Бөлімше жасау",
    "directory.organization_units.new.submitting": "Жасалуда…",
    "directory.organization_units.new.picker_error":
      "Анықтама деректерін жүктеу мүмкін болмады. Бетті жаңартып, қайта көріңіз.",
    "directory.organization_units.new.invalid_input":
      "Сервер бұл мәндерді қабылдамады. Атауы мен кодты тексеріп, қайта көріңіз.",
    "directory.organization_units.edit.title": "Бөлімшені өңдеу",
    "directory.organization_units.edit.submit": "Сақтау",
    "directory.organization_units.edit.submitting": "Сақталуда…",
    "directory.organization_units.edit.loading": "Бөлімше жүктелүде…",
    "directory.organization_units.edit.not_found": "Бөлімше табылмады",
    "directory.organization_units.edit.invalid_input":
      "Сервер бұл мәндерді қабылдамады. Атауы мен кодты тексеріп, қайта көріңіз.",
    "directory.organization_units.edit.error": "Өзгерістерді сақтау қатесі",
    "directory.organization_units.detail.title": "Бөлімше",
    "directory.organization_units.detail.loading": "Бөлімше жүктелүде…",
    "directory.organization_units.detail.not_found": "Бөлімше табылмады",
    "directory.organization_units.detail.name": "Атауы",
    "directory.organization_units.detail.code": "Код",
    "directory.organization_units.detail.legal_entity": "Заңды тұлға",
    "directory.organization_units.detail.parent": "Жоғарғы бөлімше",
    "directory.organization_units.detail.status": "Күйі",
    "directory.organization_units.detail.closed_at": "Жабылған уақыты",
    "directory.organization_units.detail.row_version": "Жазба нұсқасы",
    "directory.organization_units.detail.created_at": "Жасалған уақыты",
    "directory.organization_units.detail.created_by": "Жасаған",
    "directory.organization_units.detail.edit": "Өңдеу",
    "directory.organization_units.detail.back": "Артқа",
    "directory.organization_units.detail.archived": "Бөлімше мұрағатталды.",
    "directory.organization_units.detail.archive": "Мұрағаттау",
    "directory.organization_units.detail.archiving": "Мұрағатталуда…",
    "directory.organization_units.detail.archive_confirm_title":
      "Бөлімшені мұрағаттау",
    "directory.organization_units.detail.archive_confirm_body":
      "Мұрағаттау бөлімшені жабады. Бар көші бөлімшелер жабылмайды.",
    "directory.organization_units.detail.archive_confirm": "Мұрағаттау",
    "directory.organization_units.detail.archive_cancel": "Болдырмау",
    "directory.organization_units.detail.error":
      "Ұйымдастыру бірлігін жүктеу қатесі",
    "directory.organization_units.form.name": "Атауы",
    "directory.organization_units.form.code": "Код",
    "directory.organization_units.form.legal_entity": "Заңды тұлға",
    "directory.organization_units.form.parent": "Жоғарғы бөлімше",
    "directory.organization_units.form.no_legal_entity":
      "(заңды тұлғаны таңдаңыз)",
    "directory.organization_units.form.no_parent": "(атасы жоқ — түпкі)",
    "directory.organization_units.form.cancel": "Болдырмау",
    "directory.organization_units.form.error": "Қате: {{message}}",
    "directory.organization_units.form.server_conflict":
      "{{field}} серверде өзгерді, сіз өңдеп жаттыңыз. Мәтніңіз сақталды — серверде қазір: {{value}}",
    "directory.organization_units.action.create": "Бөлімше жасау",
    "directory.organization_units.action.edit": "Өңдеу",
    "directory.organization_units.action.move": "Жылжыту",
    "directory.organization_units.action.moving": "Жылжытылуда…",
    "directory.organization_units.action.archive": "Мұрағаттау",
    "directory.organization_units.action.archiving": "Мұрағатталуда…",
    "directory.organization_units.action.add_child": "Көші бөлімше қосу",
    "directory.organization_units.move.title": "Бөлімшені жылжыту",
    "directory.organization_units.move.submit": "Жылжыту",
    "directory.organization_units.move.moving": "Жылжытылуда…",
    "directory.organization_units.move.moved": "Бөлімше жылжытылды.",
    "directory.organization_units.move.parent_label": "Жаңа жоғарғы бөлімше",
    "directory.organization_units.move.parent_cycle":
      "Жылжыту иерархияда цикл жасаар еді.",
    "directory.organization_units.move.cancel": "Бас тарту",
    "directory.organization_units.status.active": "Белсенді",
    "directory.organization_units.status.closed": "Жабық",
    "directory.employment.title": "Жұмыспен қамту тарихы",
    "directory.employment.empty": "Тағайындау жазбалары жоқ.",
    "directory.employment.current": "Ағымдағы тағайындау",
    "directory.teams.title": "Командалар",
    "directory.teams.subtitle": "Кросс-функционалды және жобалық топтар.",
    "directory.teams.deferred.title": "Командалар кейінірек пайда болады",
    "directory.teams.deferred.body":
      "Командалар бөлімі сәйкес API жарияланғанша кейінге қалдырылған. Синтетикалық деректерді қоймаймыз.",
    "directory.qualifications.title": "Біліктіліктер",
    "directory.qualifications.subtitle":
      "Қызметкердің сертификаттары, рұқсаттары мен лицензиялары.",
    "directory.qualifications.deferred.title":
      "Біліктіліктер кейінірек пайда болады",
    "directory.qualifications.deferred.body":
      "Біліктіліктер бөлімі сәйкес API жарияланғанша кейінге қалдырылған. Синтетикалық деректерді қоймаймыз.",
    "directory.audit.title": "Каталог аудиті",
    "directory.audit.subtitle": "Каталог жазбаларының өзгеру тарихы.",
    "directory.audit.empty": "Аудит оқиғалары жоқ.",
    "directory.audit.column.when": "Қашан",
    "directory.audit.column.actor": "Кім",
    "directory.audit.column.action": "Іс-әрекет",
    "directory.audit.column.target": "Объект",
    "directory.admin.title": "Каталог әкімшілігі",
    "directory.admin.subtitle": "Каталог әкімшісінің әрекеттері.",
    "directory.admin.denied":
      "Сізде каталог әкімшісі рөлі жоқ. Тенант операторына хабарласыңыз.",
    "directory.conflict.title": "Жазба өзгертілді",
    "directory.conflict.body":
      "Сервер сақтаудан бас тартты: басқа транзакция осы жазбаны өзгертті. Бетті жаңартып, қайталап көріңіз.",
    "directory.conflict.action.reload": "Бетті жаңарту",
    "directory.conflict.action.discard": "Өзгерістерді болдырмау",
    "directory.conflict.action.retry": "Қайталау",
    "directory.fetch.failure.title": "Каталог деректерін жүктеу мүмкін болмады",
    "directory.fetch.failure.body":
      "Сервер жауап бермеді. Байланысты тексеріп, қайталап көріңіз.",
    "directory.fetch.failure.retry": "Қайталау",
    // FR-PM-1 — workmanagement.
    "workmanagement.title": "Жобалар",
    "workmanagement.code": "Код",
    "workmanagement.name": "Атауы",
    "workmanagement.responsible_department": "Бөлімше",
    "workmanagement.project_administrator": "Жоба әкімшісі",
    "workmanagement.planned_start_date": "Басталу күні",
    "workmanagement.planned_end_date": "Аяқталу күні",
    "workmanagement.description": "Сипаттама",
    "workmanagement.status": "Күй",
    "workmanagement.status.draft": "Жоба",
    "workmanagement.status.active": "Белсенді",
    "workmanagement.status.completed": "Аяқталған",
    "workmanagement.status.archived": "Мұрағатталған",
    "workmanagement.status.pending_approval": "Келісуде",
    "workmanagement.status.closed": "Жабық",
    "workmanagement.new_button": "Жоба жасау",
    "workmanagement.save": "Сақтау",
    "workmanagement.cancel": "Болдырмау",
    "workmanagement.registry.heading": "Жобалар тізілімі",
    "workmanagement.form.title": "Жаңа жоба",
    "workmanagement.registry.empty": "Жобалар жоқ",
    "workmanagement.registry.filter.status_label": "Күй",
    "workmanagement.registry.filter.all": "Барлық күйлер",
    "workmanagement.registry.error.load_failed":
      "Жобалар тізілімін жүктеу мүмкін болмады",
    "workmanagement.registry.error.create_failed":
      "Жобаны жасау мүмкін болмады",
    "workmanagement.error.dates_invalid":
      "Аяқталу күні басталу күнінен кейін болуы керек",
    "workmanagement.error.name_required": "Жобаның атауын енгізіңіз",
    "workmanagement.error.name_too_long":
      "Жоба атауы 200 таңбадан аспауы керек",
    "workmanagement.error.description_too_long":
      "Жоба сипаттамасы 1000 таңбадан аспауы керек",
    "workmanagement.detail.code": "Жоба коды",
    "workmanagement.detail.responsible_department": "Бөлімше",
    "workmanagement.detail.project_administrator": "Жоба әкімшісі",
    "workmanagement.detail.planned_start_date": "Басталу күні",
    "workmanagement.detail.planned_end_date": "Аяқталу күні",
    "workmanagement.detail.description": "Сипаттама",
    "workmanagement.detail.created_at": "Жасалған күні",
    "workmanagement.detail.created_by": "Автор",
    "workmanagement.detail.status": "Күй",
    "workmanagement.detail.row_version": "Жол нұсқасы",
    "workmanagement.approval.title": "Жобаны келісу",
    "workmanagement.approval.stage_label": "Келісу кезеңі",
    "workmanagement.approval.panel.title": "Келісу кезеңі бойынша шешім",
    "workmanagement.approval.decision.label": "Шешім",
    "workmanagement.approval.decision.approved": "Келісу",
    "workmanagement.approval.decision.rejected": "Қабылдамау",
    "workmanagement.approval.comment.label": "Түсініктеме",
    "workmanagement.approval.comment.placeholder":
      "Себебін немесе түсініктемесін көрсетіңіз…",
    "workmanagement.approval.submit": "Шешімді тіркеу",
    "workmanagement.approval.submit_for_approval": "Келісуге жіберу",
    "workmanagement.approval.preflight.title": "Алдын ала тексеру",
    "workmanagement.approval.preflight.submittable": "Келісуге жіберуге болады",
    "workmanagement.approval.preflight.not_submittable": "Жіберуге дайын емес",
    "workmanagement.approval.history.title": "Келісу тарихы",
    "workmanagement.approval.history.empty": "Әзірге шешімдер жоқ",
    "workmanagement.approval.history.stage": "{{stage}} кезеңі",
    "workmanagement.approval.error.decision_required": "Шешімді таңдаңыз",
    "workmanagement.approval.error.comment_required":
      "Қабылдамаған кезде түсініктеме міндетті",
    "workmanagement.approval.error.not_found": "Жоба табылмады",
    // FR-PM-4 — Work Package view chrome (KK locale mirror).
    "work_package.title": "Жұмыс пакеті",
    "work_package.contract_label": "Шарт",
    "work_package.create": "Жұмыс пакетін жасау",
    "work_package.update": "Жұмыс пакетін жаңарту",
    "work_package.list_empty": "Жұмыс пакеттері жоқ",
    "work_package.assign_parent_task": "Ата-ана тапсырмасын тағайындау",
    "work_package.remove_parent_task": "Ата-ана тапсырмасын алып тастау",
    "work_package.field.code": "Пакет коды",
    "work_package.field.name": "Пакет атауы",
    "work_package.field.status": "Пакет күйі",
    "work_package.field.presentation_label": "Көрсету белгісі",
    "work_package.field.legal_document_ref": "Заң құжаты",
    "work_package.field.project_id": "Жоба",
    "work_package.field.parent_task_id": "Ата-ана тапсырмасы",
    "work_package.field.row_version": "Жол нұсқасы",
    "work_package.status.planning": "Жоспар",
    "work_package.status.executing": "Орындау",
    "work_package.status.closed": "Жабық",
    "work_package.presentation_mode.work_package": "Жұмыс пакеті",
    "work_package.presentation_mode.contract": "Шарт",
    "work_package.section.children": "Тапсырмалар",
    "work_package.section.detail": "Жұмыс пакеті мәліметтері",
    "work_package.registry.heading": "Жұмыс пакеттері тізілімі",
    "work_package.registry.filter.project_label": "Жоба",
    "work_package.registry.error.load_failed":
      "Жұмыс пакеттері тізілімін жүктеу мүмкін болмады",
    "work_package.registry.error.create_failed":
      "Жұмыс пакетін жасау мүмкін болмады",
    "work_package.error.name_required": "Жұмыс пакетінің атауын енгізіңіз",
    "work_package.error.name_too_long":
      "Жұмыс пакетінің атауы 200 таңбадан аспауы керек",
    "work_package.error.hierarchy_mandatory_levels":
      "Тікелей «Жоба → тапсырма» байланысы тыйым салынған; жұмыс пакетін жасаңыз",
    "work_package.error.parent_missing": "Ата-ана тапсырмасы табылмады",
    "work_package.error.parent_other_tenant":
      "Ата-ана тапсырмасы басқа тенантқа тиесілі",
    "work_package.error.parent_other_work_package":
      "Ата-ана тапсырмасы басқа жұмыс пакетіне тиесілі",
    "work_package.error.cycle_detected":
      "Байланыс тапсырмалар иерархиясында цикл жасайды",
    "work_package.error.second_immediate_parent":
      "Тапсырманың тікелей ата-ана тапсырмасы бар",
    "work_package.error.legal_document_ref_invalid":
      "Көрсетілген заң құжаты осы тенантта табылмады",
  },
  en: {
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.loading": "Loading…",
    "common.error": "Error",
    "common.retry": "Retry",
    "common.close": "Close",
    "common.back": "Back",
    "common.confirm": "Confirm",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.create": "Create",
    "common.search": "Search",
    "common.copy": "Copy",
    "common.yes": "Yes",
    // FR-UI-CORE-10 AC-5: inline validation chrome label.
    "validation.error.inline": "Validation error",
    "validation.error.code_default": "Field contains an error",
    // FR-UI-VIEW-1 AC-4 (rv-2): list-level 422 chrome.
    "validation.error.list": "Validation failed",
    "common.no": "No",
    "common.required": "Required",
    "common.optional": "Optional",
    "conflict.title": "Write conflict",
    "conflict.body":
      "This record was modified after you loaded it. Choose how to proceed.",
    "conflict.reapply": "Re-apply my changes onto current version",
    "conflict.refetch": "Refetch latest",
    "conflict.discard": "Discard my changes",
    "conflict.serverVersion": "Current version (server)",
    "conflict.yourVersion": "Your changes",
    "deprovision.confirm": "Confirm De-provision",
    "conflict.currentRowVersion": "Row version",
    // FR-UI-FORM-1 AC-2 — reference picker chrome (English fallback).
    "reference.placeholder": "Search…",
    "reference.empty": "No results",
    "reference.loading": "Loading…",
    "reference.error": "Failed to load options",
    "reference.cancel": "Cancel",
    "reference.select": "Select",
    // FR-UI-PROM-2: non-skippable human-review gate chrome (English fallback).
    "promote.gate.section.diff": "Changes",
    "promote.gate.section.assertions": "Passed assertions",
    "promote.gate.section.artifact": "NEEDS_REVIEW package",
    "promote.gate.diff.empty": "No changes to review.",
    "promote.gate.assertions.none":
      "This promotion was not generated by an agent. No assertions recorded.",
    "promote.gate.assertions.recorded": "recorded",
    "promote.gate.artifact.none":
      "No NEEDS_REVIEW artifact attached. This is a human-authored submission.",
    "promote.gate.artifact.envelope_meta": "Envelope metadata",
    "promote.gate.artifact.task_spec_id": "task_spec_id",
    "promote.gate.artifact.task_spec_version": "task_spec_version",
    "promote.gate.artifact.kind": "kind",
    "promote.gate.artifact.agent_principal": "agent_principal",
    "promote.gate.artifact.submitted_at": "submitted_at",
    "promote.gate.artifact.attempt_count": "attempts",
    "promote.gate.artifact.spec_hash": "spec_hash",
    "promote.gate.artifact.final_status": "final_status",
    "promote.gate.artifact.spec": "spec",
    "promote.gate.artifact.attempts": "Attempts",
    "promote.gate.artifact.started_at": "started_at",
    "promote.gate.artifact.completed_at": "completed_at",
    "promote.gate.artifact.terminal_failure_reason": "terminal_failure_reason",
    "promote.gate.artifact.bpm_step_log_snapshot": "bpm_step_log_snapshot",
    "promote.gate.artifact.open_full_envelope": "Open full artifact envelope",
    "promote.gate.buttons.approve": "Approve Promotion",
    "promote.gate.buttons.approving": "Approving…",
    "promote.gate.buttons.reject": "Reject",
    "promote.gate.required_acknowledgement":
      "Acknowledge irreversibility before approving",
    "promote.gate.approve.error": "Failed to approve promotion.",
    "promote.gate.reject.reason_label": "Reason for rejection",
    "promote.gate.reject.reason_placeholder":
      "Describe what needs to be fixed…",
    "promote.gate.reject.confirm": "Confirm Reject",
    "promote.gate.reject.pending": "Rejecting…",
    "promote.gate.reject.cancel": "Cancel",
    "promote.gate.reject.error": "Failed to reject promotion.",
    "promote.gate.error.load_failed": "Failed to load review:",
    // FR-UI-ADM-1: entity-type CRUD permission matrix editor chrome.
    // Flat-key layout matching the rest of the bundle; see the
    // COMMON_RESOURCES doc comment above for the rationale.
    "admin.matrix.title": "Permission Matrix",
    "admin.matrix.subtitle":
      "Manage CRUD permissions for roles and entity types",
    "admin.matrix.column.role": "Role",
    "admin.matrix.column.create": "Create",
    "admin.matrix.column.read": "Read",
    "admin.matrix.column.update": "Update",
    "admin.matrix.column.delete": "Delete",
    "admin.matrix.column.all": "All",
    "admin.matrix.column.platform": "Platform scope (server-managed)",
    "admin.matrix.action.save": "Save role",
    "admin.matrix.action.cancel": "Cancel",
    "admin.matrix.action.new_role": "New role",
    "admin.matrix.action.delete_role": "Delete role",
    "admin.matrix.confirm.delete_role":
      "Delete role {{roleName}}? This cannot be undone.",
    "admin.matrix.system_role.readonly": "System role — read-only",
    "admin.matrix.banner.pending_review":
      "{count, plural, one {# role modified in staging — pending review} other {# roles modified in staging — pending review}}",
    "admin.matrix.banner.open_review": "Open review",
    "admin.matrix.loading": "Loading matrix...",
    "admin.errors.staging_only.title": "Permission editor is staging-only",
    "admin.errors.staging_only.body":
      "Open this page on your tenant's staging subdomain.",
    "admin.errors.staging_only.code": "ENV_PRODUCTION",
    "admin.errors.version_conflict.title": "Version conflict",
    "admin.errors.version_conflict.body":
      "This role has been changed since you loaded it.",
    "admin.errors.version_conflict.refetch": "Refetch latest",
    "admin.errors.version_conflict.merge": "Merge my changes",
    "admin.errors.version_conflict.discard": "Discard my changes",
    // FR-UI-ADM-3 — formula-editor widget chrome (English fallback).
    "formula.editor.label": "Formula",
    "formula.editor.validate": "Validate",
    "formula.editor.type": "Type: {{type}}",
    "formula.editor.placeholder": "Enter formula…",
    "formula.editor.a11y.description":
      "Formula editor with server-side validation. The server checks syntax and type; formulas are not evaluated locally.",
    // FR-UI-LIST-1 — definition-driven list view chrome (English fallback).
    "lists.title": "Records",
    "lists.table.aria_label": "Entity records table",
    "lists.empty.no_records": "No records to display.",
    "lists.empty.permission_denied": "You don't have access to these records.",
    "lists.filter.group_label": "Filters",
    "lists.filter.placeholder": "Filter…",
    "lists.filter.aria_input": "Filter by field {{field}}",
    "lists.sort.asc": "▲",
    "lists.sort.desc": "▼",
    "lists.sort.toggle_aria": "Toggle sort for {{field}}",
    "lists.pagination.next": "Next",
    "lists.pagination.aria_label": "Pagination",
    "lists.pagination.page_size": "Page size: {{pageSize}}",
    "lists.boolean.yes": "Yes",
    "lists.boolean.no": "No",
    "lists.error.fetch_failure": "Failed to load records.",
    // FR-UI-VIEW-2 — Tree view chrome (English fallback).
    "lists.tree.title": "Records tree",
    "lists.tree.error.empty": "This entity type has no records.",
    "lists.tree.error.fetch_failure": "Failed to load the records tree.",
    "lists.tree.error.permission_denied":
      "You do not have access to these records.",
    "lists.tree.error.stale_version":
      "This entity's structure has changed. Please refresh the page.",
    "lists.tree.error.rate_limit": "Too many requests. Please retry shortly.",
    "lists.tree.error.validation_error":
      "The tree configuration for this entity type was rejected by the server.",
    "lists.tree.count_banner": "{{shown}} of {{total}} records shown.",
    // FR-UI-VIEW-3 — Gantt view chrome (English fallback). The
    // capBanner key uses {{count}} for the deferred number +
    // {{total}} for the total — see the resource bundle's
    // COMMON_RESOURCES doc comment for the interpolation engine
    // contract.
    "lists.gantt.title": "Gantt chart",
    "lists.gantt.noStartField": "no start date",
    "lists.gantt.zeroDuration": "start is after end",
    "lists.gantt.capBanner":
      "Showing {{count}} of {{total}} records. The remaining records have not been loaded.",
    "lists.gantt.loadMore": "Load more",
    "lists.gantt.invalidConfig":
      "The Gantt view configuration is missing or invalid.",
    "lists.gantt.dependsOnLabel": "Dependencies",
    "lists.gantt.conflictOnMove":
      "This record was changed by another user. Please refresh the page.",
    // FR-UI-PROC-1 — execution-trace viewer + failed-instance
    // dashboard chrome (English fallback).
    "processes.failed.title": "Failed instances",
    "processes.failed.subtitle":
      "Process instances in the failed state for this tenant. Retry a step or cancel the instance from each row.",
    "failed_instance.empty": "No failed instances.",
    "failed_instance.list_aria_label": "Failed instances for this tenant",
    "failed_instance.failed_node_label": "Failed node",
    "failed_instance.reason_label": "Reason",
    "failed_instance.retries_label": "Retries attempted",
    "failed_instance.trace_label": "Failure trace",
    "failed_instance.trace_summary": "{{count}} step entries",
    "failed_instance.view_trace": "View trace",
    "failed_instance.retry_button": "Retry step",
    "failed_instance.retry_pending": "Retrying…",
    "failed_instance.retry_error": "Retry failed.",
    "failed_instance.retry_not_failed":
      "Instance is no longer in failed state. Refresh to see the latest.",
    "failed_instance.cancel_button": "Cancel instance",
    "failed_instance.cancel_pending": "Cancelling…",
    "failed_instance.cancel_error": "Cancel failed.",
    "failed_instance.cancel_confirm_title": "Cancel this instance?",
    "failed_instance.cancel_confirm_body":
      "This terminates the process instance. The failure record will be retained for audit; no further steps will execute.",
    "failed_instance.restored_orphan_badge": "Restored orphan",
    "failed_instance.restored_orphan_explainer":
      "This failure was recorded during tenant restore. The originating step may no longer be reproducible.",
    "failed_instance.state.fetch_failure": "Failed to load failed instances.",
    "trace.title": "Execution trace",
    "trace.subtitle":
      "Step log for the selected process instance, ordered chronologically.",
    "trace.table_aria_label": "Execution trace step log",
    "trace.column.node_id": "Node",
    "trace.column.status": "Status",
    "trace.column.retry_count": "Retries",
    "trace.column.started_at": "Started",
    "trace.column.duration_ms": "Duration",
    "trace.column.error_message": "Error",
    "trace.empty": "No step log entries.",
    "trace.state.fetch_failure": "Failed to load the step log.",
    "admin.errors.fetch_failure": "Failed to load permission matrix",
    "admin.errors.permission_denied":
      "You don't have permission to edit permissions",
    "admin.errors.rate_limit": "Too many requests. Please wait.",
    "admin.errors.stale_version":
      "Definition version is stale. Reload the page.",
    "admin.errors.network": "Network error. Check your connection.",
    // FR-UI-ADM-4 — admin viewer (role × entity-type field
    // classification) chrome. Flat-key layout; see
    // COMMON_RESOURCES doc comment.
    "admin.field_visibility.title":
      "Field visibility: {{roleName}} / {{entityType}}",
    "admin.field_visibility.subtitle":
      "Which fields role {{roleName}} sees for entity type {{entityType}}",
    "admin.field_visibility.column.field": "Field",
    "admin.field_visibility.column.classification": "Classification",
    "admin.field_visibility.states.visible": "visible",
    "admin.field_visibility.states.hidden_cosmetic": "hidden (cosmetic)",
    "admin.field_visibility.states.denied_security": "denied (security)",
    "admin.field_visibility.tooltip.denied_security_explainer":
      "The server does not return this field — it is a security boundary (FR-AUTH-7).",
    "admin.field_visibility.aria.visible": "visible",
    "admin.field_visibility.aria.hidden_cosmetic":
      "hidden by visibility formula — cosmetic, server preserves the value",
    "admin.field_visibility.aria.denied_security":
      "denied by permission — security boundary, field is not returned in API responses",
    "admin.field_visibility.aria.table_label":
      "Field visibility for role {{roleName}} on entity type {{entityType}}",
    "admin.field_visibility.empty": "No fields to display.",
    // FR-UI-ADM-6 — webhook endpoint admin editor chrome (English).
    "admin.webhooks.title": "Webhook Endpoints",
    "admin.webhooks.subtitle":
      "Manage endpoints and active signing key ids (never values)",
    "admin.webhooks.column.url": "URL",
    "admin.webhooks.column.process_def": "Process Definition",
    "admin.webhooks.column.signing_key_id": "Signing Key ID",
    "admin.webhooks.column.status": "Status",
    "admin.webhooks.column.version": "Version",
    "admin.webhooks.column.actions": "Actions",
    "admin.webhooks.status.active": "active",
    "admin.webhooks.status.disabled": "disabled",
    "admin.webhooks.action.add": "Add endpoint",
    "admin.webhooks.action.disable": "Disable",
    "admin.webhooks.action.delete": "Delete",
    "admin.webhooks.dialog.create.title": "Create webhook endpoint",
    "admin.webhooks.dialog.create.label.url": "URL",
    "admin.webhooks.dialog.create.label.process_def": "Process definition ID",
    "admin.webhooks.dialog.create.cta": "Create",
    "admin.webhooks.dialog.create.success.title": "Webhook created",
    "admin.webhooks.dialog.create.success.copy_key_value":
      "Copy this value now — you will not be able to see it again.",
    "admin.webhooks.dialog.create.success.copied": "Copied!",
    "admin.webhooks.dialog.conflict.title":
      "Another admin changed this endpoint",
    "admin.webhooks.dialog.conflict.refetch": "Refetch and merge",
    "admin.webhooks.dialog.conflict.discard": "Discard my changes",
    "admin.webhooks.empty.message":
      "No webhook endpoints yet. Click Add endpoint to create one.",
    "admin.webhooks.errors.staging_only":
      "Webhook endpoint management is only available in staging.",
    "admin.webhooks.errors.version_conflict":
      "Another admin changed this endpoint. Refetch and merge, or discard your changes.",
    "admin.webhooks.errors.fetch_failure": "Failed to load webhook endpoints.",
    "admin.webhooks.errors.permission_denied":
      "You don't have permission to manage webhook endpoints.",
    "admin.webhooks.errors.rate_limit":
      "Too many requests. Try again in {{seconds}}s.",
    "admin.webhooks.banner.pending_review":
      "{count, plural, one {# endpoint modified in staging — pending review} other {# endpoints modified in staging — pending review}}",
    "admin.webhooks.banner.open_review": "Open review",
    "admin.webhooks.table.aria_label": "Webhook endpoints table",
    "admin.webhooks.keys.title": "Webhook HMAC Keys",
    "admin.webhooks.keys.active": "Active",
    "admin.webhooks.keys.grace_period": "Grace period",
    "admin.webhooks.keys.expired": "Expired",
    "admin.webhooks.keys.rotate_action": "Rotate Key",
    "admin.webhooks.keys.issue_action": "Issue Key",
    "admin.webhooks.keys.revoke_action": "Revoke",
    "admin.webhooks.keys.grace_window_label": "Grace window",
    "admin.webhooks.keys.grace_window_hours":
      "{hours, plural, one {# hour} other {# hours}}",
    "admin.webhooks.keys.grace_until_label": "Expires at",
    "admin.webhooks.keys.expired_at_label": "Expired on",
    "admin.webhooks.keys.key_id_label": "Key ID",
    "admin.webhooks.keys.created_at_label": "Created",
    "admin.webhooks.keys.state_label": "State",
    "admin.webhooks.keys.one_time_copy_title": "New key value (one-time)",
    "admin.webhooks.keys.one_time_copy_warning":
      "Copy this value now. It will NOT be shown again.",
    "admin.webhooks.keys.copy_button": "Copy",
    "admin.webhooks.keys.done_button": "Done",
    "admin.webhooks.keys.value_cleared_message":
      "This key value has been cleared for security. Rotate again to re-issue.",
    "admin.webhooks.keys.destructive_rotate_title":
      "Rotate while in grace period?",
    "admin.webhooks.keys.destructive_rotate_warning":
      "A key is currently in grace period. Rotating will revoke it and terminate its grace window. In-flight webhook deliveries using that key may fail.",
    "admin.webhooks.keys.confirm_rotate_button": "Confirm Rotate",
    "admin.webhooks.keys.cancel_button": "Cancel",
    "admin.webhooks.keys.revoke_confirm_title": "Revoke key?",
    "admin.webhooks.keys.revoke_confirm_warning":
      "Revoking is immediate and cannot be undone. Type the key ID to confirm.",
    "admin.webhooks.keys.revoke_confirm_placeholder": "Type the key ID",
    "admin.webhooks.keys.new_deliveries_tooltip":
      "Webhook deliveries created AFTER this rotation will use this key. Deliveries in flight before the rotation continue using the grace-period key until {{grace_until}}.",
    "admin.webhooks.keys.loading_error": "Failed to load HMAC keys.",
    "admin.webhooks.keys.retry": "Retry",
    "admin.webhooks.keys.permission_denied":
      "You don't have permission to manage HMAC keys.",
    "admin.webhooks.keys.countdown.separator": "and",
    "admin.webhooks.keys.countdown.expired": "Expired",
    "admin.webhooks.keys.grace_window_minutes_min":
      "minimum {{minutes}} minutes",
    "admin.webhooks.keys.grace_window_minutes_max":
      "maximum {{minutes}} minutes",
    "admin.webhooks.keys.errors.invalid_grace_window":
      "Invalid grace window. Allowed range: 1–72 hours.",
    "admin.webhooks.keys.errors.staging_only":
      "Webhook HMAC key management is only available in staging.",
    // FR-UI-ADM-14 -- tier & quota admin panel.
    "tier.title": "Tier & Quota",
    "tier.subtitle":
      "Read-only view of your tenant's tier limits and current usage.",
    "tier.tier.small": "Small",
    "tier.tier.middle": "Middle",
    "tier.tier.corporate": "Corporate",
    "tier.limits.script_wall_clock_ms": "Script wall-clock (ms)",
    "tier.limits.script_memory_pages": "Script memory pages",
    "tier.limits.storage_mb": "Storage quota (MB)",
    "tier.limits.file_storage_mb": "File storage quota (MB)",
    "tier.limits.concurrent_sandboxes": "Concurrent sandboxes",
    "tier.limits.agent_retry_budget": "Agent retry budget",
    "tier.limits.max_single_upload_mb": "Max single upload (MB)",
    "tier.limits.outbox_depth_cap": "Outbox depth cap",
    "tier.usage.storage_bytes": "Storage usage",
    "tier.usage.file_storage_bytes": "File storage usage",
    "tier.usage.concurrent_sandboxes": "Concurrent sandboxes in use",
    "tier.unavailable": "unavailable",
    "tier.state.loading": "Loading tier and quota…",
    "tier.state.fetch_failure": "Failed to load tier and quota.",
    "tier.state.permission_denied":
      "You don't have permission to view tier and quota.",
    "tier.state.rate_limit": "Too many requests. Try again in {{seconds}}s.",
    // FR-UI-ADM-14 -- definition audit log viewer.
    "audit.title": "Definition Audit Log",
    "audit.filter.definition_type": "Definition type",
    "audit.filter.all_types": "All types",
    "audit.filter.author": "Author",
    "audit.filter.after": "After",
    "audit.filter.before": "Before",
    "audit.filter.clear": "Clear filters",
    "audit.column.definition_type": "Type",
    "audit.column.definition_id": "Definition",
    "audit.column.action": "Action",
    "audit.column.author": "Author",
    "audit.column.agent": "Agent",
    "audit.column.agent_label": "Agent-authored entry",
    "audit.column.task_spec": "Task spec",
    "audit.column.orchestrator": "Orchestrator",
    "audit.column.version": "Version",
    "audit.column.created_at": "When",
    "audit.column.definition_type.form": "Form",
    "audit.column.definition_type.process": "Process",
    "audit.column.definition_type.entity": "Entity",
    "audit.column.definition_type.permission": "Permission",
    "audit.column.definition_type.task_spec": "Task spec",
    "audit.empty": "No audit entries match the current filters.",
    "audit.pagination.label": "Audit log pagination",
    "audit.pagination.previous": "Previous",
    "audit.pagination.next": "Next",
    "audit.state.loading": "Loading audit log…",
    "audit.state.fetch_failure": "Failed to load audit log.",
    "audit.state.permission_denied":
      "You don't have permission to view the audit log.",
    "audit.state.validation_error":
      "Invalid filter — please check your inputs.",
    "audit.state.rate_limit": "Too many requests. Try again in {{seconds}}s.",
    // FR-UI-ADM-8 — effects admin (HTTP endpoints + email channels)
    "admin.effects.http.page.title": "HTTP Effect Endpoints",
    "admin.effects.http.empty": "No HTTP endpoints configured yet.",
    "admin.effects.http.table_aria_label": "HTTP effect endpoints table",
    "admin.effects.http.column.url": "URL",
    "admin.effects.http.column.method": "Method",
    "admin.effects.http.column.status": "Status",
    "admin.effects.http.column.actions": "Actions",
    "admin.effects.http.action.create": "Add endpoint",
    "admin.effects.http.action.disable": "Disable",
    "admin.effects.http.action.delete": "Delete",
    "admin.effects.http.field.url": "URL",
    "admin.effects.http.field.method": "HTTP Method",
    "admin.effects.http.field.timeout": "Timeout (seconds)",
    "admin.effects.http.dialog.create.title": "Add HTTP endpoint",
    "admin.effects.http.dialog.create.success.title": "Endpoint created",
    "admin.effects.http.dialog.create.success.description":
      "Copy the auth secret now — it will not be shown again.",
    "admin.effects.http.dialog.create.success.copy_secret": "Copy secret",
    "admin.effects.email.page.title": "Email Notification Channels",
    "admin.effects.email.empty": "No email channels configured yet.",
    "admin.effects.email.table_aria_label": "Email channels table",
    "admin.effects.email.column.from": "From address",
    "admin.effects.email.column.subject": "Subject",
    "admin.effects.email.column.locales": "Locales",
    "admin.effects.email.column.status": "Status",
    "admin.effects.email.column.actions": "Actions",
    "admin.effects.email.action.create": "Add channel",
    "admin.effects.email.action.disable": "Disable",
    "admin.effects.email.action.delete": "Delete",
    "admin.effects.email.field.from_address": "From address",
    "admin.effects.email.field.subject": "Subject",
    "admin.effects.email.field.provider": "Provider",
    "admin.effects.email.field.body": "Body (locale-aware)",
    "admin.effects.email.dialog.create.title": "Add email channel",
    "admin.effects.email.dialog.create.success.title": "Channel created",
    "admin.effects.email.dialog.create.success.description":
      "Copy the SMTP secret now — it will not be shown again.",
    "admin.effects.email.dialog.create.success.copy_secret": "Copy secret",
    "admin.effects.status.active": "active",
    "admin.effects.status.disabled": "disabled",
    "admin.effects.dialog.conflict.title": "Version conflict",
    "admin.effects.dialog.conflict.server_version": "Server version",
    "admin.effects.dialog.conflict.discard": "Discard my changes",
    "admin.effects.dialog.conflict.refetch": "Refetch latest",
    // FR-UI-ADM-10 -- secret reference manager admin chrome (English fallback).
    "admin.secrets.title": "Secrets",
    "admin.secrets.create_button": "Create secret",
    "admin.secrets.empty_state": "No secrets created yet.",
    "admin.secrets.table.name": "Name",
    "admin.secrets.table.ref_id": "Reference id",
    "admin.secrets.table.status": "Status",
    "admin.secrets.table.created_at": "Created",
    "admin.secrets.table.updated_at": "Updated",
    "admin.secrets.table.version": "Version",
    "admin.secrets.table.actions": "Actions",
    "admin.secrets.dialog.create.title": "Create secret",
    "admin.secrets.dialog.create.description":
      "Enter a name. The server will generate a value.",
    "admin.secrets.dialog.create.name_label": "Secret name",
    "admin.secrets.dialog.create.name_hint":
      "Up to 120 characters. Used for identification in this list only.",
    "admin.secrets.dialog.create.success.title": "Secret created",
    "admin.secrets.dialog.create.success.copy_secret_value":
      "Copy the secret value now -- it will not be shown again.",
    "admin.secrets.dialog.create.success.copied": "Copied!",
    "admin.secrets.dialog.impact.title": "Delete secret",
    "admin.secrets.dialog.impact.subtitle":
      "Secret '{{name}}' will be deleted. First, check where it is referenced.",
    "admin.secrets.dialog.impact.references_label":
      "{{count}} effect configuration(s) reference this secret:",
    "admin.secrets.dialog.impact.kind_http_endpoint": "HTTP endpoint",
    "admin.secrets.dialog.impact.kind_email_channel": "Email channel",
    "admin.secrets.dialog.impact.safe_to_delete":
      "No effect configuration references this secret. Safe to delete.",
    "admin.secrets.dialog.impact.acknowledge_label":
      "I understand these effects will lose access to the secret after deletion.",
    "admin.secrets.banner.pending_review":
      "{{count}} secret(s) pending promotion review",
    "admin.secrets.banner.open_review": "Open review",
    "admin.secrets.status.active": "active",
    "admin.secrets.status.disabled": "disabled",
    // FR-UI-ADM-11 -- template registry admin chrome (English fallback).
    "admin.templates.registry.title": "Template Registry",
    "admin.templates.registry.column.template_id": "Template ID",
    "admin.templates.registry.column.version": "Version",
    "admin.templates.registry.column.description": "Description",
    "admin.templates.registry.column.publisher_kind": "Publisher",
    "admin.templates.registry.column.pre_vetted": "Pre-vetted",
    "admin.templates.registry.column.published_at": "Published",
    "admin.templates.registry.column.published_by": "By",
    "admin.templates.registry.column.content_hash": "Content hash",
    "admin.templates.registry.column.actions": "Actions",
    "admin.templates.registry.badge.auto_activates":
      "Auto-activates on provisioning",
    "admin.templates.registry.badge.tooltip":
      "This template auto-promotes from staging to production when chosen during tenant provisioning. When installed manually via this page, it still requires the normal human-review gate before promotion.",
    "admin.templates.registry.action.install": "Install",
    "admin.templates.registry.action.installing": "Installing…",
    // FR-UI-OPS-1 — operator console chrome (English fallback).
    // Flat-key layout matching the rest of the bundle; see the
    // COMMON_RESOURCES doc comment for the rationale.
    "operator.title": "Operator Console",
    "operator.summary.title": "Cross-tenant summary",
    "operator.summary.tenantCount": "Tenants",
    "operator.summary.activeInstances": "Active instances",
    "operator.summary.errorRate": "Error rate",
    "operator.summary.empty": "No tenants yet",
    "operator.summary.computedAt": "Last updated",
    // FR-UI-OPS-3 — per-tenant error-count badge suffix
    // (renders as e.g. "6 err" inside the redesigned <Badge> cell).
    "operator.summary.errorUnit": "err",
    "operator.tokenPrompt.label": "Operator token",
    "operator.tokenPrompt.button": "Enter",
    "operator.tokenPrompt.placeholder": "Paste your operator token",
    "operator.tokenPrompt.error.unauthorized":
      "Unauthorized. Please check your token and try again.",
    "apex.title": "ASCOA Platform",
    "apex.subtitle":
      "You are at the platform apex. The operator console is the control plane for tenant provisioning, deprovisioning, and cross-tenant observability.",
    "apex.how_to_enter_title": "Enter the operator console",
    "apex.how_to_enter_body":
      "The operator console sits on the same domain under /operator/. You will need a platform operator token (configured in PLATFORM_ADMIN_TOKEN). Click below to enter the token gate; once accepted, you land on the cross-tenant summary.",
    "apex.open_operator": "Open operator console",
    "apex.tour_title": "Tenant URLs",
    "apex.tour_body":
      "Each tenant runs on a subdomain. The operator console can show you the full list once you sign in. Tenants are reached directly via their subdomain; no entry from the bare apex.",
    "apex.tour_local": "default dev tenant",
    "apex.tour_tenant": "per-tenant subdomain (provisioned)",
    "operator.error.fetchFailure": "Failed to load summary.",
    "operator.error.retry": "Retry",
    "operator.summary.table.tenant": "Tenant",
    "operator.summary.table.instanceCount": "Active instances",
    "operator.summary.table.errorCount": "Errors",
    "operator.summary.table.activeSandboxCount": "Active sandboxes",
    "operator.summary.table.aria_label": "Per-tenant breakdown",
    // FR-UI-OPS-2 — operator nav shell + drill-down + audit
    // placeholder chrome (English fallback).
    "operator.nav.label": "Operator sections",
    "operator.nav.summary": "Summary",
    "operator.nav.tenants": "Tenants",
    "operator.nav.effects": "Effects / Ops",
    "operator.nav.audit": "Audit",
    "operator.drillDown.title": "Tenant details",
    "operator.drillDown.instanceCount": "Active instances",
    "operator.drillDown.errorCount": "Errors",
    "operator.drillDown.activeSandboxCount": "Active sandboxes",
    "operator.drillDown.openAdmin": "Open tenant /admin",
    "operator.drillDown.openFor": "Open details for tenant {{slug}}",
    "operator.drillDown.close": "Close",
    "operator.audit.title": "Audit",
    "operator.audit.placeholder": "Audit log is not yet implemented.",
    "operator.tenants.loadError": "Failed to load tenants.",
    "operator.topbar.localeLabel": "Interface language",
    "operator.tenants.list.title": "Tenants",
    "operator.tenants.list.empty": "No tenants found.",
    "operator.tenants.list.refresh": "Refresh",
    "operator.tenants.list.loading": "Loading tenants…",
    "operator.tenants.list.errorPrefix": "Failed to load tenants:",
    "operator.tenants.list.deprovision": "De-provision",
    "operator.tenants.list.ariaLabel":
      "List of tenants with de-provisioning actions",
    "operator.tenants.list.col.slug": "Slug",
    "operator.tenants.list.col.name": "Name",
    "operator.tenants.list.col.tier": "Tier",
    "operator.tenants.list.col.status": "Status",
    "operator.tenants.list.col.tenantId": "Tenant ID",
    "operator.tenants.list.col.actions": "Actions",
    // ISS-509 (BA ruling): localized "Unknown" status label for the
    // decoder fallback path (status field absent from wire).
    "operator.tenants.list.status.unknown": "Unknown",
    // ISS-510: localized status + tier value labels.
    "operator.tenants.list.status.active": "Active",
    "operator.tenants.list.status.disabled": "Disabled",
    "operator.tenants.list.status.quiesced": "Quiesced",
    "operator.tenants.list.tier.small": "Small",
    "operator.tenants.list.tier.middle": "Middle",
    "operator.tenants.list.tier.corporate": "Corporate",
    // FR-UI-CABINET-1 — tenant workspace shell ("My Cabinet") chrome
    // (English locale). Flat-key layout per COMMON_RESOURCES convention.
    "cabinet.title": "Tenant workspace",
    "cabinet.nav.label": "Workspace sections",
    "cabinet.nav.cabinet": "My Cabinet",
    "cabinet.nav.tasks": "My Tasks",
    "cabinet.nav.processes": "Processes",
    "cabinet.nav.admin": "Admin",
    "cabinet.nav.section.records": "Records",
    "cabinet.nav.section.admin": "Admin",
    "cabinet.landing.title": "My Cabinet",
    "cabinet.landing.subtitle": "Your work at a glance.",
    "cabinet.landing.tiles_label": "Summary",
    "cabinet.landing.tasks_due_soon": "Tasks due soon",
    "cabinet.landing.open_processes": "Open processes",
    "cabinet.landing.failed_processes": "Failed processes",
    "cabinet.landing.recent_records": "Recent records",
    "cabinet.landing.recent_records.placeholder":
      "Recent records appear here once per-entity-type permissions are configured.",
    "cabinet.landing.recent_records.configureAction": "Configure permissions",
    "cabinet.admin.title": "Admin",
    "cabinet.admin.subtitle": "Manage your tenant configuration.",
    "cabinet.admin.grid_label": "Admin areas",
    "cabinet.admin.denied": "You do not have access to the admin section.",
    "cabinet.admin.tile.users": "Roles",
    "cabinet.admin.tile.permissions_matrix": "Permissions Matrix",
    "cabinet.admin.tile.record_field_permissions": "Record & Field Permissions",
    "cabinet.admin.tile.webhooks": "Webhook Endpoints",
    "cabinet.admin.tile.hmac_keys": "HMAC Keys",
    "cabinet.admin.tile.http_endpoints": "HTTP Endpoints",
    "cabinet.admin.tile.email_channels": "Email Channels",
    "cabinet.admin.tile.secrets": "Secrets",
    "cabinet.admin.tile.templates": "Templates",
    "cabinet.admin.tile.template_updates": "Template Updates",
    "cabinet.admin.tile.tier": "Tier & Quota",
    "cabinet.admin.tile.audit": "Audit Log",
    "cabinet.admin.tile.forms": "Forms",
    "cabinet.admin.tile.processes": "Processes",
    "cabinet.errors.fetch_failure": "Failed to load.",
    "cabinet.errors.permission_denied": "You do not have access to this.",
    "cabinet.errors.rate_limit": "Too many requests. Please wait.",
    "cabinet.errors.stale_version": "This version is no longer available.",
    "cabinet.errors.tile_label": "Summary",
    "cabinet.errors.retry_after": "Retry available in {{seconds}}s.",
    "admin.templates.registry.action.install_aria":
      "Install template {{template_id}} version {{version}}",
    "admin.templates.registry.success.installed":
      "Template successfully installed into staging",
    "admin.templates.registry.success.installed.review_link":
      "Open promotion review",
    "admin.templates.registry.empty.message": "No published templates yet.",
    "admin.templates.registry.table.aria_label": "Template registry table",
    "admin.templates.registry.errors.staging_only":
      "Template install is only available in staging.",
    "admin.templates.registry.errors.permission_denied":
      "You don't have permission to install templates.",
    "admin.templates.registry.errors.fetch_failure":
      "Failed to load the template registry.",
    "admin.templates.registry.errors.rate_limit":
      "Too many requests. Please wait.",
    "admin.templates.registry.errors.seeding_failed":
      "Failed to install template: seeding error.",
    "admin.templates.registry.errors.publisher_not_allowed":
      "Template publisher is not allowed for this tenant.",
    "admin.templates.registry.errors.template_not_found":
      "Template or version not found.",
    // FR-UI-ADM-13 — template export/import admin chrome (English
    // locale fallback). Flat-key layout per COMMON_RESOURCES
    // convention.
    "admin.templates.export_import.action.export": "Export",
    "admin.templates.export_import.action.exporting": "Exporting…",
    "admin.templates.export_import.action.export_aria":
      "Export template {{template_id}} version {{version}}",
    "admin.templates.export_import.action.import": "Import",
    "admin.templates.export_import.notice.operator_console_required":
      "Use the operator console to import templates.",
    "admin.templates.export_import.dialog.import.title": "Import template",
    "admin.templates.export_import.dialog.import.file_label":
      "Template bundle file",
    "admin.templates.export_import.dialog.import.submit": "Submit",
    "admin.templates.export_import.dialog.import.submit_disabled_tooltip":
      "Use the operator console to submit the import",
    "admin.templates.export_import.dialog.replace.title":
      "Replace existing template?",
    "admin.templates.export_import.dialog.replace.existing_label": "Existing:",
    "admin.templates.export_import.dialog.replace.imported_label": "Imported:",
    "admin.templates.export_import.dialog.replace.diff":
      "existing v{{existing_version}} → import v{{import_version}}",
    "admin.templates.export_import.dialog.replace.confirm": "Replace",
    "admin.templates.export_import.dialog.replace.cancel": "Cancel",
    "admin.templates.export_import.dialog.replace.server_rules_notice":
      "Server-side version immutability rules apply on submit — this confirmation does not bypass them.",
    "admin.templates.export_import.errors.manifest_missing":
      "manifest.json not found in the archive.",
    "admin.templates.export_import.errors.manifest_invalid_json":
      "manifest.json is not valid JSON.",
    "admin.templates.export_import.errors.cross_publisher_kind":
      "Only platform-published bundles can be imported by operators.",
    "admin.templates.export_import.errors.manifest_incomplete":
      "Manifest is missing required fields.",
    "admin.templates.export_import.errors.file_pick_failed":
      "File could not be read.",
    "admin.templates.export_import.errors.payload_too_large":
      "Bundle is too large.",
    "admin.templates.export_import.errors.validation_failed":
      "Bundle validation failed.",
    "admin.templates.export_import.tooltip.replaces_preserves_customizations":
      "FR-TPL-4 preserves tenant customizations on install — this is not affected by re-importing the template version.",
    "admin.templates.export_import.source.imported": "imported",
    "admin.templates.export_import.source.native": "native",
    // FR-UI-ADM-2 — record-level + field-level permission editors
    // (English locale fallback). Flat-key layout per
    // COMMON_RESOURCES convention.
    "admin.record_permissions.page_title":
      "Record-level & field-level permission editor",
    "admin.record_permissions.role": "Role",
    "admin.record_permissions.cell.ownership": "Ownership rule",
    "admin.record_permissions.cell.fields": "Field permissions",
    "admin.record_permissions.cell.owner": "Owner field",
    "admin.record_permissions.editor.title": "Edit record rule",
    "admin.record_permissions.editor.tab.ownership": "Ownership",
    "admin.record_permissions.editor.tab.condition": "Condition",
    "admin.record_permissions.editor.expr_label": "Expression (expr-lang)",
    "admin.record_permissions.editor.preview_button": "Preview",
    "admin.record_permissions.editor.preview.loading": "Loading sample…",
    "admin.record_permissions.editor.preview.matches":
      "Matches {{matches}} of {{total}} sampled records (preview)",
    "admin.record_permissions.conflict.title": "Version conflict",
    "admin.record_permissions.conflict.body":
      "These permissions have changed since you last loaded. Choose an action.",
    "admin.record_permissions.conflict.refetch": "Refetch latest",
    "admin.record_permissions.conflict.merge": "Merge my changes",
    "admin.record_permissions.conflict.discard": "Discard my changes",
    "admin.record_permissions.errors.staging_only_body":
      "Permission editors are only available in staging.",
    "admin.record_permissions.error.title": "Failed to load",
    "admin.field_permissions.title": "Edit field permissions",
    "admin.field_permissions.field": "Field",
    "admin.field_permissions.state": "State",
    "admin.field_permissions.state.readable": "Readable",
    "admin.field_permissions.state.writable": "Writable",
    "admin.field_permissions.state.hidden": "Hidden",
    "admin.entity_field_owner.title": "Entity owner field",
    "admin.entity_field_owner.field_label": "Owner field",

    // FR-UI-ADM-5 English locale additions.
    "admin.users.title": "Users & Roles",
    "admin.users.subtitle":
      "Assign Keycloak realm roles to tenant users. Changes take effect on the user's next OIDC token refresh.",
    "admin.users.column.display_name": "Name",
    "admin.users.column.email": "Email",
    "admin.users.column.assigned_roles": "Assigned roles",
    "admin.users.column.actions": "Actions",
    "admin.users.column.select_all": "Select all",
    "admin.users.column.select_user": "Select user {{name}}",
    "admin.users.action.assign_roles": "Assign roles",
    "admin.users.action.assign_roles_for": "Assign roles for {{name}}",
    "admin.users.action.sync_refresh": "Refresh sync",
    "admin.users.action.sync_refresh_cooldown": "Please wait…",
    "admin.users.sync_status.ok": "Sync OK",
    "admin.users.sync_status.degraded": "Sync degraded",
    "admin.users.sync_status.failed": "Sync failed",
    "admin.users.dialog.role_picker.title": "Select roles",
    "admin.users.dialog.role_picker.label.roles": "Roles",
    "admin.users.dialog.role_picker.empty":
      "No roles available. Sync Keycloak.",
    "admin.users.dialog.role_picker.cta": "Save",
    "admin.users.dialog.role_picker.error": "Save failed.",
    "admin.users.dialog.bulk_assign.title": "Bulk assign roles ({{count}})",
    "admin.users.dialog.bulk_assign.cta": "Assign {{count}}",
    "admin.users.dialog.bulk_assign.roles_placeholder":
      "admin, viewer, editor (comma-separated)",
    "admin.users.dialog.bulk_assign.partial_success":
      "Assigned to {{assigned}} users.",
    "admin.users.dialog.bulk_assign.failures_label": "Failures ({{count}})",
    "admin.users.dialog.bulk_assign.dismiss": "Done",
    "admin.users.dialog.bulk_assign.error": "Bulk assignment failed.",
    "admin.users.bulk.selected_count": "Selected: {{count}}",
    "admin.users.orphan_panel.toggle": "Orphaned users ({{count}}) — review",
    "admin.users.orphan_panel.title": "Orphaned users",
    "admin.users.orphan_panel.empty": "No orphaned users.",
    "admin.users.orphan_panel.remove_action":
      "Remove role assignments for {{id}}",
    "admin.users.orphan_panel.remove_action_short": "Clean",
    "admin.users.orphan_panel.cleaned": "Cleaned",
    "admin.users.notice.takes_effect_next_login":
      "Roles take effect on the user's next login.",
    "admin.users.notice.dismiss": "Dismiss",
    "admin.users.empty.message": "No users found.",
    "admin.users.error.fetch_failure": "Failed to load users.",
    "admin.users.error.permission_denied":
      "You do not have permission to view users.",
    "admin.users.error.stale_version": "Version is stale. Refresh the page.",
    // FR-UI-ADM-9 — effects operations panel chrome.
    "admin.effects.metrics.page_title": "Effects Operations",
    "admin.effects.metrics.section.metrics": "Per-channel metrics",
    "admin.effects.metrics.section.circuit":
      "Per-endpoint circuit-breaker state",
    "admin.effects.metrics.table_label": "Effect channel metrics",
    "admin.effects.metrics.circuit_table_label": "Circuit-breaker state",
    "admin.effects.metrics.no_endpoints":
      "No circuit-breaker endpoints configured.",
    "admin.effects.metrics.column.channel_name": "Channel",
    "admin.effects.metrics.column.delivery_count": "Deliveries (since start)",
    "admin.effects.metrics.column.success_rate": "Success %",
    "admin.effects.metrics.column.retry_count": "Retries",
    "admin.effects.metrics.column.dead_letter_count": "Dead-letters",
    "admin.effects.metrics.column.latency_p50": "p50 ms",
    "admin.effects.metrics.column.latency_p95": "p95 ms",
    "admin.effects.metrics.column.latency_p99": "p99 ms",
    "admin.effects.metrics.column.endpoint": "Endpoint",
    "admin.effects.metrics.column.state": "State",
    "admin.effects.metrics.column.time_in_state": "Time in state",
    "admin.effects.metrics.column.failures_5min": "Failures observed (5 min)",
    "admin.effects.metrics.column.consecutive_failures": "Consecutive failures",
    "admin.effects.metrics.column.action": "Action",
    "admin.effects.metrics.refresh.now": "Refresh now",
    "admin.effects.metrics.last_refreshed": "Last refreshed: {{time}}",
    "admin.effects.metrics.last_refreshed_prefix": "Last refreshed:",
    "admin.effects.metrics.historical_placeholder_title":
      "Historical data (last 7 days)",
    "admin.effects.metrics.historical_placeholder_body":
      "Historical data (last 7 days) — coming soon (requires metrics persistence; tracked gap in ARCH §8.4).",
    "admin.effects.metrics.circuit_open_banner":
      "Circuit open for {{endpoint}}. ",
    "admin.effects.metrics.circuit_open_affected":
      "Affected deliveries queued for retry.",
    "admin.effects.metrics.banner_dismiss": "Dismiss",
    "admin.effects.metrics.banner_dismiss_label":
      "Dismiss circuit-open notice for {{endpoint}}",
    "admin.effects.metrics.policy.consecutive_failures":
      "Consecutive failures (1-100)",
    "admin.effects.metrics.policy.open_timeout_ms":
      "Open timeout ms (1000-300000)",
    "admin.effects.metrics.policy.half_open_max_probes":
      "Half-open max probes (1-10)",
    "admin.effects.metrics.policy.out_of_range":
      "Value must be in [{{min}}, {{max}}]",
    "admin.effects.metrics.policy.updated_toast": "Policy updated",
    "admin.effects.metrics.dialog_title": "Configure breaker",
    "admin.effects.metrics.dialog_save": "Save",
    "admin.effects.metrics.dialog_saving": "Saving…",
    "admin.effects.metrics.dialog_cancel": "Cancel",
    "admin.effects.metrics.dialog_description":
      "Adjust the circuit-breaker parameters for the selected endpoint.",
    "admin.effects.metrics.configure_endpoint":
      "Configure breaker for {{endpoint}}",
    "admin.effects.metrics.loading": "Loading effects metrics…",
    "admin.effects.metrics.error_fetch":
      "Failed to load effects metrics: {{message}}",
    "admin.effects.metrics.error_401":
      "Operator credentials required. Please sign in again.",
    "admin.effects.metrics.error_504":
      "Effects server is not responding (timeout).",
    "admin.effects.metrics.error_5xx": "Effects server returned an error.",
    // FR-UI-BLD-1 -- form-builder chrome (English fallback).
    "admin.builder.page_title": "Form Builder",
    "admin.builder.save.cta": "Save form",
    "admin.builder.save.saving": "Saving...",
    "admin.builder.save.invalid": "Invalid input",
    "admin.builder.palette.aria": "Field palette",
    "admin.builder.palette.title": "Fields",
    "admin.builder.canvas.title": "Form canvas",
    "admin.builder.canvas.empty": "Drag a field from the palette to begin",
    "admin.builder.canvas.remove": "Remove field {{id}}",
    "admin.builder.settings.aria": "Settings",
    "admin.builder.settings.title": "Settings",
    "admin.builder.settings.entity_type": "Entity type",
    "admin.builder.settings.field_heading": "Field {{id}}",
    "admin.builder.settings.label": "Label",
    "admin.builder.settings.placeholder": "Placeholder",
    "admin.builder.settings.required": "Required",
    "admin.builder.settings.visible_when": "Visible when (formula)",
    "admin.builder.settings.computed_from": "Computed from (formula)",
    "admin.builder.settings.target_entity": "Target entity",
    "admin.builder.settings.no_selection": "Select a field on the canvas",
    "admin.builder.formula.ok": "Formula valid",
    "admin.builder.formula.error": "Formula invalid",
    "admin.builder.field_types.string": "String",
    "admin.builder.field_types.text": "Text",
    "admin.builder.field_types.number": "Number",
    "admin.builder.field_types.integer": "Integer",
    "admin.builder.field_types.float": "Float",
    "admin.builder.field_types.boolean": "Boolean",
    "admin.builder.field_types.date": "Date",
    "admin.builder.field_types.datetime": "Datetime",
    "admin.builder.field_types.enum": "Enum",
    "admin.builder.field_types.reference": "Reference",
    // FR-UI-BLD-1 -- BPM designer chrome (English fallback).
    "admin.designer.page_title": "Process Designer",
    "admin.designer.save.cta": "Save process",
    "admin.designer.save.saving": "Saving...",
    "admin.designer.save.invalid": "Invalid input",
    "admin.designer.palette.aria": "Node palette",
    "admin.designer.palette.title": "Nodes",
    "admin.designer.canvas.title": "Process canvas",
    "admin.designer.canvas.empty": "Drag a node from the palette to begin",
    "admin.designer.canvas.aria": "BPM process canvas",
    "admin.designer.canvas.remove": "Remove node {{id}}",
    "admin.designer.canvas.dragging": "Dragging {{id}}",
    "admin.designer.settings.aria": "Properties",
    "admin.designer.settings.title": "Properties",
    "admin.designer.settings.process_name": "Process name",
    "admin.designer.settings.process_description": "Description",
    "admin.designer.settings.node_heading": "Node {{id}}",
    "admin.designer.settings.node_label": "Label",
    "admin.designer.settings.form_id": "Form",
    "admin.designer.settings.candidate_role_id": "Candidate role",
    "admin.designer.settings.assignee_user_id": "Assigned user",
    "admin.designer.settings.due_in_seconds": "Due in (seconds)",
    "admin.designer.settings.script_id": "Script",
    "admin.designer.settings.script_formula": "Formula (optional)",
    "admin.designer.settings.delay_expression": "Delay (expression)",
    "admin.designer.settings.edge_heading": "Transition {{id}}",
    "admin.designer.settings.edge_guard": "Guard (formula)",
    "admin.designer.settings.no_config": "This node has no settings",
    "admin.designer.settings.no_selection":
      "Select a node or transition on the canvas",
    "admin.designer.guard.ok": "Guard valid",
    "admin.designer.guard.error": "Guard invalid",
    "admin.designer.node_types.start": "Start",
    "admin.designer.node_types.end": "End",
    "admin.designer.node_types.userTask": "User task",
    "admin.designer.node_types.scriptTask": "Script task",
    "admin.designer.node_types.xorGateway": "XOR gateway",
    "admin.designer.node_types.andGateway": "AND gateway",
    "admin.designer.node_types.timerCatch": "Timer",
    // FR-UI-BLD-1 -- list-page chrome (English fallback, ISS-f4fa1b7b-258).
    "admin.forms.list.title": "Forms",
    "admin.forms.list.empty": "No forms yet.",
    "admin.forms.list.aria": "Forms list",
    "admin.processes.list.title": "Processes",
    "admin.processes.list.empty": "No processes yet.",
    "admin.processes.list.aria": "Processes list",
    // FR-UI-DASH-1 -- per-tenant observability dashboard chrome
    // (English fallback). 19 chrome keys covering AC-1 widgets,
    // AC-2 alert-rule authoring, AC-3 active-alerts panel,
    // AC-4 refresh / loading states, AC-5 drill-down labels.
    "observability.dashboard.title": "Tenant observability",
    "observability.dashboard.subtitle":
      "Aggregated metrics, error rates, and step durations for this tenant.",
    "observability.dashboard.refresh": "Refresh now",
    "observability.dashboard.author_alert": "Author alert rule",
    "observability.dashboard.error.rate_limit":
      "Too many requests. Please wait.",
    "observability.dashboard.widget.drill_down": "View failed instances",
    "observability.dashboard.widget.instance_count.title":
      "Process instances (last 24h)",
    "observability.dashboard.widget.instance_count.active": "Active",
    "observability.dashboard.widget.instance_count.failed": "Failed",
    "observability.dashboard.widget.instance_count.completed": "Completed",
    "observability.dashboard.widget.error_rate.title":
      "Error rate (last 7 days)",
    "observability.dashboard.widget.error_rate.empty":
      "No recent step activity.",
    "observability.dashboard.widget.error_rate.sparkline_aria":
      "Per-hour error rate sparkline",
    "observability.dashboard.widget.step_duration.title":
      "Step duration percentiles",
    "observability.dashboard.widget.step_duration.p50": "p50",
    "observability.dashboard.widget.step_duration.p95": "p95",
    "observability.dashboard.widget.step_duration.p99": "p99",
    "observability.dashboard.widget.process_breakdown.title":
      "Instance count by process type",
    "observability.dashboard.widget.process_breakdown.empty":
      "No process instances yet.",
    "observability.dashboard.widget.process_breakdown.instances_label":
      "instances",
    "observability.dashboard.widget.process_breakdown.failed_label": "failed",
    "observability.dashboard.alerts.title": "Active alerts",
    "observability.dashboard.alerts.empty":
      "Active alerts will appear here once FR-OBS-4 ships.",
    "observability.dashboard.alert_rule.title": "Author alert rule",
    "observability.dashboard.alert_rule.name_label": "Rule name",
    "observability.dashboard.alert_rule.severity_label": "Severity",
    "observability.dashboard.alert_rule.expression_label": "Expression",
    "observability.dashboard.alert_rule.save": "Save",
    "observability.dashboard.alert_rule.saving": "Saving…",
    "observability.dashboard.alert_rule.error.persist_unavailable":
      "alert-rule persistence will ship with FR-OBS-4",
    // FR-UI-CABINET-2 — workspace chrome (topbar + footer + breadcrumbs).
    "cabinet.topbar.skipToMain": "Skip to main content",
    "cabinet.topbar.environment.production": "Production",
    "cabinet.topbar.environment.staging": "Staging",
    "cabinet.topbar.environment.sandbox": "Sandbox",
    "cabinet.languageMenu.label.en": "English",
    "cabinet.languageMenu.label.ru": "Русский",
    "cabinet.languageMenu.label.kk": "Қазақша",
    "cabinet.languageMenu.label.uk": "Ukrainian",
    "cabinet.languageMenu.label.be": "Belarusian",
    "cabinet.footer.copyright": "© {{year}} ABiTech. All rights reserved.",
    "cabinet.footer.supportLabel": "Support",
    "cabinet.notfound.title": "This page is not built yet",
    "cabinet.notfound.subtitle":
      "The address you opened does not have a screen yet. The team knows.",
    "cabinet.notfound.back": "Back to My Cabinet",
    // FR-UI-CABINET-3 — required user parameters page + chrome button (English fallback).
    "cabinet.required_user_parameters.menu_label": "Required user parameters",
    "cabinet.required_user_parameters.title": "Required user parameters",
    "cabinet.required_user_parameters.subtitle":
      "Your tenant requires these parameters.",
    "cabinet.required_user_parameters.save": "Save",
    "cabinet.required_user_parameters.resolve": "Provide values",
    "cabinet.required_user_parameters.retry": "Retry",
    "cabinet.required_user_parameters.missing": "Missing",
    "cabinet.required_user_parameters.not_provided": "Not yet provided",
    "cabinet.required_user_parameters.supplied": "Provided",
    "cabinet.required_user_parameters.missing_count":
      "{count} required parameters missing",
    "cabinet.required_user_parameters.missing_badge_aria":
      "Missing required parameter",
    "cabinet.required_user_parameters.stale_version_banner":
      "This page was modified by another session. Refresh to continue.",
    "cabinet.required_user_parameters.stale_version.title": "Version conflict",
    "cabinet.required_user_parameters.stale_version.body":
      "This page was modified by another session. Current row version: {{version}}.",
    "cabinet.required_user_parameters.stale_version.refetch":
      "Get latest version",
    "cabinet.required_user_parameters.fetch_failure.title":
      "We could not load this page",
    "cabinet.required_user_parameters.fetch_failure.body": "Please try again.",
    "cabinet.required_user_parameters.permission_denied.title":
      "You don't have access to this",
    "cabinet.required_user_parameters.permission_denied.body":
      "Contact your administrator if you believe this is a mistake.",
    "cabinet.required_user_parameters.rate_limit.title": "Too many requests",
    "cabinet.required_user_parameters.rate_limit.body":
      "Please wait a moment and try again.",
    "cabinet.required_user_parameters.empty.title": "No required parameters",
    "cabinet.required_user_parameters.empty.body":
      "Your tenant has not configured any required user parameters.",
    "cabinet.required_user_parameters.summary.complete":
      "All required parameters are provided.",
    "cabinet.required_user_parameters.summary.missing_count":
      "{count} required parameters are still missing.",
    "cabinet.required_user_parameters.summary.supplied_count":
      "{count} provided.",
    // FR-CFG-1..4: tenant presentation, preferences, help, module-config (English).
    "theme.picker.title": "Theme",
    "theme.picker.preview": "Preview",
    "theme.picker.save": "Save",
    "theme.picker.discard": "Discard",
    "theme.picker.empty": "No saved themes.",
    "theme.picker.fetch_failure": "Failed to load theme.",
    "theme.picker.permission_denied": "No access to theme.",
    "theme.picker.stale_version": "Theme was updated — please refresh.",
    "theme.picker.rate_limit": "Too many requests. Retry in {{seconds}}s.",
    "theme.picker.validation_error": "Theme has validation errors.",
    "theme.fallback.platform_default": "Platform default theme",
    "preferences.favorites": "Favorites",
    "preferences.page_settings": "Page settings",
    "preferences.current_org": "Current organisation",
    "preferences.current_location": "Current location",
    "preferences.current_locale": "Interface language",
    "preferences.save.success": "Preferences saved",
    "preferences.save.stale_rv": "Preferences were updated — please refresh.",
    "preferences.admin_read_only": "Admin-only",
    "help.tour.step_counter": "Step {{current}} of {{total}}",
    "help.tour.next": "Next",
    "help.tour.back": "Back",
    "help.tour.skip": "Skip",
    "help.tour.dismiss": "Dismiss",
    "help.tour.permission_denied.title": "Tour unavailable",
    "help.tour.permission_denied.body": "You do not have access to this tour.",
    "help.tour.permission_denied.target_id": "Target identifier",
    "help.tour.stale_version.title": "Tour was updated",
    "help.tour.stale_version.detail": "Tour steps have changed.",
    "help.tour.stale_version.active_conflict": "Active version conflict",
    "help.tour.stale_version.reset_progress": "Reset progress",
    "help.tour.rate_limit.title": "Too many requests",
    "help.tour.rate_limit.detail": "Retry in {{seconds}} seconds.",
    "help.tour.rate_limit.countdown": "Retry in {{seconds}}s",
    "help.tour.validation_error.title": "Tour has errors",
    "help.tour.validation_error.item": "Step {{step}}: {{error}}",
    "help.center.title": "Help center",
    "help.center.empty.title": "No tours available",
    "help.center.empty.body":
      "When an administrator publishes a tour, it appears here.",
    "help.center.empty.author": "Author",
    "config.editor.title": "Module configuration",
    "config.editor.namespace": "Namespace",
    "config.editor.environment": "Environment",
    "config.editor.value": "Value",
    "config.editor.save": "Save",
    "config.editor.discard": "Discard",
    "config.editor.promote": "Promote",
    "config.editor.schema_error.title": "Schema error",
    "config.editor.schema_error.item": "{{path}}: {{message}}",
    "config.editor.stale_rv":
      "Configuration version is stale — please refresh.",
    "config.editor.production_admin.reason_label": "Reason for change",
    "config.editor.production_admin.reason_required": "A reason is required.",
    "config.editor.promotion_review.title": "Promotion request",
    "config.editor.promotion_review.approve": "Approve",
    "config.editor.promotion_review.reject": "Reject",
    // FR-COL-1 — record communication threads chrome.
    "comments.tab_label": "Comments",
    "comments.composer.placeholder": "Write a comment...",
    "comments.composer.submit": "Submit",
    "comments.delete.confirm": "Delete comment?",
    "comments.deleted_marker": "(deleted)",
    "comments.empty": "No comments",
    "activity.tab_label": "Activity",
    "activity.empty": "No events",
    // FR-UI-DIR-1 — People and Organization Directory Workspace chrome (en).
    "directory.nav.section": "Directory",
    "directory.nav.people": "People",
    "directory.nav.organization": "Organization",
    "directory.nav.positions": "Positions",
    "directory.nav.employment": "Employment",
    "directory.nav.locations": "Locations",
    "directory.nav.teams": "Teams",
    "directory.nav.counterparties": "Counterparties",
    "directory.nav.qualifications": "Qualifications",
    "directory.nav.audit": "Directory audit",
    "directory.nav.admin": "Directory admin",
    "directory.people.title": "People directory",
    "directory.people.subtitle": "Tenant employees and contractors.",
    "directory.people.search.placeholder":
      "Search by name, email, employee ID…",
    "directory.people.filter.status": "Status",
    "directory.people.filter.unit": "Unit",
    "directory.people.filter.location": "Location",
    "directory.people.filter.clear": "Clear filters",
    "directory.people.empty": "No matches for the current filters.",
    "directory.people.row.status": "Status",
    "directory.people.row.unit": "Unit",
    "directory.people.row.title": "Title",
    "directory.people.row.location": "Location",
    "directory.people.row.email": "Email",
    "directory.people.row.tabs.summary": "Summary",
    "directory.person.summary.title": "Person summary",
    "directory.person.summary.tabs.overview": "Overview",
    "directory.person.summary.tabs.contacts": "Contacts",
    "directory.person.summary.tabs.employment": "Employment",
    "directory.person.summary.tabs.qualifications": "Qualifications",
    "directory.person.summary.tabs.audit": "Audit",
    "directory.person.summary.email": "Email",
    "directory.person.summary.phone": "Phone",
    "directory.person.summary.manager": "Manager",
    "directory.person.summary.unavailable": "Hidden by privacy settings.",
    "directory.person.summary.status.active": "Active",
    "directory.person.summary.status.inactive": "Inactive",
    "directory.person.summary.status.on_leave": "On leave",
    "directory.org.title": "Organization structure",
    "directory.org.subtitle": "Units and their hierarchy.",
    "directory.org.empty": "No units configured.",
    "directory.org.members_label": "Members",
    "directory.positions.title": "Positions",
    "directory.positions.subtitle": "Tenant position catalogue.",
    "directory.positions.empty": "No positions defined.",
    "directory.locations.title": "Locations",
    "directory.locations.subtitle": "Tenant addresses and sites.",
    "directory.locations.empty": "No locations defined.",
    "directory.locations.form.error": "Error: {{message}}",
    "directory.locations.form.server_conflict":
      "{{field}} changed on the server while you were editing. Your text was kept — the server now holds: {{value}}",
    "directory.people.form.error": "Error: {{message}}",
    "directory.people.form.server_conflict":
      "{{field}} changed on the server while you were editing. Your text was kept — the server now holds: {{value}}",
    // ISS-517 — Organization unit CRUD admin surface (en).
    "directory.organization_units.new.title": "New organization unit",
    "directory.organization_units.new.submit": "Create unit",
    "directory.organization_units.new.submitting": "Creating…",
    "directory.organization_units.new.picker_error":
      "Could not load the reference data. Reload the page and try again.",
    "directory.organization_units.new.invalid_input":
      "The server rejected these values. Check the name and code and try again.",
    "directory.organization_units.edit.title": "Edit organization unit",
    "directory.organization_units.edit.submit": "Save",
    "directory.organization_units.edit.submitting": "Saving…",
    "directory.organization_units.edit.loading": "Loading organization unit…",
    "directory.organization_units.edit.not_found":
      "Organization unit not found",
    "directory.organization_units.edit.invalid_input":
      "The server rejected these values. Check the name and code and try again.",
    "directory.organization_units.edit.error": "Error saving changes",
    "directory.organization_units.detail.title": "Organization unit",
    "directory.organization_units.detail.loading": "Loading organization unit…",
    "directory.organization_units.detail.not_found":
      "Organization unit not found",
    "directory.organization_units.detail.name": "Name",
    "directory.organization_units.detail.code": "Code",
    "directory.organization_units.detail.legal_entity": "Legal entity",
    "directory.organization_units.detail.parent": "Parent organization unit",
    "directory.organization_units.detail.status": "Status",
    "directory.organization_units.detail.closed_at": "Closed at",
    "directory.organization_units.detail.row_version": "Row version",
    "directory.organization_units.detail.created_at": "Created at",
    "directory.organization_units.detail.created_by": "Created by",
    "directory.organization_units.detail.edit": "Edit",
    "directory.organization_units.detail.back": "Back",
    "directory.organization_units.detail.archived":
      "Organization unit archived.",
    "directory.organization_units.detail.archive": "Archive",
    "directory.organization_units.detail.archiving": "Archiving…",
    "directory.organization_units.detail.archive_confirm_title":
      "Archive organization unit",
    "directory.organization_units.detail.archive_confirm_body":
      "Archiving closes this unit. Existing child units are not closed.",
    "directory.organization_units.detail.archive_confirm": "Archive",
    "directory.organization_units.detail.archive_cancel": "Cancel",
    "directory.organization_units.detail.error":
      "Error loading organization unit",
    "directory.organization_units.form.name": "Name",
    "directory.organization_units.form.code": "Code",
    "directory.organization_units.form.legal_entity": "Legal entity",
    "directory.organization_units.form.parent": "Parent",
    "directory.organization_units.form.no_legal_entity":
      "(select a legal entity)",
    "directory.organization_units.form.no_parent": "(no parent — root)",
    "directory.organization_units.form.cancel": "Cancel",
    "directory.organization_units.form.error": "Error: {{message}}",
    "directory.organization_units.form.server_conflict":
      "{{field}} changed on the server while you were editing. Your text was kept — the server now holds: {{value}}",
    "directory.organization_units.action.create": "Create unit",
    "directory.organization_units.action.edit": "Edit",
    "directory.organization_units.action.move": "Move",
    "directory.organization_units.action.moving": "Moving…",
    "directory.organization_units.action.archive": "Archive",
    "directory.organization_units.action.archiving": "Archiving…",
    "directory.organization_units.action.add_child": "Add child",
    "directory.organization_units.move.title": "Move unit",
    "directory.organization_units.move.submit": "Move",
    "directory.organization_units.move.moving": "Moving…",
    "directory.organization_units.move.moved": "Organization unit moved.",
    "directory.organization_units.move.parent_label": "New parent",
    "directory.organization_units.move.parent_cycle":
      "Reparent would introduce a cycle.",
    "directory.organization_units.move.cancel": "Cancel",
    "directory.organization_units.status.active": "Active",
    "directory.organization_units.status.closed": "Closed",
    "directory.employment.title": "Employment history",
    "directory.employment.empty": "No assignment records.",
    "directory.employment.current": "Current assignment",
    "directory.teams.title": "Teams",
    "directory.teams.subtitle": "Cross-functional and project groups.",
    "directory.teams.deferred.title": "Teams will appear later",
    "directory.teams.deferred.body":
      "The Teams section is deferred until the corresponding API ships. We do not substitute synthetic data.",
    "directory.qualifications.title": "Qualifications",
    "directory.qualifications.subtitle":
      "Certificates, permits and licenses held by this person.",
    "directory.qualifications.deferred.title":
      "Qualifications will appear later",
    "directory.qualifications.deferred.body":
      "The Qualifications section is deferred until the corresponding API ships. We do not substitute synthetic data.",
    "directory.audit.title": "Directory audit",
    "directory.audit.subtitle": "History of changes to directory records.",
    "directory.audit.empty": "No audit events.",
    "directory.audit.column.when": "When",
    "directory.audit.column.actor": "Actor",
    "directory.audit.column.action": "Action",
    "directory.audit.column.target": "Target",
    "directory.admin.title": "Directory admin",
    "directory.admin.subtitle": "Tenant directory admin actions.",
    "directory.admin.denied":
      "You do not hold the directory admin role. Contact your tenant operator.",
    "directory.conflict.title": "Record was changed",
    "directory.conflict.body":
      "The server rejected your save: another transaction modified this record. Reload to see the latest data and try again.",
    "directory.conflict.action.reload": "Reload page",
    "directory.conflict.action.discard": "Discard changes",
    "directory.conflict.action.retry": "Retry",
    "directory.fetch.failure.title": "Failed to load directory data",
    "directory.fetch.failure.body":
      "The server did not respond. Check your connection and try again.",
    "directory.fetch.failure.retry": "Retry",
    // FR-PM-1 — workmanagement.
    "workmanagement.title": "Projects",
    "workmanagement.code": "Code",
    "workmanagement.name": "Name",
    "workmanagement.responsible_department": "Department",
    "workmanagement.project_administrator": "Project administrator",
    "workmanagement.planned_start_date": "Planned start date",
    "workmanagement.planned_end_date": "Planned end date",
    "workmanagement.description": "Description",
    "workmanagement.status": "Status",
    "workmanagement.status.draft": "Draft",
    "workmanagement.status.active": "Active",
    "workmanagement.status.completed": "Completed",
    "workmanagement.status.archived": "Archived",
    "workmanagement.status.pending_approval": "Pending approval",
    "workmanagement.status.closed": "Closed",
    "workmanagement.new_button": "Create project",
    "workmanagement.save": "Save",
    "workmanagement.cancel": "Cancel",
    "workmanagement.registry.heading": "Project registry",
    "workmanagement.form.title": "New project",
    "workmanagement.registry.empty": "No projects",
    "workmanagement.registry.filter.status_label": "Status",
    "workmanagement.registry.filter.all": "All statuses",
    "workmanagement.registry.error.load_failed":
      "Failed to load project registry",
    "workmanagement.registry.error.create_failed": "Failed to create project",
    "workmanagement.error.dates_invalid": "End date must be after start date",
    "workmanagement.error.name_required": "Enter a project name",
    "workmanagement.error.name_too_long":
      "Project name must not exceed 200 characters",
    "workmanagement.error.description_too_long":
      "Project description must not exceed 1000 characters",
    "workmanagement.detail.code": "Project code",
    "workmanagement.detail.responsible_department": "Department",
    "workmanagement.detail.project_administrator": "Project administrator",
    "workmanagement.detail.planned_start_date": "Planned start date",
    "workmanagement.detail.planned_end_date": "Planned end date",
    "workmanagement.detail.description": "Description",
    "workmanagement.detail.created_at": "Created at",
    "workmanagement.detail.created_by": "Author",
    "workmanagement.detail.status": "Status",
    "workmanagement.detail.row_version": "Row version",
    "workmanagement.approval.title": "Project approval",
    "workmanagement.approval.stage_label": "Approval stage",
    "workmanagement.approval.panel.title": "Stage decision",
    "workmanagement.approval.decision.label": "Decision",
    "workmanagement.approval.decision.approved": "Approve",
    "workmanagement.approval.decision.rejected": "Reject",
    "workmanagement.approval.comment.label": "Comment",
    "workmanagement.approval.comment.placeholder":
      "Provide a reason or comment…",
    "workmanagement.approval.submit": "Record decision",
    "workmanagement.approval.submit_for_approval": "Submit for approval",
    "workmanagement.approval.preflight.title": "Preflight check",
    "workmanagement.approval.preflight.submittable":
      "Ready to submit for approval",
    "workmanagement.approval.preflight.not_submittable": "Not ready to submit",
    "workmanagement.approval.history.title": "Approval history",
    "workmanagement.approval.history.empty": "No decisions yet",
    "workmanagement.approval.history.stage": "Stage {{stage}}",
    "workmanagement.approval.error.decision_required": "Choose a decision",
    "workmanagement.approval.error.comment_required":
      "A comment is required when rejecting",
    "workmanagement.approval.error.not_found": "Project not found",
  },
  uk: {
    "common.save": "Зберегти",
    "common.cancel": "Отмена",
    "common.loading": "Завантаження…",
    "common.error": "Помилка",
    "common.retry": "Повторити",
    "common.close": "Закрити",
    "common.back": "Назад",
    "common.confirm": "Підтвердити",
    "common.delete": "Видалити",
    "common.edit": "Змінити",
    "common.create": "Створити",
    "common.search": "Пошук",
    "common.copy": "Копіювати",
    "common.yes": "Так",
    "common.no": "Ні",
    "common.required": "Обязательное поле",
    "common.optional": "Необязательное поле",
    "validation.error.inline": "Помилка валідації",
    "validation.error.code_default": "Поле містить помилку",
    // FR-UI-VIEW-1 AC-4 (rv-2): list-level 422 chrome.
    "validation.error.list": "Перевірку не пройдено",
    "conflict.title": "Конфлікт змін",
    "conflict.reapply": "Застосувати мої зміни до поточної версії",
    "conflict.refetch": "Отримати останню версію",
    "conflict.discard": "Скасувати мої зміни",
    "conflict.serverVersion": "Поточна версія (сервер)",
    "conflict.yourVersion": "Ваши изменения",
    "conflict.currentRowVersion": "Версія рядка",
    "deprovision.confirm": "Підтвердити деактивацію",
    "reference.placeholder": "Пошук…",
    "reference.empty": "Нічого не знайдено",
    "reference.loading": "Завантаження…",
    "reference.error": "Не вдалося завантажити варіанти",
    "reference.cancel": "Отмена",
    "reference.select": "Выбрать",
    "promote.gate.section.diff": "Изменения",
    "promote.gate.section.assertions": "Пройденные проверки",
    "promote.gate.section.artifact": "Пакет NEEDS_REVIEW",
    "promote.gate.diff.empty": "Ні изменений для проверки.",
    "promote.gate.assertions.recorded": "записано",
    "promote.gate.artifact.envelope_meta": "Метаданные конверта",
    "promote.gate.artifact.task_spec_id": "task_spec_id",
    "promote.gate.artifact.task_spec_version": "task_spec_version",
    "promote.gate.artifact.kind": "kind",
    "promote.gate.artifact.agent_principal": "agent_principal",
    "promote.gate.artifact.submitted_at": "submitted_at",
    "promote.gate.artifact.attempt_count": "attempts",
    "promote.gate.artifact.spec_hash": "spec_hash",
    "promote.gate.artifact.final_status": "final_status",
    "promote.gate.artifact.spec": "spec",
    "promote.gate.artifact.attempts": "Попытки",
    "promote.gate.artifact.started_at": "started_at",
    "promote.gate.artifact.completed_at": "completed_at",
    "promote.gate.artifact.terminal_failure_reason": "terminal_failure_reason",
    "promote.gate.artifact.bpm_step_log_snapshot": "bpm_step_log_snapshot",
    "promote.gate.buttons.approve": "Утвердить промоушен",
    "promote.gate.buttons.approving": "Утверждение…",
    "promote.gate.buttons.reject": "Отклонить",
    "promote.gate.approve.error": "Не вдалося затвердити промоушен.",
    "promote.gate.reject.reason_label": "Причина отклонения",
    "promote.gate.reject.reason_placeholder": "Опишіть, що потрібно виправити…",
    "promote.gate.reject.confirm": "Підтвердити відхилення",
    "promote.gate.reject.pending": "Отклонение…",
    "promote.gate.reject.cancel": "Отмена",
    "promote.gate.reject.error": "Не вдалося відхилити промоушен.",
    "promote.gate.error.load_failed": "Не вдалося завантажити рецензію:",
    "admin.matrix.title": "Матриця разрешений",
    "admin.matrix.column.role": "Роль",
    "admin.matrix.column.create": "Створення",
    "admin.matrix.column.read": "Читання",
    "admin.matrix.column.update": "Зміна",
    "admin.matrix.column.delete": "Видалення",
    "admin.matrix.column.all": "Все",
    "admin.matrix.action.save": "Зберегти роль",
    "admin.matrix.action.cancel": "Скасувати",
    "admin.matrix.action.new_role": "Створити роль",
    "admin.matrix.action.delete_role": "Видалити роль",
    "admin.matrix.system_role.readonly": "Системна роль — лише для читання",
    "admin.matrix.banner.open_review": "Відкрити перевірку",
    "admin.matrix.loading": "Завантаження матрицы...",
    "admin.errors.staging_only.code": "ENV_PRODUCTION",
    "admin.errors.version_conflict.title": "Конфликт версий",
    "admin.errors.version_conflict.refetch": "Отримати останню версію",
    "admin.errors.version_conflict.merge": "Об'єднати мої зміни",
    "admin.errors.version_conflict.discard": "Скасувати мої зміни",
    "admin.errors.fetch_failure": "Не вдалося завантажити матрицю дозволів",
    "admin.errors.rate_limit": "Забагато запитів. Зачекайте трохи.",
    "admin.errors.network": "Сетевая помилка. Проверьте подключение.",
    "admin.field_visibility.column.field": "Поле",
    "admin.field_visibility.column.classification": "Классификация",
    "admin.field_visibility.states.visible": "видимое",
    "admin.field_visibility.states.hidden_cosmetic": "скрыто (косметика)",
    "admin.field_visibility.states.denied_security": "запрещено (безопасность)",
    "admin.field_visibility.aria.visible": "видимое",
    "admin.field_visibility.empty": "Ні полей для отображения.",
    "admin.webhooks.title": "Вебхук-эндпоинты",
    "admin.webhooks.column.url": "URL",
    "admin.webhooks.column.process_def": "Определение процесса",
    "admin.webhooks.column.signing_key_id": "Идентификатор ключа подписи",
    "admin.webhooks.column.status": "Статус",
    "admin.webhooks.column.version": "Версія",
    "admin.webhooks.column.actions": "Действия",
    "admin.webhooks.status.active": "активный",
    "admin.webhooks.status.disabled": "отключён",
    "admin.webhooks.action.add": "Добавить эндпоинт",
    "admin.webhooks.action.disable": "Отключить",
    "admin.webhooks.action.delete": "Видалити",
    "admin.webhooks.dialog.create.title": "Створити вебхук-эндпоинт",
    "admin.webhooks.dialog.create.label.url": "URL",
    "admin.webhooks.dialog.create.cta": "Створити",
    "admin.webhooks.dialog.create.success.title": "Вебхук создан",
    "admin.webhooks.dialog.create.success.copied": "Скопировано!",
    "admin.webhooks.dialog.conflict.discard": "Скасувати мої зміни",
    "admin.webhooks.banner.open_review": "Відкрити перевірку",
    "admin.webhooks.table.aria_label": "Таблица вебхук-эндпоинтов",
    "admin.webhooks.keys.title": "Webhook HMAC ключи",
    "admin.webhooks.keys.active": "Активний",
    "admin.webhooks.keys.grace_period": "Льготный период",
    "admin.webhooks.keys.expired": "Истёк",
    "admin.webhooks.keys.rotate_action": "Сменить ключ",
    "admin.webhooks.keys.issue_action": "Выпустить ключ",
    "admin.webhooks.keys.revoke_action": "Отозвать",
    "admin.webhooks.keys.grace_window_label": "Льготный период",
    "admin.webhooks.keys.grace_until_label": "Истекает",
    "admin.webhooks.keys.expired_at_label": "Истёк",
    "admin.webhooks.keys.key_id_label": "ID ключа",
    "admin.webhooks.keys.created_at_label": "Создан",
    "admin.webhooks.keys.state_label": "Состояние",
    "admin.webhooks.keys.copy_button": "Копіювати",
    "admin.webhooks.keys.done_button": "Готово",
    "admin.webhooks.keys.confirm_rotate_button": "Підтвердити смену",
    "admin.webhooks.keys.cancel_button": "Отмена",
    "admin.webhooks.keys.revoke_confirm_title": "Отозвать ключ?",
    "admin.webhooks.keys.revoke_confirm_placeholder": "Введите ID ключа",
    "admin.webhooks.keys.loading_error": "Не удалось завантажити HMAC ключи.",
    "admin.webhooks.keys.retry": "Повторити",
    "admin.webhooks.keys.countdown.separator": "и",
    "admin.webhooks.keys.countdown.expired": "Истёк",
    "admin.webhooks.keys.grace_window_minutes_min": "минимум {{minutes}} минут",
    "tier.title": "Уровень и квоты",
    "tier.tier.small": "Малый",
    "tier.tier.middle": "Средний",
    "tier.tier.corporate": "Корпоративный",
    "tier.limits.script_wall_clock_ms": "Стенд-байм-аут скрипта (мс)",
    "tier.limits.script_memory_pages": "Страницы памяти скрипта",
    "tier.limits.storage_mb": "Квота хранилища (МБ)",
    "tier.limits.file_storage_mb": "Квота файлового хранилища (МБ)",
    "tier.limits.concurrent_sandboxes": "Параллельные песочницы",
    "tier.limits.agent_retry_budget": "Бюджет повторов агента",
    "tier.limits.max_single_upload_mb": "Макс. размер загрузки (МБ)",
    "tier.limits.outbox_depth_cap": "Ограничение outbox",
    "tier.usage.storage_bytes": "Использование хранилища",
    "tier.usage.file_storage_bytes": "Использование файлов",
    "tier.usage.concurrent_sandboxes": "Активные песочницы",
    "tier.unavailable": "недоступно",
    "tier.state.loading": "Завантаження уровня и квот…",
    "tier.state.fetch_failure": "Не удалось завантажити уровень и квоты.",
    "tier.state.permission_denied": "У вас нет прав на просмотр уровня и квот.",
    "audit.title": "Журнал изменений определений",
    "audit.filter.definition_type": "Тип определения",
    "audit.filter.all_types": "Все типы",
    "audit.filter.author": "Автор",
    "audit.filter.after": "После",
    "audit.filter.before": "До",
    "audit.filter.clear": "Очистить фильтры",
    "audit.column.definition_type": "Тип",
    "audit.column.definition_id": "Определение",
    "audit.column.action": "Действие",
    "audit.column.author": "Автор",
    "audit.column.agent": "Агент",
    "audit.column.agent_label": "Запис агента",
    "audit.column.task_spec": "Спецификация задачи",
    "audit.column.orchestrator": "Оркестратор",
    "audit.column.version": "Версія",
    "audit.column.created_at": "Когда",
    "audit.column.definition_type.form": "Форма",
    "audit.column.definition_type.process": "Процесс",
    "audit.column.definition_type.entity": "Сущность",
    "audit.column.definition_type.permission": "Разрешение",
    "audit.column.definition_type.task_spec": "Спецификация",
    "audit.empty": "Ні записей по текущим фильтрам.",
    "audit.pagination.label": "Пагинация журнала",
    "audit.pagination.previous": "Назад",
    "audit.pagination.next": "Вперёд",
    "audit.state.loading": "Завантаження журнала…",
    "audit.state.fetch_failure": "Не удалось завантажити журнал.",
    "audit.state.permission_denied": "У вас нет прав на просмотр журнала.",
    "audit.state.validation_error": "Некорректный фильтр — проверьте ввод.",
    "admin.effects.http.page.title": "HTTP-эффекты",
    "admin.effects.http.empty": "HTTP-эндпоинты не настроены.",
    "admin.effects.http.table_aria_label": "Таблица HTTP-эффектов",
    "admin.effects.http.column.url": "URL",
    "admin.effects.http.column.method": "Метод",
    "admin.effects.http.column.status": "Статус",
    "admin.effects.http.column.actions": "Действия",
    "admin.effects.http.action.create": "Добавить эндпоинт",
    "admin.effects.http.action.disable": "Отключить",
    "admin.effects.http.action.delete": "Видалити",
    "admin.effects.http.field.url": "URL",
    "admin.effects.http.field.method": "HTTP-метод",
    "admin.effects.http.field.timeout": "Тайм-аут (секунды)",
    "admin.effects.http.dialog.create.title": "Добавить HTTP-эндпоинт",
    "admin.effects.http.dialog.create.success.title": "Эндпоинт создан",
    "admin.effects.email.page.title": "Email-каналы",
    "admin.effects.email.empty": "Email-каналы не настроены.",
    "admin.effects.email.table_aria_label": "Таблица Email-каналов",
    "admin.effects.email.column.from": "Адрес отправителя",
    "admin.effects.email.column.subject": "Тема",
    "admin.effects.email.column.locales": "Языки",
    "admin.effects.email.column.status": "Статус",
    "admin.effects.email.column.actions": "Действия",
    "admin.effects.email.action.create": "Добавить канал",
    "admin.effects.email.action.disable": "Отключить",
    "admin.effects.email.action.delete": "Видалити",
    "admin.effects.email.field.from_address": "Адрес отправителя",
    "admin.effects.email.field.subject": "Тема",
    "admin.effects.email.field.provider": "Провайдер",
    "admin.effects.email.field.body": "Текст письма (с поддержкой локали)",
    "admin.effects.email.dialog.create.title": "Добавить Email-канал",
    "admin.effects.email.dialog.create.success.title": "Канал создан",
    "admin.effects.status.active": "активный",
    "admin.effects.status.disabled": "отключён",
    "admin.effects.dialog.conflict.title": "Конфликт версий",
    "admin.effects.dialog.conflict.server_version": "Версія на сервере",
    "admin.effects.dialog.conflict.discard": "Скасувати мої зміни",
    "admin.effects.dialog.conflict.refetch": "Обновить данные",
    "admin.secrets.title": "Секреты",
    "admin.secrets.create_button": "Створити секрет",
    "admin.secrets.empty_state": "Секреты ещё не созданы.",
    "admin.secrets.table.name": "Название",
    "admin.secrets.table.ref_id": "ID ссылки",
    "admin.secrets.table.status": "Статус",
    "admin.secrets.table.created_at": "Создан",
    "admin.secrets.table.updated_at": "Обновлён",
    "admin.secrets.table.version": "Версія",
    "admin.secrets.table.actions": "Действия",
    "admin.secrets.dialog.create.title": "Створити секрет",
    "admin.secrets.dialog.create.name_label": "Название секрета",
    "admin.secrets.dialog.create.success.title": "Секрет создан",
    "admin.secrets.dialog.create.success.copied": "Скопировано!",
    "admin.secrets.dialog.impact.title": "Видалення секрета",
    "admin.secrets.dialog.impact.kind_http_endpoint": "HTTP-эндпоинт",
    "admin.secrets.dialog.impact.kind_email_channel": "Email-канал",
    "admin.secrets.banner.open_review": "Відкрити перевірку",
    "admin.secrets.status.active": "активный",
    "admin.secrets.status.disabled": "отключён",
    "admin.templates.registry.title": "Реестр шаблонов",
    "admin.templates.registry.column.template_id": "ID шаблона",
    "admin.templates.registry.column.version": "Версія",
    "admin.templates.registry.column.description": "Описание",
    "admin.templates.registry.column.publisher_kind": "Издатель",
    "admin.templates.registry.column.pre_vetted": "Pre-vetted",
    "admin.templates.registry.column.published_at": "Опубліковано",
    "admin.templates.registry.column.published_by": "Кем",
    "admin.templates.registry.column.content_hash": "Content hash",
    "admin.templates.registry.column.actions": "Действия",
    "admin.templates.registry.action.install": "Установить",
    "admin.templates.registry.action.installing": "Установка…",
    "admin.templates.registry.table.aria_label": "Таблица реестра шаблонов",
    // FR-UI-ADM-13 — template export/import admin chrome (Ukrainian).
    "admin.templates.export_import.action.export": "Экспорт",
    "admin.templates.export_import.action.exporting": "Экспорт…",
    "admin.templates.export_import.action.export_aria":
      "Экспортировать шаблон {{template_id}} версии {{version}}",
    "admin.templates.export_import.action.import": "Импорт",
    "admin.templates.export_import.notice.operator_console_required":
      "Для импорта шаблонов використовуйте операторську консоль.",
    "admin.templates.export_import.dialog.import.title": "Импорт шаблона",
    "admin.templates.export_import.dialog.import.file_label":
      "Файл пакета шаблона",
    "admin.templates.export_import.dialog.import.submit": "Надіслати",
    "admin.templates.export_import.dialog.import.submit_disabled_tooltip":
      "Для відправки використовуйте операторську консоль",
    "admin.templates.export_import.dialog.replace.title":
      "Замінити наявний шаблон?",
    "admin.templates.export_import.dialog.replace.existing_label": "Наявний:",
    "admin.templates.export_import.dialog.replace.imported_label":
      "Імпортований:",
    "admin.templates.export_import.dialog.replace.diff":
      "наявний v{{existing_version}} → імпорт v{{import_version}}",
    "admin.templates.export_import.dialog.replace.confirm": "Замінити",
    "admin.templates.export_import.dialog.replace.cancel": "Скасувати",
    "admin.templates.export_import.dialog.replace.server_rules_notice":
      "Серверні правила незмінності версій застосовуються під час відправлення — це підтвердження їх не скасовує.",
    "admin.templates.export_import.errors.manifest_missing":
      "manifest.json не знайдено в архіві.",
    "admin.templates.export_import.errors.manifest_invalid_json":
      "manifest.json містить невалідний JSON.",
    "admin.templates.export_import.errors.cross_publisher_kind":
      "Тільки шаблони, опубліковані платформою, можуть імпортуватися операторами.",
    "admin.templates.export_import.errors.manifest_incomplete":
      "Маніфест містить не всі обов'язкові поля.",
    "admin.templates.export_import.errors.file_pick_failed":
      "Файл не вдалося прочитати.",
    "admin.templates.export_import.errors.payload_too_large":
      "Пакет занадто великий.",
    "admin.templates.export_import.errors.validation_failed":
      "Перевірка пакета не пройдена.",
    "admin.templates.export_import.tooltip.replaces_preserves_customizations":
      "FR-TPL-4 зберігає кастомізації тенанта під час встановлення — це не зачіпається повторним імпортом версії шаблона.",
    "admin.templates.export_import.source.imported": "імпортований",
    "admin.templates.export_import.source.native": "рідний",
    "admin.templates.update.badge.singular": "{{count}} обновление доступно",
    "admin.templates.update.badge.plural": "{{count}} обновлений доступно",
    "admin.templates.update.badge.view": "Відкрити обновления",
    "admin.templates.update.title": "Обновления шаблонов",
    "admin.templates.update.list.empty": "Доступных обновлений нет.",
    "admin.templates.update.list.column.template_id": "ID шаблона",
    "admin.templates.update.list.column.installed": "Установлено",
    "admin.templates.update.list.column.target": "Целевая",
    "admin.templates.update.list.column.conflicts": "Конфликты",
    "admin.templates.update.list.column.irreversible": "Необратимо",
    "admin.templates.update.list.row.view": "Просмотр",
    "admin.templates.update.detail.title": "Обновление {{template_id}}",
    "admin.templates.update.detail.target_version": "Целевая: {{version}}",
    "admin.templates.update.classification.unchanged": "Не изменялось тенантом",
    "admin.templates.update.classification.modified": "Изменено тенантом",
    "admin.templates.update.classification.added": "Добавлено тенантом",
    "admin.templates.update.resolution.keep_tenant": "Оставить тенант",
    "admin.templates.update.resolution.accept_update": "Принять обновление",
    "admin.templates.update.resolution.merge": "Объединить",
    "admin.templates.update.resolution.close": "Закрити",
    "admin.templates.update.resolution.preview": "Просмотр объединения",
    "admin.templates.update.apply": "Применить обновление",
    "admin.templates.update.apply.success": "Обновление применено",
    "admin.templates.update.errors.unresolved": "Не все конфликты разрешены",
    "formula.editor.label": "Формула",
    "formula.editor.validate": "Проверить",
    "formula.editor.type": "Тип: {{type}}",
    "formula.editor.placeholder": "Введите формулу…",
    "lists.title": "Записи",
    "lists.table.aria_label": "Таблица записей сущности",
    "lists.empty.no_records": "Ні записей для отображения.",
    "lists.empty.permission_denied": "У вас нет доступа к этим записям.",
    "lists.filter.group_label": "Фильтры",
    "lists.filter.placeholder": "Фильтр…",
    "lists.filter.aria_input": "Фильтр по полю {{field}}",
    "lists.sort.asc": "▲",
    "lists.sort.desc": "▼",
    "lists.sort.toggle_aria": "Змінити сортировку для {{field}}",
    "lists.pagination.next": "Вперёд",
    "lists.pagination.aria_label": "Постраничная навигация",
    "lists.pagination.page_size": "Размер страницы: {{pageSize}}",
    "lists.boolean.yes": "Так",
    "lists.boolean.no": "Ні",
    "lists.error.fetch_failure": "Не удалось завантажити записи.",
    // FR-UI-VIEW-2 — Tree view chrome (Ukrainian).
    "lists.tree.title": "Дерево записів",
    "lists.tree.error.empty": "Для цієї сутності немає записів.",
    "lists.tree.error.fetch_failure": "Не вдалося завантажити дерево записів.",
    "lists.tree.error.permission_denied": "У вас немає доступу до цих записів.",
    "lists.tree.error.stale_version":
      "Структуру цієї сутності змінено. Оновіть сторінку.",
    "lists.tree.error.rate_limit":
      "Забагато запитів. Повторіть спробу пізніше.",
    "lists.tree.error.validation_error":
      "Конфігурацію дерева для цієї сутності відхилено сервером.",
    "lists.tree.count_banner": "{{shown}} з {{total}} записів показано.",
    // FR-UI-VIEW-3 — Gantt view chrome (Ukrainian). See Russian
    // block for the key rationale.
    "lists.gantt.title": "Діаграма Ганта",
    "lists.gantt.noStartField": "немає дати початку",
    "lists.gantt.zeroDuration": "дата початку пізніше дати завершення",
    "lists.gantt.capBanner":
      "Показано {{count}} записів із {{total}}. Інші записи не завантажено.",
    "lists.gantt.loadMore": "Завантажити ще",
    "lists.gantt.invalidConfig":
      "Конфігурацію діаграми Ганта відсутня або некоректна.",
    "lists.gantt.dependsOnLabel": "Залежності",
    "lists.gantt.conflictOnMove":
      "Запис було змінено іншим користувачем. Оновіть сторінку.",
    "processes.failed.title": "Неуспешные экземпляры процессов",
    "failed_instance.empty": "Ні неуспешных экземпляров.",
    "failed_instance.failed_node_label": "Узел сбоя",
    "failed_instance.reason_label": "Причина",
    "failed_instance.retries_label": "Попыток повтора",
    "failed_instance.trace_label": "Трассировка сбоя",
    "failed_instance.trace_summary": "Записей в логе: {{count}}",
    "failed_instance.view_trace": "Відкрити трассировку",
    "failed_instance.retry_button": "Повторити шаг",
    "failed_instance.retry_pending": "Повтор шага…",
    "failed_instance.retry_error": "Не удалось повторити шаг.",
    "failed_instance.cancel_button": "Скасувати экземпляр",
    "failed_instance.cancel_pending": "Отмена…",
    "failed_instance.cancel_error": "Не удалось скасувати экземпляр.",
    "failed_instance.cancel_confirm_title": "Скасувати экземпляр?",
    "failed_instance.restored_orphan_badge": "Восстановленный orphan",
    "trace.title": "Трассировка выполнения",
    "trace.table_aria_label": "Лог шагов трассировки выполнения",
    "trace.column.node_id": "Узел",
    "trace.column.status": "Статус",
    "trace.column.retry_count": "Повторы",
    "trace.column.started_at": "Начало",
    "trace.column.duration_ms": "Длительность",
    "trace.column.error_message": "Помилка",
    "trace.empty": "Ні записей в логе шагов.",
    "trace.state.fetch_failure": "Не удалось завантажити лог шагов.",
    "operator.title": "Консоль оператора",
    "operator.summary.title": "Сводка по тенантам",
    "operator.summary.tenantCount": "Тенанты",
    "operator.summary.activeInstances": "Активные экземпляры",
    "operator.summary.errorRate": "Доля ошибок",
    "operator.summary.empty": "Тенантов пока нет",
    "operator.summary.computedAt": "Обновлено",
    "operator.summary.errorUnit": "ош",
    "operator.tokenPrompt.label": "Токен оператора",
    "operator.tokenPrompt.button": "Войти",
    "operator.tokenPrompt.placeholder": "Вставьте токен оператора",
    "apex.title": "ASCOA Platform",
    "apex.how_to_enter_title": "Войти в консоль оператора",
    "apex.open_operator": "Відкрити консоль оператора",
    "apex.tour_title": "URL тенантов",
    "apex.tour_local": "дефолтный dev-тенант",
    "apex.tour_tenant": "поддомен тенанта (после провизии)",
    "operator.error.fetchFailure": "Не удалось завантажити сводку.",
    "operator.error.retry": "Повторити",
    "operator.summary.table.tenant": "Тенант",
    "operator.summary.table.instanceCount": "Активные экземпляры",
    "operator.summary.table.errorCount": "Помилки",
    "operator.summary.table.activeSandboxCount": "Активные песочницы",
    "operator.summary.table.aria_label": "Разбивка по тенантам",
    "operator.nav.label": "Разделы оператора",
    "operator.nav.summary": "Сводка",
    "operator.nav.tenants": "Тенанты",
    "operator.nav.effects": "Эффекты / Операции",
    "operator.nav.audit": "Аудит",
    "operator.drillDown.title": "Детали тенанта",
    "operator.drillDown.instanceCount": "Активные экземпляры",
    "operator.drillDown.errorCount": "Помилки",
    "operator.drillDown.activeSandboxCount": "Активные песочницы",
    "operator.drillDown.openAdmin": "Відкрити /admin тенанта",
    "operator.drillDown.openFor": "Відкрити детали тенанта {{slug}}",
    "operator.drillDown.close": "Закрити",
    "operator.audit.title": "Аудит",
    "operator.audit.placeholder": "Журнал аудита ещё не реализован.",
    "operator.tenants.loadError": "Не удалось завантажити тенантов.",
    "operator.topbar.localeLabel": "Мова інтерфейсу",
    "operator.tenants.list.title": "Тенанти",
    "operator.tenants.list.empty": "Тенантів не знайдено.",
    "operator.tenants.list.refresh": "Оновити",
    "operator.tenants.list.loading": "Завантаження тенантів…",
    "operator.tenants.list.errorPrefix": "Не удалось завантажити тенантов:",
    "operator.tenants.list.deprovision": "Деактивувати",
    "operator.tenants.list.ariaLabel": "Список тенантів з діями деактивації",
    "operator.tenants.list.col.slug": "Слаг",
    "operator.tenants.list.col.name": "Назва",
    "operator.tenants.list.col.tier": "Рівень",
    "operator.tenants.list.col.status": "Статус",
    "operator.tenants.list.col.tenantId": "ID орендаря",
    "operator.tenants.list.col.actions": "Дії",
    // ISS-509 (BA ruling): localized "Unknown" status label for the
    // decoder fallback path (status field absent from wire).
    "operator.tenants.list.status.unknown": "невідомо",
    // ISS-510: localized status + tier value labels.
    "operator.tenants.list.status.active": "Активний",
    "operator.tenants.list.status.disabled": "Вимкнений",
    "operator.tenants.list.status.quiesced": "Призупинений",
    "operator.tenants.list.tier.small": "Малий",
    "operator.tenants.list.tier.middle": "Середній",
    "operator.tenants.list.tier.corporate": "Корпоративний",
    "cabinet.title": "Рабочее пространство тенанта",
    "cabinet.nav.label": "Разделы рабочего пространства",
    "cabinet.nav.cabinet": "Мой кабинет",
    "cabinet.nav.tasks": "Мои задачи",
    "cabinet.nav.processes": "Процессы",
    "cabinet.nav.admin": "Администрирование",
    "cabinet.nav.section.records": "Записи",
    "cabinet.nav.section.admin": "Администрирование",
    "cabinet.landing.title": "Мой кабинет",
    "cabinet.landing.subtitle": "Ваша работа с первого взгляда.",
    "cabinet.landing.tiles_label": "Сводка",
    "cabinet.landing.tasks_due_soon": "Задачи скоро",
    "cabinet.landing.open_processes": "Открытые процессы",
    "cabinet.landing.failed_processes": "Неуспешные процессы",
    "cabinet.landing.recent_records": "Недавние записи",
    "cabinet.admin.title": "Администрирование",
    "cabinet.admin.subtitle": "Управление конфигурацией тенанта.",
    "cabinet.admin.grid_label": "Разделы администрирования",
    "cabinet.admin.denied": "У вас нет доступа к разделу администрирования.",
    "cabinet.admin.tile.users": "Ролі",
    "cabinet.admin.tile.permissions_matrix": "Матриця разрешений",
    "cabinet.admin.tile.record_field_permissions": "Права на записи и поля",
    "cabinet.admin.tile.webhooks": "Вебхук-эндпоинты",
    "cabinet.admin.tile.hmac_keys": "HMAC-ключи",
    "cabinet.admin.tile.http_endpoints": "HTTP-эндпоинты",
    "cabinet.admin.tile.email_channels": "Email-каналы",
    "cabinet.admin.tile.secrets": "Секреты",
    "cabinet.admin.tile.templates": "Шаблоны",
    "cabinet.admin.tile.template_updates": "Обновления шаблонов",
    "cabinet.admin.tile.tier": "Уровень и квоты",
    "cabinet.admin.tile.audit": "Журнал аудита",
    "cabinet.admin.tile.forms": "Форми",
    "cabinet.admin.tile.processes": "Процессы",
    "cabinet.errors.fetch_failure": "Не удалось завантажити.",
    "cabinet.errors.permission_denied": "У вас нет доступа к этому.",
    "cabinet.errors.rate_limit": "Забагато запитів. Подождите.",
    "cabinet.errors.stale_version": "Эта версія больше недоступна.",
    "cabinet.errors.tile_label": "Сводка",
    "cabinet.errors.retry_after": "Повтор через {{seconds}} с.",
    "admin.record_permissions.role": "Роль",
    "admin.record_permissions.cell.ownership": "Правило владения",
    "admin.record_permissions.cell.fields": "Права полей",
    "admin.record_permissions.cell.owner": "Поле владельца",
    "admin.record_permissions.editor.title": "Редактировать правила записи",
    "admin.record_permissions.editor.tab.ownership": "Владение",
    "admin.record_permissions.editor.tab.condition": "Условие",
    "admin.record_permissions.editor.expr_label": "Выражение (expr-lang)",
    "admin.record_permissions.editor.preview_button": "Предпросмотр",
    "admin.record_permissions.editor.preview.loading": "Завантаження выборки…",
    "admin.record_permissions.conflict.title": "Конфликт версії",
    "admin.record_permissions.conflict.refetch": "Отримати останню версію",
    "admin.record_permissions.conflict.merge": "Об'єднати мої зміни",
    "admin.record_permissions.conflict.discard": "Скасувати мої зміни",
    "admin.record_permissions.error.title": "Помилка загрузки",
    "admin.field_permissions.title": "Редактировать права полей",
    "admin.field_permissions.field": "Поле",
    "admin.field_permissions.state": "Состояние",
    "admin.field_permissions.state.readable": "Читання",
    "admin.field_permissions.state.writable": "Читання и запис",
    "admin.field_permissions.state.hidden": "Скрыто",
    "admin.entity_field_owner.title": "Поле владельца сущности",
    "admin.entity_field_owner.field_label": "Поле владельца",
    "admin.users.title": "Користувачі и ролі",
    "admin.users.column.display_name": "Имя",
    "admin.users.column.email": "Email",
    "admin.users.column.assigned_roles": "Назначенные ролі",
    "admin.users.column.actions": "Действия",
    "admin.users.column.select_all": "Выбрать всех",
    "admin.users.column.select_user": "Выбрать пользователя {{name}}",
    "admin.users.action.assign_roles": "Назначить ролі",
    "admin.users.action.assign_roles_for": "Назначить ролі для {{name}}",
    "admin.users.action.sync_refresh": "Обновить синхронизацию",
    "admin.users.action.sync_refresh_cooldown": "Подождите…",
    "admin.users.sync_status.ok": "Синхронизация в порядке",
    "admin.users.sync_status.degraded": "Синхронизация частичная",
    "admin.users.sync_status.failed": "Синхронизация не выполнена",
    "admin.users.dialog.role_picker.title": "Выберите ролі",
    "admin.users.dialog.role_picker.label.roles": "Ролі",
    "admin.users.dialog.role_picker.cta": "Зберегти",
    "admin.users.dialog.role_picker.error": "Не удалось зберегти.",
    "admin.users.dialog.bulk_assign.cta": "Назначить {{count}}",
    "admin.users.dialog.bulk_assign.failures_label": "Помилки ({{count}})",
    "admin.users.dialog.bulk_assign.dismiss": "Готово",
    "admin.users.bulk.selected_count": "Выбрано: {{count}}",
    "admin.users.orphan_panel.title": "Потерянные пользователи",
    "admin.users.orphan_panel.empty": "Потерянных пользователей нет.",
    "admin.users.orphan_panel.remove_action_short": "Очистить",
    "admin.users.orphan_panel.cleaned": "Очищено",
    "admin.users.notice.dismiss": "Закрити",
    "admin.users.empty.message": "Пользователей не найдено.",
    "admin.users.error.fetch_failure": "Не удалось завантажити пользователей.",
    "admin.users.error.stale_version": "Версія устарела. Обновите страницу.",
    "admin.effects.metrics.page_title": "Операционная панель эффектов",
    "admin.effects.metrics.section.metrics": "Метрики по каналам",
    "admin.effects.metrics.table_label": "Метрики каналов эффектов",
    "admin.effects.metrics.circuit_table_label": "Состояние circuit-breaker",
    "admin.effects.metrics.column.channel_name": "Канал",
    "admin.effects.metrics.column.delivery_count": "Доставки (с начала работы)",
    "admin.effects.metrics.column.success_rate": "Успех %",
    "admin.effects.metrics.column.retry_count": "Повторы",
    "admin.effects.metrics.column.dead_letter_count": "Dead-letter",
    "admin.effects.metrics.column.latency_p50": "p50 мс",
    "admin.effects.metrics.column.latency_p95": "p95 мс",
    "admin.effects.metrics.column.latency_p99": "p99 мс",
    "admin.effects.metrics.column.endpoint": "Эндпоинт",
    "admin.effects.metrics.column.state": "Состояние",
    "admin.effects.metrics.column.time_in_state": "Время в состоянии",
    "admin.effects.metrics.column.failures_5min": "Сбои за 5 мин",
    "admin.effects.metrics.column.action": "Действие",
    "admin.effects.metrics.refresh.now": "Обновить",
    "admin.effects.metrics.last_refreshed": "Последнее обновление: {{time}}",
    "admin.effects.metrics.last_refreshed_prefix": "Последнее обновление:",
    "admin.effects.metrics.banner_dismiss": "Закрити",
    "admin.effects.metrics.policy.updated_toast": "Политика обновлена",
    "admin.effects.metrics.dialog_title": "Настроить breaker",
    "admin.effects.metrics.dialog_save": "Зберегти",
    "admin.effects.metrics.dialog_saving": "Сохранение…",
    "admin.effects.metrics.dialog_cancel": "Отмена",
    "admin.effects.metrics.loading": "Завантаження метрик эффектов…",
    "admin.effects.metrics.error_504": "Сервер эффектов не отвечает (таймаут).",
    "admin.effects.metrics.error_5xx": "Сервер эффектов вернул помилку.",
    "admin.builder.page_title": "Конструктор форм",
    "admin.builder.save.cta": "Зберегти форму",
    "admin.builder.save.saving": "Сохранение...",
    "admin.builder.save.invalid": "Неверный ввод",
    "admin.builder.palette.aria": "Палитра полей",
    "admin.builder.palette.title": "Поля",
    "admin.builder.canvas.title": "Холст формы",
    "admin.builder.canvas.empty": "Перетащите поле из палитры, чтобы начать",
    "admin.builder.canvas.remove": "Видалити поле {{id}}",
    "admin.builder.settings.aria": "Налаштування",
    "admin.builder.settings.title": "Налаштування",
    "admin.builder.settings.entity_type": "Тип сущности",
    "admin.builder.settings.field_heading": "Поле {{id}}",
    "admin.builder.settings.label": "Метка",
    "admin.builder.settings.placeholder": "Подсказка",
    "admin.builder.settings.required": "Обязательное",
    "admin.builder.settings.visible_when": "Видимость (формула)",
    "admin.builder.settings.computed_from": "Вычисляется из (формула)",
    "admin.builder.settings.target_entity": "Целевая сущность",
    "admin.builder.settings.no_selection": "Выберите поле на холсте",
    "admin.builder.formula.ok": "Формула валидна",
    "admin.builder.formula.error": "Формула невалидна",
    "admin.builder.field_types.string": "Строка",
    "admin.builder.field_types.text": "Текст",
    "admin.builder.field_types.number": "Число",
    "admin.builder.field_types.integer": "Целое число",
    "admin.builder.field_types.float": "Дробное число",
    "admin.builder.field_types.boolean": "Логическое",
    "admin.builder.field_types.date": "Такта",
    "admin.builder.field_types.datetime": "Такта и время",
    "admin.builder.field_types.enum": "Перечисление",
    "admin.builder.field_types.reference": "Ссылка",
    "admin.designer.page_title": "Конструктор процессов",
    "admin.designer.save.cta": "Зберегти процесс",
    "admin.designer.save.saving": "Сохранение...",
    "admin.designer.save.invalid": "Неверный ввод",
    "admin.designer.palette.aria": "Палитра узлов",
    "admin.designer.palette.title": "Узлы",
    "admin.designer.canvas.title": "Холст процесса",
    "admin.designer.canvas.empty": "Перетащите узел из палитры, чтобы начать",
    "admin.designer.canvas.aria": "Холст BPM-процесса",
    "admin.designer.canvas.remove": "Видалити узел {{id}}",
    "admin.designer.canvas.dragging": "Перетаскивается {{id}}",
    "admin.designer.settings.aria": "Свойства",
    "admin.designer.settings.title": "Свойства",
    "admin.designer.settings.process_name": "Название процесса",
    "admin.designer.settings.process_description": "Описание",
    "admin.designer.settings.node_heading": "Узел {{id}}",
    "admin.designer.settings.node_label": "Метка",
    "admin.designer.settings.form_id": "Форма",
    "admin.designer.settings.candidate_role_id": "Кандидатская роль",
    "admin.designer.settings.assignee_user_id": "Назначенный користувач",
    "admin.designer.settings.due_in_seconds": "Срок (секунды)",
    "admin.designer.settings.script_id": "Скрипт",
    "admin.designer.settings.script_formula": "Формула (необяз.)",
    "admin.designer.settings.delay_expression": "Задержка (выражение)",
    "admin.designer.settings.edge_heading": "Переход {{id}}",
    "admin.designer.settings.edge_guard": "Условие (формула)",
    "admin.designer.settings.no_config": "У этого узла нет настроек",
    "admin.designer.guard.ok": "Условие валидно",
    "admin.designer.guard.error": "Условие невалидно",
    "admin.designer.node_types.start": "Старт",
    "admin.designer.node_types.end": "Завершение",
    "admin.designer.node_types.userTask": "Користувачская задача",
    "admin.designer.node_types.scriptTask": "Скрипт-задача",
    "admin.designer.node_types.xorGateway": "XOR-шлюз",
    "admin.designer.node_types.andGateway": "AND-шлюз",
    "admin.designer.node_types.timerCatch": "Таймер",
    "admin.forms.list.title": "Форми",
    "admin.forms.list.empty": "Форм пока нет.",
    "admin.forms.list.aria": "Список форм",
    "admin.processes.list.title": "Процессы",
    "admin.processes.list.empty": "Процессов пока нет.",
    "admin.processes.list.aria": "Список процессов",
    "observability.dashboard.title": "Наблюдаемость тенанта",
    "observability.dashboard.refresh": "Обновить",
    "observability.dashboard.author_alert": "Створити правило оповещения",
    "observability.dashboard.widget.instance_count.active": "Активные",
    "observability.dashboard.widget.instance_count.failed": "Неуспешные",
    "observability.dashboard.widget.instance_count.completed": "Завершённые",
    "observability.dashboard.widget.step_duration.p50": "p50",
    "observability.dashboard.widget.step_duration.p95": "p95",
    "observability.dashboard.widget.step_duration.p99": "p99",
    "observability.dashboard.alerts.title": "Активные оповещения",
    "observability.dashboard.alert_rule.title": "Створити правило оповещения",
    "observability.dashboard.alert_rule.name_label": "Название правила",
    "observability.dashboard.alert_rule.severity_label": "Важность",
    "observability.dashboard.alert_rule.expression_label": "Выражение",
    "observability.dashboard.alert_rule.save": "Зберегти",
    "observability.dashboard.alert_rule.saving": "Сохранение…",
    "cabinet.topbar.skipToMain": "Перейти к основному содержимому",
    "cabinet.topbar.environment.production": "Рабочая среда",
    "cabinet.topbar.environment.staging": "Тестовая среда",
    "cabinet.topbar.environment.sandbox": "Песочница",
    "cabinet.languageMenu.label.en": "English",
    "cabinet.languageMenu.label.ru": "Русский",
    "cabinet.languageMenu.label.kk": "Қазақша",
    "cabinet.languageMenu.label.uk": "Українська",
    "cabinet.languageMenu.label.be": "Білоруська",
    "cabinet.footer.copyright": "© {{year}} ABiTech. Все права защищены.",
    "cabinet.footer.supportLabel": "Поддержка",
    "cabinet.notfound.title": "Эта страница ещё не готова",
    "cabinet.notfound.subtitle":
      "Открытый адрес пока не имеет экрана. Команда знает.",
    "cabinet.notfound.back": "Назад в Мой кабинет",
    // FR-UI-CABINET-3 (Ukrainian; mirrors Russian per FR-I18N-3 fallback).
    "cabinet.required_user_parameters.menu_label":
      "Обов'язкові параметри користувача",
    "cabinet.required_user_parameters.title":
      "Обов'язкові параметри користувача",
    "cabinet.required_user_parameters.subtitle":
      "Ваш тенант вимагає зазначити ці параметри.",
    "cabinet.required_user_parameters.save": "Зберегти",
    "cabinet.required_user_parameters.resolve": "Зазначити значення",
    "cabinet.required_user_parameters.retry": "Повторити",
    "cabinet.required_user_parameters.missing": "Не заповнено",
    "cabinet.required_user_parameters.not_provided": "Ще не зазначено",
    "cabinet.required_user_parameters.supplied": "Зазначено",
    "cabinet.required_user_parameters.missing_count":
      "{count} обов'язкових параметрів не заповнено",
    "cabinet.required_user_parameters.missing_badge_aria":
      "Не заповнено обов'язковий параметр",
    "cabinet.required_user_parameters.stale_version_banner":
      "Сторінку змінено в іншій сесії. Оновіть, щоб продовжити.",
    "cabinet.required_user_parameters.stale_version.title": "Конфлікт версій",
    "cabinet.required_user_parameters.stale_version.body":
      "Сторінку змінено в іншій сесії. Поточна версія рядка: {{version}}.",
    "cabinet.required_user_parameters.stale_version.refetch":
      "Отримати останню версію",
    "cabinet.required_user_parameters.fetch_failure.title":
      "Не вдалося завантажити сторінку",
    "cabinet.required_user_parameters.fetch_failure.body":
      "Будь ласка, спробуйте ще раз.",
    "cabinet.required_user_parameters.permission_denied.title":
      "У вас немає доступу до цієї сторінки",
    "cabinet.required_user_parameters.permission_denied.body":
      "Зв'яжіться з адміністратором, якщо вважаєте, що це помилка.",
    "cabinet.required_user_parameters.rate_limit.title": "Забагато запитів",
    "cabinet.required_user_parameters.rate_limit.body":
      "Зачекайте трохи і спробуйте ще раз.",
    "cabinet.required_user_parameters.empty.title":
      "Обов'язкові параметри відсутні",
    "cabinet.required_user_parameters.empty.body":
      "Ваш тенант не налаштував обов'язкові параметри користувача.",
    "cabinet.required_user_parameters.summary.complete":
      "Усі обов'язкові параметри зазначено.",
    "cabinet.required_user_parameters.summary.missing_count":
      "{count} обов'язкових параметрів ще не заповнено.",
    "cabinet.required_user_parameters.summary.supplied_count":
      "{count} зазначено.",
    "promote.gate.artifact.open_full_envelope": "Артефакт конвертін толық ашу",
    "admin.matrix.column.platform": "Платформа ауқымы (сервер басқарады)",
    "admin.errors.permission_denied": "Рұқсаттарды өңдеуге құқығыңыз жоқ",
    "admin.webhooks.dialog.conflict.refetch": "Соңғы нұсқаны алып, біріктіру",
    "admin.webhooks.keys.one_time_copy_title": "Жаңа кілт мәні (бір реттік)",
    "admin.effects.http.dialog.create.success.copy_secret": "Құпияны көшіру",
    "admin.effects.email.dialog.create.success.copy_secret": "Құпияны көшіру",
    "admin.users.dialog.bulk_assign.title": "Жаппай рөл тағайындау ({{count}})",
    "observability.dashboard.widget.drill_down": "Сәтсіз даналарды ашу",
    "admin.errors.staging_only.title": "Permission editor is staging-only",
    "failed_instance.list_aria_label": "Failed instances for this tenant",
    "failed_instance.state.fetch_failure": "Failed to load failed instances.",
    "admin.webhooks.dialog.create.label.process_def": "Process definition ID",
    "admin.webhooks.errors.fetch_failure": "Failed to load webhook endpoints.",
    "tier.state.rate_limit": "Too many requests. Try again in {{seconds}}s.",
    "audit.state.rate_limit": "Too many requests. Try again in {{seconds}}s.",
    "admin.templates.registry.empty.message": "No published templates yet.",
    "admin.users.dialog.bulk_assign.error": "Bulk assignment failed.",
    "admin.users.orphan_panel.toggle": "Orphaned users ({{count}}) — review",
    "admin.effects.metrics.column.consecutive_failures": "Consecutive failures",
    // FR-CFG-1..4: tenant presentation, preferences, help, module-config (Ukrainian).
    "theme.picker.title": "Тема",
    "theme.picker.preview": "Попередній перегляд",
    "theme.picker.save": "Зберегти",
    "theme.picker.discard": "Скасувати",
    "theme.picker.empty": "Немає збережених тем.",
    "theme.picker.fetch_failure": "Не вдалося завантажити тему.",
    "theme.picker.permission_denied": "Немає доступу до теми.",
    "theme.picker.stale_version": "Тему оновлено — оновіть сторінку.",
    "theme.picker.rate_limit":
      "Забагато запитів. Повторіть через {{seconds}}с.",
    "theme.picker.validation_error": "Тема містить помилки валідації.",
    "theme.fallback.platform_default": "Тема платформи за замовчуванням",
    "preferences.favorites": "Вибране",
    "preferences.page_settings": "Налаштування сторінок",
    "preferences.current_org": "Поточна організація",
    "preferences.current_location": "Поточна локація",
    "preferences.current_locale": "Мова інтерфейсу",
    "preferences.save.success": "Налаштування збережено",
    "preferences.save.stale_rv": "Налаштування оновлено — оновіть сторінку.",
    "preferences.admin_read_only": "Лише для адміністратора",
    "help.tour.step_counter": "Крок {{current}} з {{total}}",
    "help.tour.next": "Далі",
    "help.tour.back": "Назад",
    "help.tour.skip": "Пропустити",
    "help.tour.dismiss": "Закрити",
    "help.tour.permission_denied.title": "Тур недоступний",
    "help.tour.permission_denied.body": "У вас немає доступу до цього туру.",
    "help.tour.permission_denied.target_id": "Ідентифікатор цілі",
    "help.tour.stale_version.title": "Тур оновлено",
    "help.tour.stale_version.detail": "Кроки туру змінилися.",
    "help.tour.stale_version.active_conflict": "Конфлікт активної версії",
    "help.tour.stale_version.reset_progress": "Скинути прогрес",
    "help.tour.rate_limit.title": "Забагато запитів",
    "help.tour.rate_limit.detail": "Повторіть через {{seconds}} секунд.",
    "help.tour.rate_limit.countdown": "Повтор через {{seconds}}с",
    "help.tour.validation_error.title": "Тур містить помилки",
    "help.tour.validation_error.item": "Крок {{step}}: {{error}}",
    "help.center.title": "Центр довідки",
    "help.center.empty.title": "Немає доступних турів",
    "help.center.empty.body":
      "Коли адміністратор опублікує тур, він з'явиться тут.",
    "help.center.empty.author": "Автор",
    "config.editor.title": "Конфігурація модуля",
    "config.editor.namespace": "Простір імен",
    "config.editor.environment": "Середовище",
    "config.editor.value": "Значення",
    "config.editor.save": "Зберегти",
    "config.editor.discard": "Скасувати",
    "config.editor.promote": "Просунути",
    "config.editor.schema_error.title": "Помилка схеми",
    "config.editor.schema_error.item": "{{path}}: {{message}}",
    "config.editor.stale_rv":
      "Версія конфігурації застаріла — оновіть сторінку.",
    "config.editor.production_admin.reason_label": "Причина зміни",
    "config.editor.production_admin.reason_required": "Вкажіть причину зміни.",
    "config.editor.promotion_review.title": "Запит на просування",
    "config.editor.promotion_review.approve": "Затвердити",
    "config.editor.promotion_review.reject": "Відхилити",
    // FR-COL-1 — record communication threads chrome.
    "comments.tab_label": "Коментарі",
    "comments.composer.placeholder": "Написати коментар...",
    "comments.composer.submit": "Надіслати",
    "comments.delete.confirm": "Видалити коментар?",
    "comments.deleted_marker": "(видалено)",
    "comments.empty": "Немає коментарів",
    "activity.tab_label": "Історія",
    "activity.empty": "Немає подій",
    // FR-PM-1 — workmanagement.
    "workmanagement.title": "Проєкти",
    "workmanagement.code": "Код",
    "workmanagement.name": "Назва",
    "workmanagement.responsible_department": "Підрозділ",
    "workmanagement.project_administrator": "Адміністратор проєкту",
    "workmanagement.planned_start_date": "Дата початку",
    "workmanagement.planned_end_date": "Дата завершення",
    "workmanagement.description": "Опис",
    "workmanagement.status": "Статус",
    "workmanagement.status.draft": "Чернетка",
    "workmanagement.status.active": "Активний",
    "workmanagement.status.completed": "Завершений",
    "workmanagement.status.archived": "Архівний",
    "workmanagement.status.pending_approval": "На погодженні",
    "workmanagement.status.closed": "Закритий",
    "workmanagement.new_button": "Створити проєкт",
    "workmanagement.save": "Зберегти",
    "workmanagement.cancel": "Скасувати",
    "workmanagement.registry.heading": "Реєстр проєктів",
    "workmanagement.form.title": "Новий проєкт",
    "workmanagement.registry.empty": "Немає проєктів",
    "workmanagement.registry.filter.status_label": "Статус",
    "workmanagement.registry.filter.all": "Усі статуси",
    "workmanagement.registry.error.load_failed":
      "Не вдалося завантажити реєстр проєктів",
    "workmanagement.registry.error.create_failed": "Не вдалося створити проєкт",
    "workmanagement.error.dates_invalid":
      "Дата завершення має бути пізніше дати початку",
    "workmanagement.error.name_required": "Введіть назву проєкту",
    "workmanagement.error.name_too_long":
      "Назва проєкту не повинна перевищувати 200 символів",
    "workmanagement.error.description_too_long":
      "Опис проєкту не повинен перевищувати 1000 символів",
    "workmanagement.detail.code": "Код проєкту",
    "workmanagement.detail.responsible_department": "Підрозділ",
    "workmanagement.detail.project_administrator": "Адміністратор проєкту",
    "workmanagement.detail.planned_start_date": "Дата початку",
    "workmanagement.detail.planned_end_date": "Дата завершення",
    "workmanagement.detail.description": "Опис",
    "workmanagement.detail.created_at": "Дата створення",
    "workmanagement.detail.created_by": "Автор",
    "workmanagement.detail.status": "Статус",
    "workmanagement.detail.row_version": "Версія рядка",
    "workmanagement.approval.title": "Погодження проєкту",
    "workmanagement.approval.stage_label": "Етап погодження",
    "workmanagement.approval.panel.title": "Рішення за етапом погодження",
    "workmanagement.approval.decision.label": "Рішення",
    "workmanagement.approval.decision.approved": "Погодити",
    "workmanagement.approval.decision.rejected": "Відхилити",
    "workmanagement.approval.comment.label": "Коментар",
    "workmanagement.approval.comment.placeholder":
      "Вкажіть причину або коментар…",
    "workmanagement.approval.submit": "Зафіксувати рішення",
    "workmanagement.approval.submit_for_approval": "Надіслати на погодження",
    "workmanagement.approval.preflight.title": "Попередня перевірка",
    "workmanagement.approval.preflight.submittable":
      "Можна надіслати на погодження",
    "workmanagement.approval.preflight.not_submittable":
      "Не готово до відправлення",
    "workmanagement.approval.history.title": "Історія погодження",
    "workmanagement.approval.history.empty": "Рішень ще немає",
    "workmanagement.approval.history.stage": "Етап {{stage}}",
    "workmanagement.approval.error.decision_required": "Виберіть рішення",
    "workmanagement.approval.error.comment_required":
      "У разі відхилення потрібен коментар",
    "workmanagement.approval.error.not_found": "Проєкт не знайдено",
  },
  be: {
    "common.save": "Захаваць",
    "common.cancel": "Отмена",
    "common.loading": "Загрузка…",
    "common.error": "Памылка",
    "common.retry": "Паўтарыць",
    "common.close": "Закрыць",
    "common.back": "Назад",
    "common.confirm": "Пацвердзіць",
    "common.delete": "Выдаліць",
    "common.edit": "Змяніць",
    "common.create": "Стварыць",
    "common.search": "Пошук",
    "common.copy": "Капіяваць",
    "common.yes": "Так",
    "common.no": "Не",
    "common.required": "Обязательное поле",
    "common.optional": "Необязательное поле",
    "validation.error.inline": "Памылка валідацыі",
    "validation.error.code_default": "Поле змяшчае памылку",
    // FR-UI-VIEW-1 AC-4 (rv-2): list-level 422 chrome.
    "validation.error.list": "Праверка не пройдзена",
    "conflict.title": "Канфлікт змен",
    "conflict.reapply": "Применить мои изменения к текущей версіі",
    "conflict.refetch": "Получить последнюю версию",
    "conflict.discard": "Адмяніць мои изменения",
    "conflict.serverVersion": "Бягучая версія (сервер)",
    "conflict.yourVersion": "Ваши изменения",
    "conflict.currentRowVersion": "Версія радка",
    "deprovision.confirm": "Пацвердзіць дэактывацыю",
    "reference.placeholder": "Пошук…",
    "reference.empty": "Нічога не знойдзена",
    "reference.loading": "Загрузка…",
    "reference.error": "Не ўдалося загрузіць варыянты",
    "reference.cancel": "Отмена",
    "reference.select": "Выбрать",
    "promote.gate.section.diff": "Изменения",
    "promote.gate.section.assertions": "Пройденные проверки",
    "promote.gate.section.artifact": "Пакет NEEDS_REVIEW",
    "promote.gate.diff.empty": "Не изменений для проверки.",
    "promote.gate.assertions.recorded": "записано",
    "promote.gate.artifact.envelope_meta": "Метаданные конверта",
    "promote.gate.artifact.task_spec_id": "task_spec_id",
    "promote.gate.artifact.task_spec_version": "task_spec_version",
    "promote.gate.artifact.kind": "kind",
    "promote.gate.artifact.agent_principal": "agent_principal",
    "promote.gate.artifact.submitted_at": "submitted_at",
    "promote.gate.artifact.attempt_count": "attempts",
    "promote.gate.artifact.spec_hash": "spec_hash",
    "promote.gate.artifact.final_status": "final_status",
    "promote.gate.artifact.spec": "spec",
    "promote.gate.artifact.attempts": "Попытки",
    "promote.gate.artifact.started_at": "started_at",
    "promote.gate.artifact.completed_at": "completed_at",
    "promote.gate.artifact.terminal_failure_reason": "terminal_failure_reason",
    "promote.gate.artifact.bpm_step_log_snapshot": "bpm_step_log_snapshot",
    "promote.gate.buttons.approve": "Утвердить промоушен",
    "promote.gate.buttons.approving": "Утверждение…",
    "promote.gate.buttons.reject": "Отклонить",
    "promote.gate.approve.error": "Не ўдалося зацвердзіць промоўшэн.",
    "promote.gate.reject.reason_label": "Причина отклонения",
    "promote.gate.reject.reason_placeholder": "Апішыце, што трэба выправіць…",
    "promote.gate.reject.confirm": "Пацвердзіць адхіленне",
    "promote.gate.reject.pending": "Отклонение…",
    "promote.gate.reject.cancel": "Отмена",
    "promote.gate.reject.error": "Не ўдалося адхіліць промоўшэн.",
    "promote.gate.error.load_failed": "Не ўдалося загрузіць рэцэнзію:",
    "admin.matrix.title": "Матрыца разрешений",
    "admin.matrix.column.role": "Роля",
    "admin.matrix.column.create": "Стварэнне",
    "admin.matrix.column.read": "Чытанне",
    "admin.matrix.column.update": "Змена",
    "admin.matrix.column.delete": "Выдаленне",
    "admin.matrix.column.all": "Все",
    "admin.matrix.action.save": "Захаваць роля",
    "admin.matrix.action.cancel": "Адмяніць",
    "admin.matrix.action.new_role": "Стварыць роля",
    "admin.matrix.action.delete_role": "Выдаліць роля",
    "admin.matrix.system_role.readonly": "Сістэмная роля — толькі для чытання",
    "admin.matrix.banner.open_review": "Адкрыць проверку",
    "admin.matrix.loading": "Загрузка матрицы...",
    "admin.errors.staging_only.code": "ENV_PRODUCTION",
    "admin.errors.version_conflict.title": "Конфликт версий",
    "admin.errors.version_conflict.refetch": "Получить последнюю версию",
    "admin.errors.version_conflict.merge": "Объединить мои изменения",
    "admin.errors.version_conflict.discard": "Адмяніць мои изменения",
    "admin.errors.fetch_failure": "Не ўдалося загрузіць матрыцу дазволаў",
    "admin.errors.rate_limit": "Зашмат запытаў. Пачакайце крыху.",
    "admin.errors.network": "Сетевая памылка. Проверьте подключение.",
    "admin.field_visibility.column.field": "Поле",
    "admin.field_visibility.column.classification": "Классификация",
    "admin.field_visibility.states.visible": "видимое",
    "admin.field_visibility.states.hidden_cosmetic": "скрыто (косметика)",
    "admin.field_visibility.states.denied_security": "запрещено (безопасность)",
    "admin.field_visibility.aria.visible": "видимое",
    "admin.field_visibility.empty": "Не полей для отображения.",
    "admin.webhooks.title": "Вебхук-эндпоинты",
    "admin.webhooks.column.url": "URL",
    "admin.webhooks.column.process_def": "Определение процесса",
    "admin.webhooks.column.signing_key_id": "Идентификатор ключа подписи",
    "admin.webhooks.column.status": "Статус",
    "admin.webhooks.column.version": "Версія",
    "admin.webhooks.column.actions": "Действия",
    "admin.webhooks.status.active": "активный",
    "admin.webhooks.status.disabled": "отключён",
    "admin.webhooks.action.add": "Добавить эндпоинт",
    "admin.webhooks.action.disable": "Отключить",
    "admin.webhooks.action.delete": "Выдаліць",
    "admin.webhooks.dialog.create.title": "Стварыць вебхук-эндпоинт",
    "admin.webhooks.dialog.create.label.url": "URL",
    "admin.webhooks.dialog.create.cta": "Стварыць",
    "admin.webhooks.dialog.create.success.title": "Вебхук создан",
    "admin.webhooks.dialog.create.success.copied": "Скопировано!",
    "admin.webhooks.dialog.conflict.discard": "Адмяніць мои изменения",
    "admin.webhooks.banner.open_review": "Адкрыць проверку",
    "admin.webhooks.table.aria_label": "Таблица вебхук-эндпоинтов",
    "admin.webhooks.keys.title": "Webhook HMAC ключи",
    "admin.webhooks.keys.active": "Актыўны",
    "admin.webhooks.keys.grace_period": "Льготный период",
    "admin.webhooks.keys.expired": "Истёк",
    "admin.webhooks.keys.rotate_action": "Сменить ключ",
    "admin.webhooks.keys.issue_action": "Выпустить ключ",
    "admin.webhooks.keys.revoke_action": "Отозвать",
    "admin.webhooks.keys.grace_window_label": "Льготный период",
    "admin.webhooks.keys.grace_until_label": "Истекает",
    "admin.webhooks.keys.expired_at_label": "Истёк",
    "admin.webhooks.keys.key_id_label": "ID ключа",
    "admin.webhooks.keys.created_at_label": "Создан",
    "admin.webhooks.keys.state_label": "Состояние",
    "admin.webhooks.keys.copy_button": "Капіяваць",
    "admin.webhooks.keys.done_button": "Готово",
    "admin.webhooks.keys.confirm_rotate_button": "Пацвердзіць смену",
    "admin.webhooks.keys.cancel_button": "Отмена",
    "admin.webhooks.keys.revoke_confirm_title": "Отозвать ключ?",
    "admin.webhooks.keys.revoke_confirm_placeholder": "Введите ID ключа",
    "admin.webhooks.keys.loading_error": "Не удалось загрузіць HMAC ключи.",
    "admin.webhooks.keys.retry": "Паўтарыць",
    "admin.webhooks.keys.countdown.separator": "и",
    "admin.webhooks.keys.countdown.expired": "Истёк",
    "admin.webhooks.keys.grace_window_minutes_min": "минимум {{minutes}} минут",
    "tier.title": "Уровень и квоты",
    "tier.tier.small": "Малый",
    "tier.tier.middle": "Средний",
    "tier.tier.corporate": "Корпоративный",
    "tier.limits.script_wall_clock_ms": "Стенд-байм-аут скрипта (мс)",
    "tier.limits.script_memory_pages": "Страницы памяти скрипта",
    "tier.limits.storage_mb": "Квота хранилища (МБ)",
    "tier.limits.file_storage_mb": "Квота файлового хранилища (МБ)",
    "tier.limits.concurrent_sandboxes": "Параллельные песочницы",
    "tier.limits.agent_retry_budget": "Бюджет повторов агента",
    "tier.limits.max_single_upload_mb": "Макс. размер загрузки (МБ)",
    "tier.limits.outbox_depth_cap": "Ограничение outbox",
    "tier.usage.storage_bytes": "Использование хранилища",
    "tier.usage.file_storage_bytes": "Использование файлов",
    "tier.usage.concurrent_sandboxes": "Активные песочницы",
    "tier.unavailable": "недоступно",
    "tier.state.loading": "Загрузка уровня и квот…",
    "tier.state.fetch_failure": "Не удалось загрузіць уровень и квоты.",
    "tier.state.permission_denied": "У вас нет прав на просмотр уровня и квот.",
    "audit.title": "Журнал изменений определений",
    "audit.filter.definition_type": "Тип определения",
    "audit.filter.all_types": "Все типы",
    "audit.filter.author": "Автор",
    "audit.filter.after": "После",
    "audit.filter.before": "До",
    "audit.filter.clear": "Очистить фильтры",
    "audit.column.definition_type": "Тип",
    "audit.column.definition_id": "Определение",
    "audit.column.action": "Действие",
    "audit.column.author": "Автор",
    "audit.column.agent": "Агент",
    "audit.column.agent_label": "Запіс агента",
    "audit.column.task_spec": "Спецификация задачи",
    "audit.column.orchestrator": "Оркестратор",
    "audit.column.version": "Версія",
    "audit.column.created_at": "Когда",
    "audit.column.definition_type.form": "Форма",
    "audit.column.definition_type.process": "Процесс",
    "audit.column.definition_type.entity": "Сущность",
    "audit.column.definition_type.permission": "Разрешение",
    "audit.column.definition_type.task_spec": "Спецификация",
    "audit.empty": "Не записей по текущим фильтрам.",
    "audit.pagination.label": "Пагинация журнала",
    "audit.pagination.previous": "Назад",
    "audit.pagination.next": "Вперёд",
    "audit.state.loading": "Загрузка журнала…",
    "audit.state.fetch_failure": "Не удалось загрузіць журнал.",
    "audit.state.permission_denied": "У вас нет прав на просмотр журнала.",
    "audit.state.validation_error": "Некорректный фильтр — проверьте ввод.",
    "admin.effects.http.page.title": "HTTP-эффекты",
    "admin.effects.http.empty": "HTTP-эндпоинты не настроены.",
    "admin.effects.http.table_aria_label": "Таблица HTTP-эффектов",
    "admin.effects.http.column.url": "URL",
    "admin.effects.http.column.method": "Метод",
    "admin.effects.http.column.status": "Статус",
    "admin.effects.http.column.actions": "Действия",
    "admin.effects.http.action.create": "Добавить эндпоинт",
    "admin.effects.http.action.disable": "Отключить",
    "admin.effects.http.action.delete": "Выдаліць",
    "admin.effects.http.field.url": "URL",
    "admin.effects.http.field.method": "HTTP-метод",
    "admin.effects.http.field.timeout": "Тайм-аут (секунды)",
    "admin.effects.http.dialog.create.title": "Добавить HTTP-эндпоинт",
    "admin.effects.http.dialog.create.success.title": "Эндпоинт создан",
    "admin.effects.email.page.title": "Email-каналы",
    "admin.effects.email.empty": "Email-каналы не настроены.",
    "admin.effects.email.table_aria_label": "Таблица Email-каналов",
    "admin.effects.email.column.from": "Адрес отправителя",
    "admin.effects.email.column.subject": "Тема",
    "admin.effects.email.column.locales": "Языки",
    "admin.effects.email.column.status": "Статус",
    "admin.effects.email.column.actions": "Действия",
    "admin.effects.email.action.create": "Добавить канал",
    "admin.effects.email.action.disable": "Отключить",
    "admin.effects.email.action.delete": "Выдаліць",
    "admin.effects.email.field.from_address": "Адрес отправителя",
    "admin.effects.email.field.subject": "Тема",
    "admin.effects.email.field.provider": "Провайдер",
    "admin.effects.email.field.body": "Текст письма (с поддержкой локали)",
    "admin.effects.email.dialog.create.title": "Добавить Email-канал",
    "admin.effects.email.dialog.create.success.title": "Канал создан",
    "admin.effects.status.active": "активный",
    "admin.effects.status.disabled": "отключён",
    "admin.effects.dialog.conflict.title": "Конфликт версий",
    "admin.effects.dialog.conflict.server_version": "Версія на сервере",
    "admin.effects.dialog.conflict.discard": "Адмяніць мои изменения",
    "admin.effects.dialog.conflict.refetch": "Обновить данные",
    "admin.secrets.title": "Секреты",
    "admin.secrets.create_button": "Стварыць секрет",
    "admin.secrets.empty_state": "Секреты ещё не созданы.",
    "admin.secrets.table.name": "Название",
    "admin.secrets.table.ref_id": "ID ссылки",
    "admin.secrets.table.status": "Статус",
    "admin.secrets.table.created_at": "Создан",
    "admin.secrets.table.updated_at": "Обновлён",
    "admin.secrets.table.version": "Версія",
    "admin.secrets.table.actions": "Действия",
    "admin.secrets.dialog.create.title": "Стварыць секрет",
    "admin.secrets.dialog.create.name_label": "Название секрета",
    "admin.secrets.dialog.create.success.title": "Секрет создан",
    "admin.secrets.dialog.create.success.copied": "Скопировано!",
    "admin.secrets.dialog.impact.title": "Выдаленне секрета",
    "admin.secrets.dialog.impact.kind_http_endpoint": "HTTP-эндпоинт",
    "admin.secrets.dialog.impact.kind_email_channel": "Email-канал",
    "admin.secrets.banner.open_review": "Адкрыць проверку",
    "admin.secrets.status.active": "активный",
    "admin.secrets.status.disabled": "отключён",
    "admin.templates.registry.title": "Реестр шаблонов",
    "admin.templates.registry.column.template_id": "ID шаблона",
    "admin.templates.registry.column.version": "Версія",
    "admin.templates.registry.column.description": "Описание",
    "admin.templates.registry.column.publisher_kind": "Издатель",
    "admin.templates.registry.column.pre_vetted": "Pre-vetted",
    "admin.templates.registry.column.published_at": "Апублікавана",
    "admin.templates.registry.column.published_by": "Кем",
    "admin.templates.registry.column.content_hash": "Content hash",
    "admin.templates.registry.column.actions": "Действия",
    "admin.templates.registry.action.install": "Установить",
    "admin.templates.registry.action.installing": "Установка…",
    "admin.templates.registry.table.aria_label": "Таблица реестра шаблонов",
    // FR-UI-ADM-13 — template export/import admin chrome (Belarusian).
    "admin.templates.export_import.action.export": "Экспорт",
    "admin.templates.export_import.action.exporting": "Экспорт…",
    "admin.templates.export_import.action.export_aria":
      "Экспартаваць шаблон {{template_id}} версіі {{version}}",
    "admin.templates.export_import.action.import": "Імпарт",
    "admin.templates.export_import.notice.operator_console_required":
      "Для імпарту шаблонаў выкарыстоўвайце кансоль аператара.",
    "admin.templates.export_import.dialog.import.title": "Імпарт шаблона",
    "admin.templates.export_import.dialog.import.file_label":
      "Файл пакета шаблона",
    "admin.templates.export_import.dialog.import.submit": "Адправіць",
    "admin.templates.export_import.dialog.import.submit_disabled_tooltip":
      "Для адпраўкі выкарыстоўвайце кансоль аператара",
    "admin.templates.export_import.dialog.replace.title":
      "Замяніць існуючы шаблон?",
    "admin.templates.export_import.dialog.replace.existing_label": "Існуючы:",
    "admin.templates.export_import.dialog.replace.imported_label":
      "Імпартаваны:",
    "admin.templates.export_import.dialog.replace.diff":
      "існуючы v{{existing_version}} → імпарт v{{import_version}}",
    "admin.templates.export_import.dialog.replace.confirm": "Замяніць",
    "admin.templates.export_import.dialog.replace.cancel": "Адмяніць",
    "admin.templates.export_import.dialog.replace.server_rules_notice":
      "Серверныя правілы нязменнасці версій прымяняюцца пры адпраўцы — гэта пацвярджэнне іх не адмяняе.",
    "admin.templates.export_import.errors.manifest_missing":
      "manifest.json не знойдзены ў архіве.",
    "admin.templates.export_import.errors.manifest_invalid_json":
      "manifest.json змяшчае невалідны JSON.",
    "admin.templates.export_import.errors.cross_publisher_kind":
      "Толькі шаблоны, апублікаваныя платформай, могуць імпартавацца аператарамі.",
    "admin.templates.export_import.errors.manifest_incomplete":
      "Маніфест змяшчае не ўсе абавязковыя палі.",
    "admin.templates.export_import.errors.file_pick_failed":
      "Файл не атрымалася прачытаць.",
    "admin.templates.export_import.errors.payload_too_large":
      "Пакет занадта вялікі.",
    "admin.templates.export_import.errors.validation_failed":
      "Праверка пакета не прайшла.",
    "admin.templates.export_import.tooltip.replaces_preserves_customizations":
      "FR-TPL-4 захоўвае кастамізацыі тэнанта пры ўсталяванні — гэта не закранаецца паўторным імпартам версіі шаблона.",
    "admin.templates.export_import.source.imported": "імпартаваны",
    "admin.templates.export_import.source.native": "родны",
    "admin.templates.update.badge.singular": "{{count}} обновление доступно",
    "admin.templates.update.badge.plural": "{{count}} обновлений доступно",
    "admin.templates.update.badge.view": "Адкрыць обновления",
    "admin.templates.update.title": "Обновления шаблонов",
    "admin.templates.update.list.empty": "Доступных обновлений нет.",
    "admin.templates.update.list.column.template_id": "ID шаблона",
    "admin.templates.update.list.column.installed": "Установлено",
    "admin.templates.update.list.column.target": "Целевая",
    "admin.templates.update.list.column.conflicts": "Конфликты",
    "admin.templates.update.list.column.irreversible": "Необратимо",
    "admin.templates.update.list.row.view": "Просмотр",
    "admin.templates.update.detail.title": "Обновление {{template_id}}",
    "admin.templates.update.detail.target_version": "Целевая: {{version}}",
    "admin.templates.update.classification.unchanged": "Не изменялось тенантом",
    "admin.templates.update.classification.modified": "Изменено тенантом",
    "admin.templates.update.classification.added": "Добавлено тенантом",
    "admin.templates.update.resolution.keep_tenant": "Оставить тенант",
    "admin.templates.update.resolution.accept_update": "Принять обновление",
    "admin.templates.update.resolution.merge": "Объединить",
    "admin.templates.update.resolution.close": "Закрыць",
    "admin.templates.update.resolution.preview": "Просмотр объединения",
    "admin.templates.update.apply": "Применить обновление",
    "admin.templates.update.apply.success": "Обновление применено",
    "admin.templates.update.errors.unresolved": "Не все конфликты разрешены",
    "formula.editor.label": "Формула",
    "formula.editor.validate": "Проверить",
    "formula.editor.type": "Тип: {{type}}",
    "formula.editor.placeholder": "Введите формулу…",
    "lists.title": "Запісы",
    "lists.table.aria_label": "Таблица записей сущности",
    "lists.empty.no_records": "Не записей для отображения.",
    "lists.empty.permission_denied": "У вас нет доступа к этим записям.",
    "lists.filter.group_label": "Фильтры",
    "lists.filter.placeholder": "Фильтр…",
    "lists.filter.aria_input": "Фильтр по полю {{field}}",
    "lists.sort.asc": "▲",
    "lists.sort.desc": "▼",
    "lists.sort.toggle_aria": "Змяніць сортировку для {{field}}",
    "lists.pagination.next": "Вперёд",
    "lists.pagination.aria_label": "Постраничная навигация",
    "lists.pagination.page_size": "Размер страницы: {{pageSize}}",
    "lists.boolean.yes": "Так",
    "lists.boolean.no": "Не",
    "lists.error.fetch_failure": "Не удалось загрузіць записи.",
    // FR-UI-VIEW-2 — Tree view chrome (Belarusian).
    "lists.tree.title": "Дрэва запісаў",
    "lists.tree.error.empty": "Для гэтай сутнасці няма запісаў.",
    "lists.tree.error.fetch_failure": "Не ўдалося загрузіць дрэва запісаў.",
    "lists.tree.error.permission_denied":
      "У вас няма доступу да гэтых запісаў.",
    "lists.tree.error.stale_version":
      "Структура гэтай сутнасці змянілася. Абнавіце старонку.",
    "lists.tree.error.rate_limit": "Зашмат запытаў. Паўтарыце спробу пазней.",
    "lists.tree.error.validation_error":
      "Канфігурацыя дрэва для гэтай сутнасці адхіленая серверам.",
    "lists.tree.count_banner": "{{shown}} з {{total}} запісаў паказана.",
    // FR-UI-VIEW-3 — Gantt view chrome (Belarusian).
    "lists.gantt.title": "Дыяграма Ганта",
    "lists.gantt.noStartField": "няма даты пачатку",
    "lists.gantt.zeroDuration": "дата пачатку пазней за дату завяршэння",
    "lists.gantt.capBanner":
      "Паказана {{count}} запісаў з {{total}}. Астатнія запісы не загружаны.",
    "lists.gantt.loadMore": "Загрузіць яшчэ",
    "lists.gantt.invalidConfig":
      "Канфігурацыя дыяграмы Ганта адсутнічае або няправільная.",
    "lists.gantt.dependsOnLabel": "Залежнасці",
    "lists.gantt.conflictOnMove":
      "Запіс быў зменены іншым карыстальнікам. Абнавіце старонку.",
    "processes.failed.title": "Неуспешные экземпляры процессов",
    "failed_instance.empty": "Не неуспешных экземпляров.",
    "failed_instance.failed_node_label": "Узел сбоя",
    "failed_instance.reason_label": "Причина",
    "failed_instance.retries_label": "Попыток повтора",
    "failed_instance.trace_label": "Трассировка сбоя",
    "failed_instance.trace_summary": "Записей в логе: {{count}}",
    "failed_instance.view_trace": "Адкрыць трассировку",
    "failed_instance.retry_button": "Паўтарыць шаг",
    "failed_instance.retry_pending": "Повтор шага…",
    "failed_instance.retry_error": "Не удалось паўтарыць шаг.",
    "failed_instance.cancel_button": "Адмяніць экземпляр",
    "failed_instance.cancel_pending": "Отмена…",
    "failed_instance.cancel_error": "Не удалось адмяніць экземпляр.",
    "failed_instance.cancel_confirm_title": "Адмяніць экземпляр?",
    "failed_instance.restored_orphan_badge": "Восстановленный orphan",
    "trace.title": "Трассировка выполнения",
    "trace.table_aria_label": "Лог шагов трассировки выполнения",
    "trace.column.node_id": "Узел",
    "trace.column.status": "Статус",
    "trace.column.retry_count": "Повторы",
    "trace.column.started_at": "Начало",
    "trace.column.duration_ms": "Длительность",
    "trace.column.error_message": "Памылка",
    "trace.empty": "Не записей в логе шагов.",
    "trace.state.fetch_failure": "Не удалось загрузіць лог шагов.",
    "operator.title": "Консоль оператора",
    "operator.summary.title": "Сводка по тенантам",
    "operator.summary.tenantCount": "Тенанты",
    "operator.summary.activeInstances": "Активные экземпляры",
    "operator.summary.errorRate": "Доля ошибок",
    "operator.summary.empty": "Тенантов пока нет",
    "operator.summary.computedAt": "Обновлено",
    "operator.summary.errorUnit": "ош",
    "operator.tokenPrompt.label": "Токен оператора",
    "operator.tokenPrompt.button": "Войти",
    "operator.tokenPrompt.placeholder": "Вставьте токен оператора",
    "apex.title": "ASCOA Platform",
    "apex.how_to_enter_title": "Войти в консоль оператора",
    "apex.open_operator": "Адкрыць консоль оператора",
    "apex.tour_title": "URL тенантов",
    "apex.tour_local": "дефолтный dev-тенант",
    "apex.tour_tenant": "поддомен тенанта (после провизии)",
    "operator.error.fetchFailure": "Не удалось загрузіць сводку.",
    "operator.error.retry": "Паўтарыць",
    "operator.summary.table.tenant": "Тенант",
    "operator.summary.table.instanceCount": "Активные экземпляры",
    "operator.summary.table.errorCount": "Памылкі",
    "operator.summary.table.activeSandboxCount": "Активные песочницы",
    "operator.summary.table.aria_label": "Разбивка по тенантам",
    "operator.nav.label": "Разделы оператора",
    "operator.nav.summary": "Сводка",
    "operator.nav.tenants": "Тенанты",
    "operator.nav.effects": "Эффекты / Операции",
    "operator.nav.audit": "Аудит",
    "operator.drillDown.title": "Детали тенанта",
    "operator.drillDown.instanceCount": "Активные экземпляры",
    "operator.drillDown.errorCount": "Памылкі",
    "operator.drillDown.activeSandboxCount": "Активные песочницы",
    "operator.drillDown.openAdmin": "Адкрыць /admin тенанта",
    "operator.drillDown.openFor": "Адкрыць детали тенанта {{slug}}",
    "operator.drillDown.close": "Закрыць",
    "operator.audit.title": "Аудит",
    "operator.audit.placeholder": "Журнал аудита ещё не реализован.",
    "operator.tenants.loadError": "Не удалось загрузіць тенантов.",
    "operator.topbar.localeLabel": "Мова інтэрфейсу",
    "operator.tenants.list.title": "Тэнанты",
    "operator.tenants.list.empty": "Тэнантаў не знойдзена.",
    "operator.tenants.list.refresh": "Абнавіць",
    "operator.tenants.list.loading": "Загрузка тэнантаў…",
    "operator.tenants.list.errorPrefix": "Не удалось загрузіць тэнантаў:",
    "operator.tenants.list.deprovision": "Дэактываваць",
    "operator.tenants.list.ariaLabel": "Спіс тэнантаў з дзеяннямі дэактывацыі",
    "operator.tenants.list.col.slug": "Слаг",
    "operator.tenants.list.col.name": "Назва",
    "operator.tenants.list.col.tier": "Узровень",
    "operator.tenants.list.col.status": "Статус",
    "operator.tenants.list.col.tenantId": "ID арэндатара",
    "operator.tenants.list.col.actions": "Дзеянні",
    // ISS-509 (BA ruling): localized "Unknown" status label for the
    // decoder fallback path (status field absent from wire).
    "operator.tenants.list.status.unknown": "невядома",
    // ISS-510: localized status + tier value labels.
    "operator.tenants.list.status.active": "актыўны",
    "operator.tenants.list.status.disabled": "адключаны",
    "operator.tenants.list.status.quiesced": "прыпынены",
    "operator.tenants.list.tier.small": "Малы",
    "operator.tenants.list.tier.middle": "Сярэдні",
    "operator.tenants.list.tier.corporate": "Карпаратыўны",
    "cabinet.title": "Рабочее пространство тенанта",
    "cabinet.nav.label": "Разделы рабочего пространства",
    "cabinet.nav.cabinet": "Мой кабинет",
    "cabinet.nav.tasks": "Мои задачи",
    "cabinet.nav.processes": "Процессы",
    "cabinet.nav.admin": "Администрирование",
    "cabinet.nav.section.records": "Запісы",
    "cabinet.nav.section.admin": "Администрирование",
    "cabinet.landing.title": "Мой кабинет",
    "cabinet.landing.subtitle": "Ваша работа с первого взгляда.",
    "cabinet.landing.tiles_label": "Сводка",
    "cabinet.landing.tasks_due_soon": "Задачи скоро",
    "cabinet.landing.open_processes": "Открытые процессы",
    "cabinet.landing.failed_processes": "Неуспешные процессы",
    "cabinet.landing.recent_records": "Недавние записи",
    "cabinet.admin.title": "Администрирование",
    "cabinet.admin.subtitle": "Управление конфигурацией тенанта.",
    "cabinet.admin.grid_label": "Разделы администрирования",
    "cabinet.admin.denied": "У вас нет доступа к разделу администрирования.",
    "cabinet.admin.tile.users": "Ролі",
    "cabinet.admin.tile.permissions_matrix": "Матрыца разрешений",
    "cabinet.admin.tile.record_field_permissions": "Права на записи и поля",
    "cabinet.admin.tile.webhooks": "Вебхук-эндпоинты",
    "cabinet.admin.tile.hmac_keys": "HMAC-ключи",
    "cabinet.admin.tile.http_endpoints": "HTTP-эндпоинты",
    "cabinet.admin.tile.email_channels": "Email-каналы",
    "cabinet.admin.tile.secrets": "Секреты",
    "cabinet.admin.tile.templates": "Шаблоны",
    "cabinet.admin.tile.template_updates": "Обновления шаблонов",
    "cabinet.admin.tile.tier": "Уровень и квоты",
    "cabinet.admin.tile.audit": "Журнал аудита",
    "cabinet.admin.tile.forms": "Формы",
    "cabinet.admin.tile.processes": "Процессы",
    "cabinet.errors.fetch_failure": "Не удалось загрузіць.",
    "cabinet.errors.permission_denied": "У вас нет доступа к этому.",
    "cabinet.errors.rate_limit": "Зашмат запытаў. Подождите.",
    "cabinet.errors.stale_version": "Эта версія больше недоступна.",
    "cabinet.errors.tile_label": "Сводка",
    "cabinet.errors.retry_after": "Повтор через {{seconds}} с.",
    "admin.record_permissions.role": "Роля",
    "admin.record_permissions.cell.ownership": "Правило владения",
    "admin.record_permissions.cell.fields": "Права полей",
    "admin.record_permissions.cell.owner": "Поле владельца",
    "admin.record_permissions.editor.title": "Редактировать правила записи",
    "admin.record_permissions.editor.tab.ownership": "Владение",
    "admin.record_permissions.editor.tab.condition": "Условие",
    "admin.record_permissions.editor.expr_label": "Выражение (expr-lang)",
    "admin.record_permissions.editor.preview_button": "Предпросмотр",
    "admin.record_permissions.editor.preview.loading": "Загрузка выборки…",
    "admin.record_permissions.conflict.title": "Конфликт версіі",
    "admin.record_permissions.conflict.refetch": "Получить последнюю версию",
    "admin.record_permissions.conflict.merge": "Объединить мои изменения",
    "admin.record_permissions.conflict.discard": "Адмяніць мои изменения",
    "admin.record_permissions.error.title": "Памылка загрузки",
    "admin.field_permissions.title": "Редактировать права полей",
    "admin.field_permissions.field": "Поле",
    "admin.field_permissions.state": "Состояние",
    "admin.field_permissions.state.readable": "Чытанне",
    "admin.field_permissions.state.writable": "Чытанне и запіс",
    "admin.field_permissions.state.hidden": "Скрыто",
    "admin.entity_field_owner.title": "Поле владельца сущности",
    "admin.entity_field_owner.field_label": "Поле владельца",
    "admin.users.title": "Карыстальнікі и ролі",
    "admin.users.column.display_name": "Имя",
    "admin.users.column.email": "Email",
    "admin.users.column.assigned_roles": "Назначенные ролі",
    "admin.users.column.actions": "Действия",
    "admin.users.column.select_all": "Выбрать всех",
    "admin.users.column.select_user": "Выбрать пользователя {{name}}",
    "admin.users.action.assign_roles": "Назначить ролі",
    "admin.users.action.assign_roles_for": "Назначить ролі для {{name}}",
    "admin.users.action.sync_refresh": "Обновить синхронизацию",
    "admin.users.action.sync_refresh_cooldown": "Подождите…",
    "admin.users.sync_status.ok": "Синхронизация в порядке",
    "admin.users.sync_status.degraded": "Синхронизация частичная",
    "admin.users.sync_status.failed": "Синхронизация не выполнена",
    "admin.users.dialog.role_picker.title": "Выберите ролі",
    "admin.users.dialog.role_picker.label.roles": "Ролі",
    "admin.users.dialog.role_picker.cta": "Захаваць",
    "admin.users.dialog.role_picker.error": "Не удалось захаваць.",
    "admin.users.dialog.bulk_assign.cta": "Назначить {{count}}",
    "admin.users.dialog.bulk_assign.failures_label": "Памылкі ({{count}})",
    "admin.users.dialog.bulk_assign.dismiss": "Готово",
    "admin.users.bulk.selected_count": "Выбрано: {{count}}",
    "admin.users.orphan_panel.title": "Потерянные пользователи",
    "admin.users.orphan_panel.empty": "Потерянных пользователей нет.",
    "admin.users.orphan_panel.remove_action_short": "Очистить",
    "admin.users.orphan_panel.cleaned": "Очищено",
    "admin.users.notice.dismiss": "Закрыць",
    "admin.users.empty.message": "Пользователей не найдено.",
    "admin.users.error.fetch_failure": "Не удалось загрузіць пользователей.",
    "admin.users.error.stale_version": "Версія устарела. Обновите страницу.",
    "admin.effects.metrics.page_title": "Операционная панель эффектов",
    "admin.effects.metrics.section.metrics": "Метрики по каналам",
    "admin.effects.metrics.table_label": "Метрики каналов эффектов",
    "admin.effects.metrics.circuit_table_label": "Состояние circuit-breaker",
    "admin.effects.metrics.column.channel_name": "Канал",
    "admin.effects.metrics.column.delivery_count": "Доставки (с начала работы)",
    "admin.effects.metrics.column.success_rate": "Успех %",
    "admin.effects.metrics.column.retry_count": "Повторы",
    "admin.effects.metrics.column.dead_letter_count": "Dead-letter",
    "admin.effects.metrics.column.latency_p50": "p50 мс",
    "admin.effects.metrics.column.latency_p95": "p95 мс",
    "admin.effects.metrics.column.latency_p99": "p99 мс",
    "admin.effects.metrics.column.endpoint": "Эндпоинт",
    "admin.effects.metrics.column.state": "Состояние",
    "admin.effects.metrics.column.time_in_state": "Время в состоянии",
    "admin.effects.metrics.column.failures_5min": "Сбои за 5 мин",
    "admin.effects.metrics.column.action": "Действие",
    "admin.effects.metrics.refresh.now": "Обновить",
    "admin.effects.metrics.last_refreshed": "Последнее обновление: {{time}}",
    "admin.effects.metrics.last_refreshed_prefix": "Последнее обновление:",
    "admin.effects.metrics.banner_dismiss": "Закрыць",
    "admin.effects.metrics.policy.updated_toast": "Политика обновлена",
    "admin.effects.metrics.dialog_title": "Настроить breaker",
    "admin.effects.metrics.dialog_save": "Захаваць",
    "admin.effects.metrics.dialog_saving": "Сохранение…",
    "admin.effects.metrics.dialog_cancel": "Отмена",
    "admin.effects.metrics.loading": "Загрузка метрик эффектов…",
    "admin.effects.metrics.error_504": "Сервер эффектов не отвечает (таймаут).",
    "admin.effects.metrics.error_5xx": "Сервер эффектов вернул памылку.",
    "admin.builder.page_title": "Конструктор форм",
    "admin.builder.save.cta": "Захаваць форму",
    "admin.builder.save.saving": "Сохранение...",
    "admin.builder.save.invalid": "Неверный ввод",
    "admin.builder.palette.aria": "Палитра полей",
    "admin.builder.palette.title": "Поля",
    "admin.builder.canvas.title": "Холст формы",
    "admin.builder.canvas.empty": "Перетащите поле из палитры, чтобы начать",
    "admin.builder.canvas.remove": "Выдаліць поле {{id}}",
    "admin.builder.settings.aria": "Налады",
    "admin.builder.settings.title": "Налады",
    "admin.builder.settings.entity_type": "Тип сущности",
    "admin.builder.settings.field_heading": "Поле {{id}}",
    "admin.builder.settings.label": "Метка",
    "admin.builder.settings.placeholder": "Подсказка",
    "admin.builder.settings.required": "Обязательное",
    "admin.builder.settings.visible_when": "Видимость (формула)",
    "admin.builder.settings.computed_from": "Вычисляется из (формула)",
    "admin.builder.settings.target_entity": "Целевая сущность",
    "admin.builder.settings.no_selection": "Выберите поле на холсте",
    "admin.builder.formula.ok": "Формула валидна",
    "admin.builder.formula.error": "Формула невалидна",
    "admin.builder.field_types.string": "Строка",
    "admin.builder.field_types.text": "Текст",
    "admin.builder.field_types.number": "Число",
    "admin.builder.field_types.integer": "Целое число",
    "admin.builder.field_types.float": "Дробное число",
    "admin.builder.field_types.boolean": "Логическое",
    "admin.builder.field_types.date": "Такта",
    "admin.builder.field_types.datetime": "Такта и время",
    "admin.builder.field_types.enum": "Перечисление",
    "admin.builder.field_types.reference": "Ссылка",
    "admin.designer.page_title": "Конструктор процессов",
    "admin.designer.save.cta": "Захаваць процесс",
    "admin.designer.save.saving": "Сохранение...",
    "admin.designer.save.invalid": "Неверный ввод",
    "admin.designer.palette.aria": "Палитра узлов",
    "admin.designer.palette.title": "Узлы",
    "admin.designer.canvas.title": "Холст процесса",
    "admin.designer.canvas.empty": "Перетащите узел из палитры, чтобы начать",
    "admin.designer.canvas.aria": "Холст BPM-процесса",
    "admin.designer.canvas.remove": "Выдаліць узел {{id}}",
    "admin.designer.canvas.dragging": "Перетаскивается {{id}}",
    "admin.designer.settings.aria": "Свойства",
    "admin.designer.settings.title": "Свойства",
    "admin.designer.settings.process_name": "Название процесса",
    "admin.designer.settings.process_description": "Описание",
    "admin.designer.settings.node_heading": "Узел {{id}}",
    "admin.designer.settings.node_label": "Метка",
    "admin.designer.settings.form_id": "Форма",
    "admin.designer.settings.candidate_role_id": "Кандидатская роля",
    "admin.designer.settings.assignee_user_id": "Назначенный карыстальнік",
    "admin.designer.settings.due_in_seconds": "Срок (секунды)",
    "admin.designer.settings.script_id": "Скрипт",
    "admin.designer.settings.script_formula": "Формула (необяз.)",
    "admin.designer.settings.delay_expression": "Задержка (выражение)",
    "admin.designer.settings.edge_heading": "Переход {{id}}",
    "admin.designer.settings.edge_guard": "Условие (формула)",
    "admin.designer.settings.no_config": "У этого узла нет настроек",
    "admin.designer.guard.ok": "Условие валидно",
    "admin.designer.guard.error": "Условие невалидно",
    "admin.designer.node_types.start": "Старт",
    "admin.designer.node_types.end": "Завершение",
    "admin.designer.node_types.userTask": "Карыстальнікская задача",
    "admin.designer.node_types.scriptTask": "Скрипт-задача",
    "admin.designer.node_types.xorGateway": "XOR-шлюз",
    "admin.designer.node_types.andGateway": "AND-шлюз",
    "admin.designer.node_types.timerCatch": "Таймер",
    "admin.forms.list.title": "Формы",
    "admin.forms.list.empty": "Форм пока нет.",
    "admin.forms.list.aria": "Список форм",
    "admin.processes.list.title": "Процессы",
    "admin.processes.list.empty": "Процессов пока нет.",
    "admin.processes.list.aria": "Список процессов",
    "observability.dashboard.title": "Наблюдаемость тенанта",
    "observability.dashboard.refresh": "Обновить",
    "observability.dashboard.author_alert": "Стварыць правило оповещения",
    "observability.dashboard.widget.instance_count.active": "Активные",
    "observability.dashboard.widget.instance_count.failed": "Неуспешные",
    "observability.dashboard.widget.instance_count.completed": "Завершённые",
    "observability.dashboard.widget.step_duration.p50": "p50",
    "observability.dashboard.widget.step_duration.p95": "p95",
    "observability.dashboard.widget.step_duration.p99": "p99",
    "observability.dashboard.alerts.title": "Активные оповещения",
    "observability.dashboard.alert_rule.title": "Стварыць правило оповещения",
    "observability.dashboard.alert_rule.name_label": "Название правила",
    "observability.dashboard.alert_rule.severity_label": "Важность",
    "observability.dashboard.alert_rule.expression_label": "Выражение",
    "observability.dashboard.alert_rule.save": "Захаваць",
    "observability.dashboard.alert_rule.saving": "Сохранение…",
    "cabinet.topbar.skipToMain": "Перейти к основному содержимому",
    "cabinet.topbar.environment.production": "Рабочая среда",
    "cabinet.topbar.environment.staging": "Тестовая среда",
    "cabinet.topbar.environment.sandbox": "Песочница",
    "cabinet.languageMenu.label.en": "English",
    "cabinet.languageMenu.label.ru": "Русский",
    "cabinet.languageMenu.label.kk": "Қазақша",
    "cabinet.languageMenu.label.uk": "Украінская",
    "cabinet.languageMenu.label.be": "Беларуская",
    "cabinet.footer.copyright": "© {{year}} ABiTech. Все права защищены.",
    "cabinet.footer.supportLabel": "Поддержка",
    "cabinet.notfound.title": "Эта страница ещё не готова",
    "cabinet.notfound.subtitle":
      "Открытый адрес пока не имеет экрана. Команда знает.",
    "cabinet.notfound.back": "Назад в Мой кабинет",
    // FR-UI-CABINET-3 (Belarusian; mirrors Russian per FR-I18N-3 fallback).
    "cabinet.required_user_parameters.menu_label":
      "Абавязковыя параметры карыстальніка",
    "cabinet.required_user_parameters.title":
      "Абавязковыя параметры карыстальніка",
    "cabinet.required_user_parameters.subtitle":
      "Ваш тэнант патрабуе зазначыць гэтыя параметры.",
    "cabinet.required_user_parameters.save": "Захаваць",
    "cabinet.required_user_parameters.resolve": "Зазначыць значэнні",
    "cabinet.required_user_parameters.retry": "Паўтарыць",
    "cabinet.required_user_parameters.missing": "Не запоўнена",
    "cabinet.required_user_parameters.not_provided": "Яшчэ не зазначана",
    "cabinet.required_user_parameters.supplied": "Зазначана",
    "cabinet.required_user_parameters.missing_count":
      "{count} абавязковых параметраў не запоўнена",
    "cabinet.required_user_parameters.missing_badge_aria":
      "Не запоўнены абавязковы параметр",
    "cabinet.required_user_parameters.stale_version_banner":
      "Старонка зменена ў іншай сесіі. Абнавіце, каб працягнуць.",
    "cabinet.required_user_parameters.stale_version.title": "Канфлікт версій",
    "cabinet.required_user_parameters.stale_version.body":
      "Старонка зменена ў іншай сесіі. Бягучая версія радка: {{version}}.",
    "cabinet.required_user_parameters.stale_version.refetch":
      "Атрымаць апошнюю версію",
    "cabinet.required_user_parameters.fetch_failure.title":
      "Не ўдалося загрузіць старонку",
    "cabinet.required_user_parameters.fetch_failure.body":
      "Калі ласка, паспрабуйце яшчэ раз.",
    "cabinet.required_user_parameters.permission_denied.title":
      "У вас няма доступу да гэтай старонкі",
    "cabinet.required_user_parameters.permission_denied.body":
      "Звярніцеся да адміністратара, калі лічыце, што гэта памылка.",
    "cabinet.required_user_parameters.rate_limit.title": "Зашмат запытаў",
    "cabinet.required_user_parameters.rate_limit.body":
      "Пачакайце трохі і паспрабуйце яшчэ раз.",
    "cabinet.required_user_parameters.empty.title":
      "Абавязковыя параметры адсутнічаюць",
    "cabinet.required_user_parameters.empty.body":
      "Ваш тэнант не наладзіў абавязковыя параметры карыстальніка.",
    "cabinet.required_user_parameters.summary.complete":
      "Усе абавязковыя параметры зазначаны.",
    "cabinet.required_user_parameters.summary.missing_count":
      "{count} абавязковых параметраў яшчэ не запоўнена.",
    "cabinet.required_user_parameters.summary.supplied_count":
      "{count} зазначана.",
    "promote.gate.artifact.open_full_envelope": "Артефакт конвертін толық ашу",
    "admin.matrix.column.platform": "Платформа ауқымы (сервер басқарады)",
    "admin.errors.permission_denied": "Рұқсаттарды өңдеуге құқығыңыз жоқ",
    "admin.webhooks.dialog.conflict.refetch": "Соңғы нұсқаны алып, біріктіру",
    "admin.webhooks.keys.one_time_copy_title": "Жаңа кілт мәні (бір реттік)",
    "admin.effects.http.dialog.create.success.copy_secret": "Құпияны көшіру",
    "admin.effects.email.dialog.create.success.copy_secret": "Құпияны көшіру",
    "admin.users.dialog.bulk_assign.title": "Жаппай рөл тағайындау ({{count}})",
    "observability.dashboard.widget.drill_down": "Сәтсіз даналарды ашу",
    "admin.errors.staging_only.title": "Permission editor is staging-only",
    "failed_instance.list_aria_label": "Failed instances for this tenant",
    "failed_instance.state.fetch_failure": "Failed to load failed instances.",
    "admin.webhooks.dialog.create.label.process_def": "Process definition ID",
    "admin.webhooks.errors.fetch_failure": "Failed to load webhook endpoints.",
    "tier.state.rate_limit": "Too many requests. Try again in {{seconds}}s.",
    "audit.state.rate_limit": "Too many requests. Try again in {{seconds}}s.",
    "admin.templates.registry.empty.message": "No published templates yet.",
    "admin.users.dialog.bulk_assign.error": "Bulk assignment failed.",
    "admin.users.orphan_panel.toggle": "Orphaned users ({{count}}) — review",
    "admin.effects.metrics.column.consecutive_failures": "Consecutive failures",
    // FR-CFG-1..4: tenant presentation, preferences, help, module-config (Belarusian).
    "theme.picker.title": "Тэма",
    "theme.picker.preview": "Папярэдні прагляд",
    "theme.picker.save": "Захаваць",
    "theme.picker.discard": "Адмяніць",
    "theme.picker.empty": "Няма захаваных тэм.",
    "theme.picker.fetch_failure": "Не атрымалася загрузіць тэму.",
    "theme.picker.permission_denied": "Няма доступу да тэмы.",
    "theme.picker.stale_version": "Тэма абноўлена — абнавіце старонку.",
    "theme.picker.rate_limit": "Зашмат запытаў. Паўтарыце праз {{seconds}}с.",
    "theme.picker.validation_error": "Тэма змяшчае памылкі валідацыі.",
    "theme.fallback.platform_default": "Тэма платформы па змаўчанні",
    "preferences.favorites": "Абранае",
    "preferences.page_settings": "Налады старонак",
    "preferences.current_org": "Бягучая арганізацыя",
    "preferences.current_location": "Бягучая лакацыя",
    "preferences.current_locale": "Мова інтэрфейсу",
    "preferences.save.success": "Налады захаваны",
    "preferences.save.stale_rv": "Налады абноўлены — абнавіце старонку.",
    "preferences.admin_read_only": "Толькі для адміністратара",
    "help.tour.step_counter": "Крок {{current}} з {{total}}",
    "help.tour.next": "Далей",
    "help.tour.back": "Назад",
    "help.tour.skip": "Прапусціць",
    "help.tour.dismiss": "Закрыць",
    "help.tour.permission_denied.title": "Тур недаступны",
    "help.tour.permission_denied.body": "У вас няма доступу да гэтага тура.",
    "help.tour.permission_denied.target_id": "Ідэнтыфікатар мэты",
    "help.tour.stale_version.title": "Тур абноўлены",
    "help.tour.stale_version.detail": "Крокі тура змяніліся.",
    "help.tour.stale_version.active_conflict": "Канфлікт актыўнай версіі",
    "help.tour.stale_version.reset_progress": "Скінуць прагрэс",
    "help.tour.rate_limit.title": "Зашмат запытаў",
    "help.tour.rate_limit.detail": "Паўтарыце праз {{seconds}} секунд.",
    "help.tour.rate_limit.countdown": "Паўтор праз {{seconds}}с",
    "help.tour.validation_error.title": "Тур змяшчае памылкі",
    "help.tour.validation_error.item": "Крок {{step}}: {{error}}",
    "help.center.title": "Цэнтр даведкі",
    "help.center.empty.title": "Няма даступных тураў",
    "help.center.empty.body":
      "Калі адміністратар апублікуе тур, ён з'явіцца тут.",
    "help.center.empty.author": "Аўтар",
    "config.editor.title": "Канфігурацыя модуля",
    "config.editor.namespace": "Прастора імёнаў",
    "config.editor.environment": "Асяроддзе",
    "config.editor.value": "Значэнне",
    "config.editor.save": "Захаваць",
    "config.editor.discard": "Адмяніць",
    "config.editor.promote": "Прасунуць",
    "config.editor.schema_error.title": "Памылка схемы",
    "config.editor.schema_error.item": "{{path}}: {{message}}",
    "config.editor.stale_rv":
      "Версія канфігурацыі састарэла — абнавіце старонку.",
    "config.editor.production_admin.reason_label": "Прычына змены",
    "config.editor.production_admin.reason_required": "Увядзіце прычыну змены.",
    "config.editor.promotion_review.title": "Запыт на прасоўванне",
    "config.editor.promotion_review.approve": "Зацвердзіць",
    "config.editor.promotion_review.reject": "Адхіліць",
    // FR-COL-1 — record communication threads chrome.
    "comments.tab_label": "Каментары",
    "comments.composer.placeholder": "Напісаць каментар...",
    "comments.composer.submit": "Адправіць",
    "comments.delete.confirm": "Выдаліць каментар?",
    "comments.deleted_marker": "(выдалена)",
    "comments.empty": "Няма каментароў",
    "activity.tab_label": "Гісторыя",
    "activity.empty": "Няма падзей",
    // FR-PM-1 — workmanagement.
    "workmanagement.title": "Праекты",
    "workmanagement.code": "Код",
    "workmanagement.name": "Назва",
    "workmanagement.responsible_department": "Падраздзяленне",
    "workmanagement.project_administrator": "Адміністратар праекта",
    "workmanagement.planned_start_date": "Дата пачатку",
    "workmanagement.planned_end_date": "Дата завяршэння",
    "workmanagement.description": "Апісанне",
    "workmanagement.status": "Статус",
    "workmanagement.status.draft": "Чарнавік",
    "workmanagement.status.active": "Актыўны",
    "workmanagement.status.completed": "Завершаны",
    "workmanagement.status.archived": "Архіўны",
    "workmanagement.status.pending_approval": "На ўзгадненні",
    "workmanagement.status.closed": "Закрыты",
    "workmanagement.new_button": "Стварыць праект",
    "workmanagement.save": "Захаваць",
    "workmanagement.cancel": "Скасаваць",
    "workmanagement.registry.heading": "Рэестр праектаў",
    "workmanagement.form.title": "Новы праект",
    "workmanagement.registry.empty": "Няма праектаў",
    "workmanagement.registry.filter.status_label": "Статус",
    "workmanagement.registry.filter.all": "Усе статусы",
    "workmanagement.registry.error.load_failed":
      "Не атрымалася загрузіць рэестр праектаў",
    "workmanagement.registry.error.create_failed":
      "Не атрымалася стварыць праект",
    "workmanagement.error.dates_invalid":
      "Дата завяршэння павінна быць пазней даты пачатку",
    "workmanagement.error.name_required": "Увядзіце назву праекта",
    "workmanagement.error.name_too_long":
      "Назва праекта не павінна перавышаць 200 сімвалаў",
    "workmanagement.error.description_too_long":
      "Апісанне праекта не павінна перавышаць 1000 сімвалаў",
    "workmanagement.detail.code": "Код праекта",
    "workmanagement.detail.responsible_department": "Падраздзяленне",
    "workmanagement.detail.project_administrator": "Адміністратар праекта",
    "workmanagement.detail.planned_start_date": "Дата пачатку",
    "workmanagement.detail.planned_end_date": "Дата завяршэння",
    "workmanagement.detail.description": "Апісанне",
    "workmanagement.detail.created_at": "Дата стварэння",
    "workmanagement.detail.created_by": "Аўтар",
    "workmanagement.detail.status": "Статус",
    "workmanagement.detail.row_version": "Версія радка",
    "workmanagement.approval.title": "Пагадненне праекта",
    "workmanagement.approval.stage_label": "Этап пагаднення",
    "workmanagement.approval.panel.title": "Рашэнне па этапе пагаднення",
    "workmanagement.approval.decision.label": "Рашэнне",
    "workmanagement.approval.decision.approved": "Пагадзіць",
    "workmanagement.approval.decision.rejected": "Адхіліць",
    "workmanagement.approval.comment.label": "Каментар",
    "workmanagement.approval.comment.placeholder":
      "Увядзіце прычыну або каментар…",
    "workmanagement.approval.submit": "Зафіксаваць рашэнне",
    "workmanagement.approval.submit_for_approval": "Адправіць на пагадненне",
    "workmanagement.approval.preflight.title": "Папярэдняя праверка",
    "workmanagement.approval.preflight.submittable":
      "Можна адправіць на пагадненне",
    "workmanagement.approval.preflight.not_submittable":
      "Не гатова да адпраўлення",
    "workmanagement.approval.history.title": "Гісторыя пагаднення",
    "workmanagement.approval.history.empty": "Рашэнняў пакуль няма",
    "workmanagement.approval.history.stage": "Этап {{stage}}",
    "workmanagement.approval.error.decision_required": "Выберыце рашэнне",
    "workmanagement.approval.error.comment_required":
      "Пры адхіленні каментар абавязковы",
    "workmanagement.approval.error.not_found": "Праект не знойдзены",
  },
};

/**
 * The `conflict` namespace (FR-UI-CORE-6 bridge contract).
 *
      "alert-rule persistence will ship with FR-OBS-4",
  },
} as const;

/**
 * Resource bundle for the `conflict` namespace.
 *
 * Deliberately empty. The `conflict.*` keys live as flat keys in the
 * `common` namespace (see COMMON_RESOURCES above) to preserve the
 * FR-UI-CORE-6 bridge contract. This namespace exists in `ns` so
 * callers that bind to it explicitly (`useTranslation("conflict")`)
 * remain valid — they'll receive `t()` calls resolved against the
 * common bundle via i18next's namespace machinery, which returns
 * the same flat-key values.
 */
const CONFLICT_RESOURCES = {
  ru: {},
  kk: {},
  en: {},
  uk: {},
  be: {},
} as const;

/**
 * ISS-442 (counterparties UX) — `directory.counterparties.*` keys.
 *
 * BA spec (verbatim):
 *   (b) Translation all locales (RU authoritative, KK falls back
 *       to RU when a key is missing, EN must have real English).
 *       Covers the list + new/edit/detail + enum labels. No
 *       hardcoded strings. i18n namespace `directory.counterparties.*`
 *       (consistent with how the rest of `apps/web/src/features/
 *       directory/` namespaces).
 *
 * Implementation:
 *   - Authored for the three shipped locales (ru / kk / en). RU
 *     is the canonical Russian; KK is Kazakh; EN is the developer
 *     / fallback language. Each key has a real translation in all
 *     three locales (no whitespace-only values, no placeholder
 *     strings).
 *   - uk + be fall back to ru (i18next's `fallbackLng: 'ru'` chain
 *     resolves a missing key in uk → ru first, then en, then the
 *     key string). We copy the Russian values into uk + be so the
 *     strings render in Russian for users with those locales
 *     (the BA's "RU authoritative" half) — without duplicating the
 *     translation work; if/when a real uk / be translator is hired
 *     the entries are local to one constant and the diff is
 *     trivial.
 *   - All keys are flat (no nested objects) to match the rest of
 *     the bundle (see COMMON_RESOURCES doc comment above for the
 *     flat-key rationale).
 *   - The bundle is merged into the `common` namespace at init
 *     time (see the `resources` constant below) so the keys are
 *     addressable as `t("directory.counterparties.list.title")`
 *     from a default-namespace `useTranslation()` hook — same
 *     contract as the rest of the bundle.
 */
const DIRECTORY_COUNTERPARTIES_RESOURCES = {
  // Russian (authoritative).
  ru: {
    // list
    "directory.counterparties.list.title": "Контрагенты",
    "directory.counterparties.list.column.name": "Название",
    "directory.counterparties.list.column.status": "Статус",
    "directory.counterparties.list.column.last_update": "Последнее обновление",
    "directory.counterparties.list.empty": "Контрагенты не найдены",
    "directory.counterparties.list.loading": "Загрузка контрагентов…",
    "directory.counterparties.list.error":
      "Не удалось загрузить контрагентов: {{message}}",
    "directory.counterparties.list.refresh": "Обновить",
    "directory.counterparties.list.search_by_name": "Поиск по названию",
    "directory.counterparties.list.legal_form_filter":
      "Организационно-правовая форма",
    "directory.counterparties.list.legal_form_all": "Все",
    "directory.counterparties.list.new": "Новый контрагент",
    "directory.counterparties.list.view": "Просмотр",
    // detail
    "directory.counterparties.detail.not_found": "Контрагент не найден",
    "directory.counterparties.detail.edit": "Редактировать",
    "directory.counterparties.detail.back": "Назад",
    "directory.counterparties.detail.audit_title": "История изменений",
    "directory.counterparties.detail.audit_empty": "Записей пока нет",
    "directory.counterparties.detail.loading": "Загрузка контрагента…",
    "directory.counterparties.detail.error":
      "Не удалось загрузить контрагента: {{message}}",
    "directory.counterparties.detail.field.name": "Название",
    "directory.counterparties.detail.field.legal_form":
      "Организационно-правовая форма",
    "directory.counterparties.detail.field.tax_id": "ИНН",
    "directory.counterparties.detail.field.contact_person_id":
      "ID контактного лица",
    "directory.counterparties.detail.field.email": "Электронная почта",
    "directory.counterparties.detail.field.phone": "Телефон",
    "directory.counterparties.detail.field.address": "Адрес",
    "directory.counterparties.detail.field.notes": "Примечания",
    "directory.counterparties.detail.field.status": "Статус",
    "directory.counterparties.detail.field.row_version": "Версия строки (OCC)",
    "directory.counterparties.detail.field.created_at": "Дата создания",
    "directory.counterparties.detail.field.created_by_subject":
      "Кем создан (subject)",
    "directory.counterparties.detail.field.updated_at": "Последнее обновление",
    "directory.counterparties.detail.field.updated_by_subject":
      "Кем обновлён (subject)",
    // edit
    "directory.counterparties.edit.title": "Редактировать контрагента",
    "directory.counterparties.edit.cancel": "Отмена",
    "directory.counterparties.edit.submit": "Сохранить",
    "directory.counterparties.edit.submitting": "Сохранение…",
    "directory.counterparties.edit.loading": "Загрузка контрагента…",
    "directory.counterparties.edit.not_found": "Контрагент не найден",
    "directory.counterparties.edit.error": "Ошибка: {{message}}",
    "directory.counterparties.edit.row_version":
      "Версия строки (OCC): {{value}}",
    // new
    "directory.counterparties.new.title": "Новый контрагент",
    "directory.counterparties.new.cancel": "Отмена",
    "directory.counterparties.new.submit": "Создать контрагента",
    "directory.counterparties.new.submitting": "Создание…",
    "directory.counterparties.new.error": "Ошибка: {{message}}",
    // enum labels
    "directory.counterparties.legal_form.individual": "Физическое лицо",
    "directory.counterparties.legal_form.sole_proprietor": "ИП",
    "directory.counterparties.legal_form.partnership": "Товарищество",
    "directory.counterparties.legal_form.corporation": "Корпорация",
    "directory.counterparties.legal_form.cooperative": "Кооператив",
    "directory.counterparties.legal_form.sole_prop": "ИП",
    "directory.counterparties.legal_form.llc": "ООО",
    "directory.counterparties.legal_form.jsc": "АО",
    "directory.counterparties.legal_form.plc": "ПАО",
    "directory.counterparties.legal_form.other": "Другое",
    "directory.counterparties.status.active": "Активен",
    "directory.counterparties.status.removed": "Удалён",
  },
  // Kazakh (full Kazakh translations; falls back to ru on missing
  // keys per the BA spec's "KK falls back to RU when a key is
  // missing" rule, but we author the full set for the canonical
  // locale coverage).
  kk: {
    "directory.counterparties.list.title": "Контрагенттер",
    "directory.counterparties.list.column.name": "Атауы",
    "directory.counterparties.list.column.status": "Күй",
    "directory.counterparties.list.column.last_update": "Соңғы жаңарту",
    "directory.counterparties.list.empty": "Контрагенттер табылмады",
    "directory.counterparties.list.loading": "Контрагенттер жүктелуде…",
    "directory.counterparties.list.error":
      "Контрагенттерді жүктеу мүмкін болмады: {{message}}",
    "directory.counterparties.list.refresh": "Жаңарту",
    "directory.counterparties.list.search_by_name": "Атауы бойынша іздеу",
    "directory.counterparties.list.legal_form_filter":
      "Ұйымдастырушылық-құқықтық нысаны",
    "directory.counterparties.list.legal_form_all": "Барлығы",
    "directory.counterparties.list.new": "Жаңа контрагент",
    "directory.counterparties.list.view": "Қарау",
    "directory.counterparties.detail.not_found": "Контрагент табылмады",
    "directory.counterparties.detail.edit": "Өңдеу",
    "directory.counterparties.detail.back": "Артқа",
    "directory.counterparties.detail.audit_title": "Өзгерістер тарихы",
    "directory.counterparties.detail.audit_empty": "Жазбалар жоқ",
    "directory.counterparties.detail.loading": "Контрагент жүктелуде…",
    "directory.counterparties.detail.error":
      "Контрагентті жүктеу мүмкін болмады: {{message}}",
    "directory.counterparties.detail.field.name": "Атауы",
    "directory.counterparties.detail.field.legal_form":
      "Ұйымдастырушылық-құқықтық нысаны",
    "directory.counterparties.detail.field.tax_id": "ЖСН",
    "directory.counterparties.detail.field.contact_person_id":
      "Байланыс тұлғасының ID",
    "directory.counterparties.detail.field.email": "Электрондық пошта",
    "directory.counterparties.detail.field.phone": "Телефон",
    "directory.counterparties.detail.field.address": "Мекенжай",
    "directory.counterparties.detail.field.notes": "Ескертпелер",
    "directory.counterparties.detail.field.status": "Күй",
    "directory.counterparties.detail.field.row_version": "Жол нұсқасы (OCC)",
    "directory.counterparties.detail.field.created_at": "Жасалған күні",
    "directory.counterparties.detail.field.created_by_subject":
      "Кім жасаған (subject)",
    "directory.counterparties.detail.field.updated_at": "Соңғы жаңарту",
    "directory.counterparties.detail.field.updated_by_subject":
      "Кім жаңартқан (subject)",
    "directory.counterparties.edit.title": "Контрагентті өңдеу",
    "directory.counterparties.edit.cancel": "Бас тарту",
    "directory.counterparties.edit.submit": "Сақтау",
    "directory.counterparties.edit.submitting": "Сақталуда…",
    "directory.counterparties.edit.loading": "Контрагент жүктелуде…",
    "directory.counterparties.edit.not_found": "Контрагент табылмады",
    "directory.counterparties.edit.error": "Қате: {{message}}",
    "directory.counterparties.edit.row_version": "Жол нұсқасы (OCC): {{value}}",
    "directory.counterparties.new.title": "Жаңа контрагент",
    "directory.counterparties.new.cancel": "Бас тарту",
    "directory.counterparties.new.submit": "Контрагентті жасау",
    "directory.counterparties.new.submitting": "Жасалуда…",
    "directory.counterparties.new.error": "Қате: {{message}}",
    "directory.counterparties.legal_form.individual": "Жеке тұлға",
    "directory.counterparties.legal_form.sole_proprietor": "ЖК",
    "directory.counterparties.legal_form.partnership": "Серіктестік",
    "directory.counterparties.legal_form.corporation": "Корпорация",
    "directory.counterparties.legal_form.cooperative": "Кооператив",
    "directory.counterparties.legal_form.sole_prop": "ЖК",
    "directory.counterparties.legal_form.llc": "ЖШС",
    "directory.counterparties.legal_form.jsc": "АҚ",
    "directory.counterparties.legal_form.plc": "БАҚ",
    "directory.counterparties.legal_form.other": "Басқа",
    "directory.counterparties.status.active": "Белсенді",
    "directory.counterparties.status.removed": "Жойылған",
  },
  // English (developer / fallback language; BA spec "EN must have
  // real English").
  en: {
    "directory.counterparties.list.title": "Counterparties",
    "directory.counterparties.list.column.name": "Name",
    "directory.counterparties.list.column.status": "Status",
    "directory.counterparties.list.column.last_update": "Last update",
    "directory.counterparties.list.empty": "No counterparties found",
    "directory.counterparties.list.loading": "Loading counterparties…",
    "directory.counterparties.list.error":
      "Failed to load counterparties: {{message}}",
    "directory.counterparties.list.refresh": "Refresh",
    "directory.counterparties.list.search_by_name": "Search by name",
    "directory.counterparties.list.legal_form_filter": "Legal form",
    "directory.counterparties.list.legal_form_all": "All",
    "directory.counterparties.list.new": "New counterparty",
    "directory.counterparties.list.view": "View",
    "directory.counterparties.detail.not_found": "Counterparty not found",
    "directory.counterparties.detail.edit": "Edit",
    "directory.counterparties.detail.back": "Back",
    "directory.counterparties.detail.audit_title": "Audit trail",
    "directory.counterparties.detail.audit_empty": "No audit entries yet",
    "directory.counterparties.detail.loading": "Loading counterparty…",
    "directory.counterparties.detail.error":
      "Failed to load counterparty: {{message}}",
    "directory.counterparties.detail.field.name": "Name",
    "directory.counterparties.detail.field.legal_form": "Legal form",
    "directory.counterparties.detail.field.tax_id": "Tax id",
    "directory.counterparties.detail.field.contact_person_id":
      "Contact person id",
    "directory.counterparties.detail.field.email": "Email",
    "directory.counterparties.detail.field.phone": "Phone",
    "directory.counterparties.detail.field.address": "Address",
    "directory.counterparties.detail.field.notes": "Notes",
    "directory.counterparties.detail.field.status": "Status",
    "directory.counterparties.detail.field.row_version": "Row version (OCC)",
    "directory.counterparties.detail.field.created_at": "Created at",
    "directory.counterparties.detail.field.created_by_subject":
      "Created by subject",
    "directory.counterparties.detail.field.updated_at": "Last update",
    "directory.counterparties.detail.field.updated_by_subject":
      "Updated by subject",
    "directory.counterparties.edit.title": "Edit counterparty",
    "directory.counterparties.edit.cancel": "Cancel",
    "directory.counterparties.edit.submit": "Save",
    "directory.counterparties.edit.submitting": "Saving…",
    "directory.counterparties.edit.loading": "Loading counterparty…",
    "directory.counterparties.edit.not_found": "Counterparty not found",
    "directory.counterparties.edit.error": "Error: {{message}}",
    "directory.counterparties.edit.row_version": "Row version (OCC): {{value}}",
    "directory.counterparties.new.title": "New counterparty",
    "directory.counterparties.new.cancel": "Cancel",
    "directory.counterparties.new.submit": "Create counterparty",
    "directory.counterparties.new.submitting": "Creating…",
    "directory.counterparties.new.error": "Error: {{message}}",
    "directory.counterparties.legal_form.individual": "Individual",
    "directory.counterparties.legal_form.sole_proprietor": "Sole proprietor",
    "directory.counterparties.legal_form.partnership": "Partnership",
    "directory.counterparties.legal_form.corporation": "Corporation",
    "directory.counterparties.legal_form.cooperative": "Cooperative",
    "directory.counterparties.legal_form.sole_prop": "Sole proprietorship",
    "directory.counterparties.legal_form.llc": "LLC",
    "directory.counterparties.legal_form.jsc": "JSC",
    "directory.counterparties.legal_form.plc": "PLC",
    "directory.counterparties.legal_form.other": "Other",
    "directory.counterparties.status.active": "Active",
    "directory.counterparties.status.removed": "Removed",
  },
  // Ukrainian + Belarusian (FR-I18N-3, CIS-region expansion).
  // These locales fall back to ru via the i18next fallback chain
  // when a key is missing; we copy the Russian values here so the
  // strings render in Russian for users with those locales (the
  // BA's "RU authoritative" half). If/when a real uk / be
  // translator is hired, the entries are local to this constant
  // and the diff is trivial.
  uk: {
    "directory.counterparties.list.title": "Контрагенты",
    "directory.counterparties.list.column.name": "Название",
    "directory.counterparties.list.column.status": "Статус",
    "directory.counterparties.list.column.last_update": "Последнее обновление",
    "directory.counterparties.list.empty": "Контрагенты не найдены",
    "directory.counterparties.list.loading": "Загрузка контрагентов…",
    "directory.counterparties.list.error":
      "Не удалось загрузить контрагентов: {{message}}",
    "directory.counterparties.list.refresh": "Обновить",
    "directory.counterparties.list.search_by_name": "Поиск по названию",
    "directory.counterparties.list.legal_form_filter":
      "Организационно-правовая форма",
    "directory.counterparties.list.legal_form_all": "Все",
    "directory.counterparties.list.new": "Новый контрагент",
    "directory.counterparties.list.view": "Просмотр",
    "directory.counterparties.detail.not_found": "Контрагент не найден",
    "directory.counterparties.detail.edit": "Редактировать",
    "directory.counterparties.detail.back": "Назад",
    "directory.counterparties.detail.audit_title": "История изменений",
    "directory.counterparties.detail.audit_empty": "Записей пока нет",
    "directory.counterparties.detail.loading": "Загрузка контрагента…",
    "directory.counterparties.detail.error":
      "Не удалось загрузить контрагента: {{message}}",
    "directory.counterparties.detail.field.name": "Название",
    "directory.counterparties.detail.field.legal_form":
      "Организационно-правовая форма",
    "directory.counterparties.detail.field.tax_id": "ИНН",
    "directory.counterparties.detail.field.contact_person_id":
      "ID контактного лица",
    "directory.counterparties.detail.field.email": "Электронная почта",
    "directory.counterparties.detail.field.phone": "Телефон",
    "directory.counterparties.detail.field.address": "Адрес",
    "directory.counterparties.detail.field.notes": "Примечания",
    "directory.counterparties.detail.field.status": "Статус",
    "directory.counterparties.detail.field.row_version": "Версия строки (OCC)",
    "directory.counterparties.detail.field.created_at": "Дата создания",
    "directory.counterparties.detail.field.created_by_subject":
      "Кем создан (subject)",
    "directory.counterparties.detail.field.updated_at": "Последнее обновление",
    "directory.counterparties.detail.field.updated_by_subject":
      "Кем обновлён (subject)",
    "directory.counterparties.edit.title": "Редактировать контрагента",
    "directory.counterparties.edit.cancel": "Отмена",
    "directory.counterparties.edit.submit": "Сохранить",
    "directory.counterparties.edit.submitting": "Сохранение…",
    "directory.counterparties.edit.loading": "Загрузка контрагента…",
    "directory.counterparties.edit.not_found": "Контрагент не найден",
    "directory.counterparties.edit.error": "Ошибка: {{message}}",
    "directory.counterparties.edit.row_version":
      "Версия строки (OCC): {{value}}",
    "directory.counterparties.new.title": "Новый контрагент",
    "directory.counterparties.new.cancel": "Отмена",
    "directory.counterparties.new.submit": "Создать контрагента",
    "directory.counterparties.new.submitting": "Создание…",
    "directory.counterparties.new.error": "Ошибка: {{message}}",
    "directory.counterparties.legal_form.individual": "Физическое лицо",
    "directory.counterparties.legal_form.sole_proprietor": "ИП",
    "directory.counterparties.legal_form.partnership": "Товарищество",
    "directory.counterparties.legal_form.corporation": "Корпорация",
    "directory.counterparties.legal_form.cooperative": "Кооператив",
    "directory.counterparties.legal_form.sole_prop": "ФОП",
    "directory.counterparties.legal_form.llc": "ТОВ",
    "directory.counterparties.legal_form.jsc": "АТ",
    "directory.counterparties.legal_form.plc": "ПАТ",
    "directory.counterparties.legal_form.other": "Інше",
    "directory.counterparties.status.active": "Активен",
    "directory.counterparties.status.removed": "Удалён",
  },
  be: {
    "directory.counterparties.list.title": "Контрагенты",
    "directory.counterparties.list.column.name": "Название",
    "directory.counterparties.list.column.status": "Статус",
    "directory.counterparties.list.column.last_update": "Последнее обновление",
    "directory.counterparties.list.empty": "Контрагенты не найдены",
    "directory.counterparties.list.loading": "Загрузка контрагентов…",
    "directory.counterparties.list.error":
      "Не удалось загрузить контрагентов: {{message}}",
    "directory.counterparties.list.refresh": "Обновить",
    "directory.counterparties.list.search_by_name": "Поиск по названию",
    "directory.counterparties.list.legal_form_filter":
      "Организационно-правовая форма",
    "directory.counterparties.list.legal_form_all": "Все",
    "directory.counterparties.list.new": "Новый контрагент",
    "directory.counterparties.list.view": "Просмотр",
    "directory.counterparties.detail.not_found": "Контрагент не найден",
    "directory.counterparties.detail.edit": "Редактировать",
    "directory.counterparties.detail.back": "Назад",
    "directory.counterparties.detail.audit_title": "История изменений",
    "directory.counterparties.detail.audit_empty": "Записей пока нет",
    "directory.counterparties.detail.loading": "Загрузка контрагента…",
    "directory.counterparties.detail.error":
      "Не удалось загрузить контрагента: {{message}}",
    "directory.counterparties.detail.field.name": "Название",
    "directory.counterparties.detail.field.legal_form":
      "Организационно-правовая форма",
    "directory.counterparties.detail.field.tax_id": "ИНН",
    "directory.counterparties.detail.field.contact_person_id":
      "ID контактного лица",
    "directory.counterparties.detail.field.email": "Электронная почта",
    "directory.counterparties.detail.field.phone": "Телефон",
    "directory.counterparties.detail.field.address": "Адрес",
    "directory.counterparties.detail.field.notes": "Примечания",
    "directory.counterparties.detail.field.status": "Статус",
    "directory.counterparties.detail.field.row_version": "Версия строки (OCC)",
    "directory.counterparties.detail.field.created_at": "Дата создания",
    "directory.counterparties.detail.field.created_by_subject":
      "Кем создан (subject)",
    "directory.counterparties.detail.field.updated_at": "Последнее обновление",
    "directory.counterparties.detail.field.updated_by_subject":
      "Кем обновлён (subject)",
    "directory.counterparties.edit.title": "Редактировать контрагента",
    "directory.counterparties.edit.cancel": "Отмена",
    "directory.counterparties.edit.submit": "Сохранить",
    "directory.counterparties.edit.submitting": "Сохранение…",
    "directory.counterparties.edit.loading": "Загрузка контрагента…",
    "directory.counterparties.edit.not_found": "Контрагент не найден",
    "directory.counterparties.edit.error": "Ошибка: {{message}}",
    "directory.counterparties.edit.row_version":
      "Версия строки (OCC): {{value}}",
    "directory.counterparties.new.title": "Новый контрагент",
    "directory.counterparties.new.cancel": "Отмена",
    "directory.counterparties.new.submit": "Создать контрагента",
    "directory.counterparties.new.submitting": "Создание…",
    "directory.counterparties.new.error": "Ошибка: {{message}}",
    "directory.counterparties.legal_form.individual": "Физическое лицо",
    "directory.counterparties.legal_form.sole_proprietor": "ИП",
    "directory.counterparties.legal_form.partnership": "Товарищество",
    "directory.counterparties.legal_form.corporation": "Корпорация",
    "directory.counterparties.legal_form.cooperative": "Кооператив",
    "directory.counterparties.legal_form.sole_prop": "ІП",
    "directory.counterparties.legal_form.llc": "ТАА",
    "directory.counterparties.legal_form.jsc": "АА",
    "directory.counterparties.legal_form.plc": "ПАА",
    "directory.counterparties.legal_form.other": "Іншае",
    "directory.counterparties.status.active": "Активен",
    "directory.counterparties.status.removed": "Удалён",
  },
} as const;

const resources = {
  ru: {
    common: {
      ...COMMON_RESOURCES.ru,
      ...DIRECTORY_COUNTERPARTIES_RESOURCES.ru,
    },
    conflict: CONFLICT_RESOURCES.ru,
  },
  kk: {
    common: {
      ...COMMON_RESOURCES.kk,
      ...DIRECTORY_COUNTERPARTIES_RESOURCES.kk,
    },
    conflict: CONFLICT_RESOURCES.kk,
  },
  en: {
    common: {
      ...COMMON_RESOURCES.en,
      ...DIRECTORY_COUNTERPARTIES_RESOURCES.en,
    },
    conflict: CONFLICT_RESOURCES.en,
  },
  uk: {
    common: {
      ...COMMON_RESOURCES.uk,
      ...DIRECTORY_COUNTERPARTIES_RESOURCES.uk,
    },
    conflict: CONFLICT_RESOURCES.uk,
  },
  be: {
    common: {
      ...COMMON_RESOURCES.be,
      ...DIRECTORY_COUNTERPARTIES_RESOURCES.be,
    },
    conflict: CONFLICT_RESOURCES.be,
  },
} as const;

// Idempotent guard. HMR, StrictMode double-invoke, and Vitest's reset
// behavior can all cause init() to be called more than once. We
// detect prior init by checking a private symbol — `i18next.isInitialized`
// is the public signal but is `false` between `createInstance()` and
// `init()`'s synchronous completion, which makes the check racy in
// SSR-style environments. A module-scoped flag is the durable answer.
let didInit = false;

function initI18nOnce(): void {
  if (didInit) return;
  didInit = true;
  void i18n.use(initReactI18next).init({
    resources,
    lng: "ru",
    fallbackLng: "ru",
    supportedLngs: ["ru", "kk", "en", "uk", "be"],
    // Per FR-UI-CORE-8 AC-1a / F-2: missing-key resolution MUST be
    // `ru → kk → en → key`. i18next's fallback-chain semantics give
    // us `ru → kk → en` automatically with `fallbackLng: 'ru'` and
    // the three locales in `supportedLngs` — the final "→ key"
    // fallback is provided by i18next itself when the key is absent
    // from every resource bundle.
    nonExplicitSupportedLngs: false,
    defaultNS: "common",
    ns: ["common", "conflict"],
    // FR-UI-CORE-8 / F-3 (bridge contract preserved): the legacy
    // FR-UI-CORE-6 bridge accepted flat keys like `conflict.title`
    // from a `useTranslation()` call with no namespace argument.
    // i18next defaults to `fallbackNS: false`, which would silently
    // break the bridge contract — `t("conflict.title")` on the
    // default-namespace hook would return the key string instead of
    // the localized value. We deliberately keep `fallbackNS: false`
    // here and structure the resource bundle (see the `resources`
    // constant) so ALL keys — both `common.*` and `conflict.*` —
    // live as flat keys in the `common` namespace. The `conflict`
    // namespace is still declared in `ns` so existing callers that
    // bind to it explicitly (`useTranslation("conflict")`) remain
    // valid, but they share the same flat-key structure.
    fallbackNS: false,
    interpolation: {
      // React's JSX rendering already escapes text children. We do
      // NOT want i18next to do its own HTML escaping on top — it
      // would double-escape characters like `&` and produce
      // mojibake in the rendered DOM.
      escapeValue: false,
    },
    react: {
      // Suspense is not used in this build (no <Suspense> wrappers
      // anywhere in the SPA). Disabling it avoids the
      // "useTranslation triggered but no suspense boundary"
      // console warning during dev.
      useSuspense: false,
    },
    // We never use the browser `Intl`-derived language detector —
    // locale selection is an explicit application concern
    // (session store seeded by `tenant-config.default_locale`,
    // per ARCHITECTURE_WEB.md §3 and FR-UI-CORE-8 F-4).
    returnNull: false,
  });
}

initI18nOnce();

export default i18n;
